/**
 * Group Detail Page - Visa en specifik beredskapsgrupp
 */

import { useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';
import ModalBackdrop from '@/components/ModalBackdrop';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { useQueryClient } from '@tanstack/react-query';
import resizeFileToBase64, { resizeToMaxSize } from '@/utils/image';
import {
  Users,
  MessageSquare,
  FileText,
  Calendar,
  Settings,
  Share2,
  ChevronLeft,
  Plus,
  Crown,
  MoreVertical,
  ClipboardList,
  Search,
  HandHelping,
  HelpCircle,
  Package,
  ShoppingCart,
  Info,
  Pin,
  X,
  Trash2,
  Edit2,
  Globe,
  Lock,
  Mail,
  Phone,
  Clock,
  AlertTriangle,
  Loader2,
  MapPin,
  ChevronUp,
  ChevronDown,
  Image,
  Send,
  CheckCheck,
  FileStack,
  Flag,
} from 'lucide-react';
import { CityAutocomplete } from '@/components/CityAutocomplete';
import PremiumRequiredModal from '@/components/PremiumRequiredModal';
import { useBulletinBoardStore, type NoteCategory, type BulletinNote } from '../stores/bulletin-board-store';
import { useGroupWikiStore, type WikiArticle } from '../stores/group-wiki-store';
import { useGroupCalendarStore, type CalendarEvent } from '../stores/group-calendar-store';
import { apiGet, apiPost, apiPut, apiAnonGet, markThreadAsRead, getThreadReadStatuses, updateTabVisit, getTabVisit } from '@/services/api-client';
import { apiDelete } from '@/services/api-client';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import DOMPurify from 'dompurify';
import { getNativePlatform } from '@/utils/native-platform';

// Thread templates
interface ThreadTemplate {
  title: string;
  content: string;
}

const threadTemplates: ThreadTemplate[] = [
  {
    title: 'Vattenförsörjning - tips och erfarenheter',
    content: 'Hur har ni löst vattenförsörjningen i er beredskap? Dela gärna era tips om vattenrening, förvaring och alternativa vattenkällor.'
  },
  {
    title: 'Matförråd - vad fungerar för er?',
    content: 'Vilka matvaror har ni i ert förråd? Dela gärna tips om hållbarhet, rotation och smarta lösningar för torr- och konserveringsmat.'
  },
  {
    title: 'Uppvärmning utan el',
    content: 'Hur förbereder ni er för uppvärmning om elen försvinner? Dela era lösningar och erfarenheter kring alternativ värme.'
  },
  {
    title: 'Gruppens övningsplan',
    content: 'Förslag: Vi borde öva regelbundet på vår beredskap. Vad tycker ni om att planera en gemensam övning? Vad ska vi träna på?'
  },
  {
    title: 'Kommunikation vid kris',
    content: 'Hur ska vi kommunicera med varandra om telefonnätet fallerar? Låt oss diskutera samlingsplatser, radiolösningar och alternativa kontaktvägar.'
  },
  {
    title: 'Första hjälpen-utrustning',
    content: 'Vilken första hjälpen-utrustning bör vi ha både hemma och i gruppen? Dela era rekommendationer och erfarenheter.'
  },
  {
    title: 'Ansvarsfördelning i gruppen',
    content: 'För att vara effektiva behöver vi tydliga roller. Vilka ansvarsområden bör vi fördela mellan gruppmedlemmarna?'
  },
  {
    title: 'Ekonomisk beredskap',
    content: 'Hur tänker ni kring ekonomisk beredskap? Kontanter, värdeförvaring och säkerhet - dela era tankar och tips.'
  },
  {
    title: 'Barnens beredskap',
    content: 'Hur involverar vi barn i beredskapen på ett åldersanpassat sätt? Dela era tips och erfarenheter.'
  },
  {
    title: 'Lokal samverkan',
    content: 'Hur kan vi samverka bättre med andra grupper eller organisationer i närområdet? Vad kan vi lära av varandra?'
  }
];

// Wiki article templates
interface WikiArticleTemplate {
  title: string;
  content: string;
  suggestedTags?: string[];
}

const wikiArticleTemplates: WikiArticleTemplate[] = [
  {
    title: 'Gruppens syfte och mål',
    content: `# Gruppens syfte och mål

## Syfte
Vår beredskapsgrupp finns för att:
- Stärka vår gemensamma förmåga att hantera kriser och störningar
- Dela kunskap och erfarenheter inom beredskap
- Skapa en trygg och stöttande gemenskap
- Samverka och hjälpas åt i vardagen och vid extraordinära händelser

## Långsiktiga mål
1. Varje medlem ska ha grundläggande kunskaper i beredskap
2. Gruppen ska ha en plan för hur vi kommunicerar och samlas vid kris
3. Vi ska regelbundet öva och utbilda oss
4. Bygga upp ett nätverk av kompetenser och resurser

## Kortsiktiga mål (detta året)
- [ ] Genomföra minst 4 gemensamma övningar
- [ ] Kartlägga gruppens samlade kompetenser
- [ ] Etablera kommunikationsplan
- [ ] Genomföra grundkurs i första hjälpen`,
    suggestedTags: ['om gruppen', 'syfte']
  },
  {
    title: 'Stadgar och regler',
    content: `# Stadgar och regler

## § 1 - Gruppens namn och syfte
Gruppens namn är [GRUPPENS NAMN]. Gruppen är en ideell förening för personer som vill stärka sin beredskap gemensamt.

## § 2 - Medlemskap
- Medlemskap är öppet för alla som delar gruppens värderingar
- Premium-medlemskap krävs för aktiva roller (ägare/ansvariga)
- Medlemmar förväntas bidra till gruppens aktiviteter efter förmåga

## § 3 - Beslut och möten
- Årsmöte hålls en gång per år
- Beslut fattas med enkel majoritet
- Vid lika röstetal har ägare/ordförande utslagsröst

## § 4 - Ekonomi
- Gruppen kan ha gemensam kassa för inköp av utrustning
- Ekonomisk redovisning ska presenteras vid årsmötet
- Större inköp (över XXX kr) beslutas gemensamt

## § 5 - Utträde och uteslutning
- Medlem kan när som helst begära utträde
- Medlem som bryter mot reglerna kan uteslutas efter beslut

## § 6 - Stadgeändring
Ändring av stadgar kräver 2/3 majoritet vid årsmöte.`,
    suggestedTags: ['om gruppen', 'regler']
  },
  {
    title: 'Ordningsregler och ansvar',
    content: `# Ordningsregler och ansvar

## Grundläggande regler
1. **Respekt** - Behandla alla medlemmar med respekt och värdighet
2. **Sekretess** - Personlig information och diskussioner stannar i gruppen
3. **Konstruktivitet** - Bidra positivt till diskussioner
4. **Engagemang** - Delta aktivt efter din förmåga
5. **Säkerhet först** - Alla övningar och aktiviteter ska vara säkra

## Kommunikationsregler
- Ingen politik eller religion i gruppdiskussioner
- Fokus på beredskap och praktiska frågor
- Respektera olika kunskapsnivåer
- Hjälp och stötta varandra

## Ansvar vid aktiviteter
- Varje deltagare ansvarar för sin egen säkerhet
- Följ instruktioner från ansvarig ledare
- Meddela om du inte kan delta i planerad aktivitet
- Respektera andras egendom och utrustning

## Sociala medier och offentlighet
- Foton från aktiviteter delas endast med godkännande
- Gruppens interna diskussioner är inte offentliga
- Var försiktig med vad du delar om gruppens verksamhet

## Konsekvenser vid regelbrott
1. Första gången: Muntlig påminnelse
2. Andra gången: Skriftlig varning
3. Tredje gången: Tillfällig avstängning
4. Vid allvarliga överträdelser: Omedelbar uteslutning`,
    suggestedTags: ['om gruppen', 'regler']
  },
  {
    title: 'Kommunikationsplan vid kris',
    content: `# Kommunikationsplan vid kris

## Primär kommunikationsväg
**App och mobiltelefon**
- Gruppens chattfunktion i appen
- SMS till gruppmedlemmar
- Telefon vid behov

## Sekundär kommunikationsväg (om mobiltelefonin inte fungerar)
**Radio**
- Kanal: [ANGE KANAL]
- Frekvens: [ANGE FREKVENS]
- Tid för kontroll: Varje dag kl. 08:00, 12:00 och 18:00
- Utrustning: PMR/Jaktradio/Amatörradio

## Tertiär kommunikationsväg
**Fysiska samlingsplatser**
1. **Primär samlingsplats:** [PLATS OCH ADRESS]
   - Tid: Varje dag kl. 10:00
2. **Sekundär samlingsplats:** [PLATS OCH ADRESS]
   - Tid: Varje dag kl. 14:00

## Nödkontakter i gruppen
| Namn | Roll | Telefon | Adress |
|------|------|---------|--------|
| [Namn] | Gruppansvarig | [Tfn] | [Adress] |
| [Namn] | Vice ansvarig | [Tfn] | [Adress] |
| [Namn] | Förstahjälpen | [Tfn] | [Adress] |

## Larmkedja
1. Gruppansvarig larmar vice ansvarig
2. Vice ansvarig larmar områdesansvariga
3. Områdesansvariga larmar sina tilldelade medlemmar

## Meddelanden och koder
- **Kod Grön:** Ingen fara, informationsmöte
- **Kod Gul:** Förhöjd beredskap, håll kontakt
- **Kod Röd:** Akut situation, samlas vid samlingsplats`,
    suggestedTags: ['planering', 'kommunikation']
  },
  {
    title: 'Roller och ansvar',
    content: `# Roller och ansvar i gruppen

## Ledningsgrupp
### Gruppansvarig/Ägare
- Övergripande ansvar för gruppens verksamhet
- Kallar till möten
- Representerar gruppen utåt
- Kontaktperson: [NAMN]

### Vice gruppansvarig
- Stödjer gruppansvarig
- Tar över vid frånvaro
- Kontaktperson: [NAMN]

### Kassör
- Hanterar gruppens ekonomi
- Redovisar vid årsmöte
- Kontaktperson: [NAMN]

## Ansvarsområden
### Utbildningsansvarig
- Planerar kurser och workshops
- Kontakt med externa utbildare
- Kontaktperson: [NAMN]

### Övningsansvarig
- Planerar praktiska övningar
- Säkerhetsansvar vid övningar
- Kontaktperson: [NAMN]

### Utrustningsansvarig
- Inventerar gruppens utrustning
- Ansvarar för underhåll
- Kontaktperson: [NAMN]

### Kommunikationsansvarig
- Uppdaterar gruppens wiki och information
- Hanterar utskick
- Kontaktperson: [NAMN]

### Sociala aktiviteter
- Arrangerar grupptillställningar
- Samordnar familjeevenemang
- Kontaktperson: [NAMN]

## Kompetenser i gruppen
Registrera medlemmarnas kompetenser:
- Första hjälpen: [NAMN, NAMN]
- Brandskydd: [NAMN]
- Matlagning: [NAMN, NAMN]
- Radio/kommunikation: [NAMN]
- Vattenrening: [NAMN]
- Jakt/vilt: [NAMN]
- Snickeri: [NAMN]
- Odling: [NAMN, NAMN]`,
    suggestedTags: ['om gruppen', 'roller']
  },
  {
    title: 'Checklistor för beredskap',
    content: `# Checklistor för beredskap

## Personlig beredskap - grundläggande
- [ ] Mat för minst 3 dagar
- [ ] Vatten 3 liter/person/dag i 3 dagar
- [ ] Reservbelysning (ficklampor, ljus, tändstickor)
- [ ] Batterier och/eller powerbank
- [ ] Batteridriven radio (FM/DAB)
- [ ] Första hjälpen-låda
- [ ] Hygienartiklar
- [ ] Kontanter
- [ ] Viktiga handlingar i vattentät påse

## Hemmet - beredskap för längre störningar
- [ ] Matförråd för 1-2 veckor
- [ ] Alternativ värmekälla
- [ ] Gasolkök eller stormkök
- [ ] Sov- och isoleringsutrustning
- [ ] Vattenförvaring och rening
- [ ] Verktygslåda
- [ ] Brandsläckare och brandvarnare

## Utrymningsbag ("Flyktpåse")
- [ ] Kläder för 72 timmar
- [ ] Värmetäcke
- [ ] Dricksvatten och energimat
- [ ] Första hjälpen
- [ ] ID-handlingar (kopior)
- [ ] Kontaktlistor
- [ ] Laddare och powerbank
- [ ] Hygienartiklar
- [ ] Eventuella mediciner

## Fordon
- [ ] Vindrutetorkare
- [ ] Startkit för batteri
- [ ] Reflexväst
- [ ] Varningstriangel
- [ ] Förstahjälpen-kit
- [ ] Reservdäck och verktyg
- [ ] Filt och varma kläder
- [ ] Vatten och snacks

## Årlig kontroll
- [ ] Kontrollera bäst före-datum på mat
- [ ] Testa ficklampor och radioapparater
- [ ] Byt batterier
- [ ] Uppdatera kontaktlistor
- [ ] Kontrollera första hjälpen-utrustning
- [ ] Servicera generator/värmekälla`,
    suggestedTags: ['checklistor', 'praktiskt']
  },
  {
    title: 'Övningsplan för året',
    content: `# Övningsplan [ÅR]

## Q1 - Januari-Mars
### Januari: Teoriövning
- **Tema:** Hemmaplan och riskanalys
- **Aktivitet:** Workshop i att göra sin egen beredskapsplan
- **Ansvarig:** [NAMN]
- **Datum:** [DATUM]

### Februari: Praktisk övning
- **Tema:** Matlagning utan el
- **Aktivitet:** Gemensam matlagning utomhus
- **Ansvarig:** [NAMN]
- **Datum:** [DATUM]

### Mars: Utbildning
- **Tema:** Första hjälpen grund
- **Aktivitet:** HLR och grundläggande första hjälpen
- **Ansvarig:** [NAMN]
- **Datum:** [DATUM]

## Q2 - April-Juni
### April: Praktisk övning
- **Tema:** Vattenrening
- **Aktivitet:** Test av olika vattenreningssystem
- **Ansvarig:** [NAMN]
- **Datum:** [DATUM]

### Maj: Teoriövning
- **Tema:** Radiokommunikation
- **Aktivitet:** Introduktion till PMR/Jaktradio
- **Ansvarig:** [NAMN]
- **Datum:** [DATUM]

### Juni: Social aktivitet
- **Tema:** Familjedag
- **Aktivitet:** Aktiviteter för hela familjen
- **Ansvarig:** [NAMN]
- **Datum:** [DATUM]

## Q3 - Juli-September
### Juli: Sommaruppehåll
- Ingen planerad aktivitet

### Augusti: Praktisk övning
- **Tema:** Navigation utan GPS
- **Aktivitet:** Orientering med karta och kompass
- **Ansvarig:** [NAMN]
- **Datum:** [DATUM]

### September: Storsövning
- **Tema:** Heldag med olika scenarion
- **Aktivitet:** Kombinerad övning
- **Ansvarig:** [NAMN]
- **Datum:** [DATUM]

## Q4 - Oktober-December
### Oktober: Utbildning
- **Tema:** Brandskydd
- **Aktivitet:** Hantering av brandsläckare
- **Ansvarig:** [NAMN]
- **Datum:** [DATUM]

### November: Teoriövning
- **Tema:** Utvärdering av året
- **Aktivitet:** Årsmöte och planering
- **Ansvarig:** [NAMN]
- **Datum:** [DATUM]

### December: Social aktivitet
- **Tema:** Avslutning
- **Aktivitet:** Gemensam sammankomst
- **Ansvarig:** [NAMN]
- **Datum:** [DATUM]`,
    suggestedTags: ['planering', 'övning']
  },
  {
    title: 'Gemensam utrustning',
    content: `# Gemensam utrustning

## Kommunikation
| Utrustning | Antal | Placering | Ansvarig |
|------------|-------|-----------|----------|
| PMR-radio | [X] st | [PLATS] | [NAMN] |
| Megafon | [X] st | [PLATS] | [NAMN] |
| Batterier (AA/AAB) | [X] förp | [PLATS] | [NAMN] |

## Första hjälpen
| Utrustning | Antal | Placering | Ansvarig |
|------------|-------|-----------|----------|
| Första hjälpen-väska (stor) | [X] st | [PLATS] | [NAMN] |
| Hjärtstartare (AED) | [X] st | [PLATS] | [NAMN] |
| Bår/transport | [X] st | [PLATS] | [NAMN] |
| Filtar | [X] st | [PLATS] | [NAMN] |

## Matlagning och vatten
| Utrustning | Antal | Placering | Ansvarig |
|------------|-------|-----------|----------|
| Gasolkök | [X] st | [PLATS] | [NAMN] |
| Gasoltub | [X] st | [PLATS] | [NAMN] |
| Vattendunkar (25L) | [X] st | [PLATS] | [NAMN] |
| Vattenrenare | [X] st | [PLATS] | [NAMN] |
| Gryta (stor) | [X] st | [PLATS] | [NAMN] |

## Verktyg och materiel
| Utrustning | Antal | Placering | Ansvarig |
|------------|-------|-----------|----------|
| Generator | [X] st | [PLATS] | [NAMN] |
| Motorsåg | [X] st | [PLATS] | [NAMN] |
| Skottkärra | [X] st | [PLATS] | [NAMN] |
| Spade/Hacka | [X] st | [PLATS] | [NAMN] |
| Yxa | [X] st | [PLATS] | [NAMN] |

## Tält och utrustning
| Utrustning | Antal | Placering | Ansvarig |
|------------|-------|-----------|----------|
| Tält (stor) | [X] st | [PLATS] | [NAMN] |
| Liggunderlag | [X] st | [PLATS] | [NAMN] |
| Sovsäck | [X] st | [PLATS] | [NAMN] |

## Övrigt
| Utrustning | Antal | Placering | Ansvarig |
|------------|-------|-----------|----------|
| Brandsl��ckare | [X] st | [PLATS] | [NAMN] |
| Brandfilt | [X] st | [PLATS] | [NAMN] |
| Reflexvästar | [X] st | [PLATS] | [NAMN] |

## Underhållsschema
- **Januari:** Inventering och kontroll av all utrustning
- **April:** Service av motorsåg och generator
- **Juli:** Kontroll av första hjälpen-utrustning
- **Oktober:** Kontroll av brandsläckare`,
    suggestedTags: ['utrustning', 'praktiskt']
  },
  {
    title: 'Säkerhet vid övningar',
    content: `# Säkerhet vid övningar

## Allmänna säkerhetsregler
1. Alla övningar ska ha en utsedd övningsansvarig
2. Säkerhetsgenomgång innan varje övning
3. Deltagare ska känna sig fria att avbryta vid obehag
4. Första hjälpen-utrustning ska finnas tillgänglig
5. Mobiltäckning eller annan kommunikation ska finnas

## Före övningen
### Planering
- [ ] Syfte och mål definierat
- [ ] Riskbedömning gjord
- [ ] Övningsansvarig utsedd
- [ ] Deltagarlista upprättad
- [ ] Säkerhetsutrustning kontrollerad
- [ ] Väderleksprognos granskad

### Information till deltagare
- [ ] Tid och plats meddelat
- [ ] Vad som ska övas
- [ ] Rekommenderad utrustning
- [ ] Säkerhetsinfo skickad
- [ ] Nödkontakter delgivna

## Under övningen
### Obligatorisk säkerhetsgenomgång
1. Presentation av övningsansvarig
2. Genomgång av övningens syfte
3. Identifiering av risker
4. Placering av första hjälpen
5. Avbrottstecken och nödsignaler
6. Samlingsplats vid avbrott

### Löpande säkerhet
- Regelbundna headcounts
- Kontroll av deltagares välbefinnande
- Pauser enligt behov
- Anpassning vid väderomslag

## Efter övningen
### Avslutning
- [ ] Headcount - alla med?
- [ ] Utrustning återlämnad
- [ ] Området återställt
- [ ] Skador/incidenter rapporterade

### Utvärdering
- Vad gick bra?
- Vad kan förbättras?
- Säkerhetsaspekter?
- Lärdomar för nästa gång?

## Särskilda säkerhetsregler
### Eldning och matlagning
- Brandsläckare och vatten tillgängligt
- Säkert avstånd till tält/material
- Ansvarig eldvakt utsedd
- Elden släckt ordentligt efter övning

### Vattenaktiviteter
- Flytväst obligatorisk
- Minst 2 personer tillsammans
- Ingen simning under kalla årstider

### Verktyg och maskiner
- Skyddsutrustning obligatorisk
- Instruktion innan användning
- Endast behöriga använder farliga verktyg
- Säkerhetsavstånd till andra

### Nattövningar
- Extra belysning och reflexer
- Buddy-system (alltid minst 2 personer)
- Välkända områden
- Ökad säkerhetsmarginal

## Incidentrapportering
Om något händer:
1. Avbryt övningen omedelbart
2. Tillkalla hjälp (112 vid behov)
3. Första hjälpen enligt behov
4. Dokumentera händelsen
5. Informera gruppansvarig
6. Lär av händelsen`,
    suggestedTags: ['övning', 'säkerhet']
  },
  {
    title: 'Lokal samverkan och kontakter',
    content: `# Lokal samverkan och kontakter

## Kommunala kontakter
### Krisledning
- **Organisation:** Kommunens krisledningsnämnd
- **Kontakt:** [NAMN/TITEL]
- **Telefon:** [NUMMER]
- **E-post:** [E-POST]
- **Webb:** [LÄNK]

### Räddningstjänst
- **Station:** [NAMN]
- **Telefon (admin):** [NUMMER]
- **Nödnummer:** 112
- **Förebyggande:** [KONTAKT]

### Socialförvaltning
- **Kontakt:** [NAMN]
- **Telefon:** [NUMMER]
- **E-post:** [E-POST]

## Lokala organisationer
### Frivilliga försvarsorganisationer
- **Civilförsvarsförbundet:** [KONTAKT]
- **Frivilliga Automobilkåren:** [KONTAKT]
- **Frivilliga Radioorganisationen:** [KONTAKT]
- **Sjövärnskåren:** [KONTAKT]
- **Lottakåren:** [KONTAKT]

### Andra föreningar
- **Röda Korset:** [KONTAKT]
- **Svenska Brukshundsklubben (sök/räddning):** [KONTAKT]
- **Hemvärnet:** [KONTAKT]

### Lokala företag (samverkan)
- **[FÖRETAG 1]:** [KONTAKT] - [VERKSAMHET]
- **[FÖRETAG 2]:** [KONTAKT] - [VERKSAMHET]
- **[FÖRETAG 3]:** [KONTAKT] - [VERKSAMHET]

## Andra beredskapsgrupper i området
| Gruppnamn | Kontaktperson | Område | Kontakt |
|-----------|---------------|--------|----------|
| [NAMN] | [PERSON] | [OMRÅDE] | [KONTAKT] |
| [NAMN] | [PERSON] | [OMRÅDE] | [KONTAKT] |
| [NAMN] | [PERSON] | [OMRÅDE] | [KONTAKT] |

## Samverkansavtal
### Med andra grupper
- Informationsutbyte
- Gemensamma övningar
- Resursdelning vid kris
- Erfarenhetsutbyte

### Med närområdet
- Kontakt med grannar och närboende
- Identifiering av särskilda behov (äldre, funktionsvarierade)
- Gemensamma resurser (ved, verktyg, kunskap)

## Årligt samverkansmöte
- **När:** [MÅNAD]
- **Plats:** [PLATS]
- **Agenda:**
  - Lägesrapport från varje grupp
  - Gemensamma utmaningar
  - Planering av gemensamma aktiviteter
  - Uppdatering av kontaktlistor`,
    suggestedTags: ['samverkan', 'kontakter']
  },
  {
    title: 'Nödkontakter och resurser',
    content: `# Nödkontakter och resurser

## Nödnummer
| Tjänst | Nummer | Notering |
|--------|--------|----------|
| Nödnummer (SOS) | 112 | Polis, ambulans, räddning, SOS |
| Giftinformation | 112 | Be att bli kopplad till Giftinformationscentralen |
| 1177 Vårdguiden | 1177 | Sjukvårdsrådgivning |
| Knegare Hund | 020-510 510 | Psykiskt stöd, jourhavande präst mm |

## Lokala nödnummer
| Tjänst | Nummer | Adress |
|--------|--------|--------|
| Polisstation | [NUMMER] | [ADRESS] |
| Vårdcentral | [NUMMER] | [ADRESS] |
| Sjukhus akutmottagning | [NUMMER] | [ADRESS] |
| Räddningsstation | [NUMMER] | [ADRESS] |
| Tandakut | [NUMMER] | [ADRESS] |

## Kriscentrum och stöd
| Organisation | Nummer | Verksamhet |
|--------------|--------|------------|
| Kvinnofridslinjen | 020-50 50 50 | Stöd vid våld |
| BRIS | 116 111 | Barn- och ungdomstelefon |
| Jourhavande präst | 112 | Ring 112 och be om präst |
| Suicide Zero | 90101 | Självmordspreventiv hjälp |

## Kommunala tjänster
| Tjänst | Nummer | E-post |
|--------|--------|--------|
| Kommun växel | [NUMMER] | [E-POST] |
| Teknisk förvaltning | [NUMMER] | [E-POST] |
| Felanmälan vatten/avlopp | [NUMMER] | [E-POST] |
| Felanmälan el | [NUMMER] | [E-POST] |
| Felanmälan fjärrvärme | [NUMMER] | [E-POST] |

## Försäkring och assistans
| Tjänst | Nummer | Dygnet runt |
|--------|--------|-------------|
| Hemförsäkring (egen) | [NUMMER] | Ja/Nej |
| Bilförsäkring | [NUMMER] | Ja/Nej |
| Assistansjour | [NUMMER] | Ja/Nej |
| Begravningsbyrå (krisbyrån) | [NUMMER] | Ja |

## Krishantering steg-för-steg
### Vid akut fara (112)
1. Ring 112
2. Beskriv vad som hänt
3. Ange plats
4. Följ instruktioner
5. Lägg inte på förrän du får besked

### Vid störningar
1. Kontrollera omfattning (bara vi eller hela området?)
2. Kontrollera information (lokal radio, kommunens webb)
3. Kontakta relevant tjänst
4. Informera gruppen vid behov
5. Aktivera beredskapsplan

## Informationskanaler vid kris
- **Radio:** P4 [DISTRIKT] ([FREKVENS] MHz)
- **Webb:** krisinformation.se
- **Webb:** din kommun: [LÄNK]
- **SMS:** 113 13 (texttelefon för nödsamtal)

## Apotek och sjukvård
| Apotek | Adress | Öppettider | Telefon |
|--------|--------|------------|----------|
| [NAMN] | [ADRESS] | [TIDER] | [NUMMER] |
| Jourhavande apotek | [ADRESS] | 24/7 | [NUMMER] |`,
    suggestedTags: ['kontakter', 'nöd']
  }
];

// Calendar event templates
interface EventTemplate {
  title: string;
  description: string;
  suggestedTags?: string[];
}

const eventTemplates: EventTemplate[] = [
  {
    title: 'Gruppens årsmöte',
    description: 'Årligt möte för att utvärdera årets aktiviteter, diskutera budget och planera för kommande år. Alla medlemmar välkomna.',
    suggestedTags: ['möte', 'planering']
  },
  {
    title: 'Såddag - odling och trädgård',
    description: 'Gemensam såddag där vi hjälps åt att förbereda och så våra odlingar. Ta med frön om du har, annars delar vi på det vi har. Fika ingår!',
    suggestedTags: ['odling', 'praktisk övning']
  },
  {
    title: 'Familjedag med aktiviteter',
    description: 'En dag för hela familjen med fokus på beredskap på ett roligt sätt. Barnaktiviteter, grillning och övningar anpassade för alla åldrar.',
    suggestedTags: ['familj', 'social']
  },
  {
    title: 'Första hjälpen-utbildning',
    description: 'Grundläggande första hjälpen-kurs med fokus på vanliga skador och situationer. HLR ingår. Begränsat antal platser - anmäl intresse i god tid.',
    suggestedTags: ['utbildning', 'första hjälpen']
  },
  {
    title: 'Brandskyddsutbildning',
    description: 'Praktisk utbildning i brandskydd och släckning. Vi går igenom olika typer av brandsläckare, hur man agerar vid brand och förebyggande åtgärder.',
    suggestedTags: ['utbildning', 'brandsäkerhet']
  },
  {
    title: 'Praktisk matlagning utan el',
    description: 'Workshop där vi lagar mat över öppen eld och använder alternativa metoder. Ta med egna ingredienser eller använd gruppens gemensamma förråd.',
    suggestedTags: ['praktisk övning', 'mat']
  },
  {
    title: 'Vattenprovtagning och rening',
    description: 'Praktisk övning i att ta vattenprover, bedöma vattenkvalitet och använda olika reningsmetoder. Vi testar flera olika vattenreningssystem.',
    suggestedTags: ['praktisk övning', 'vatten']
  },
  {
    title: 'Radiokommunikation - grundkurs',
    description: 'Introduktion till radiokommunikation för beredskap. Vi går igenom jaktradio, PMR och grundläggande amatörradio. Ta med egen radio om du har.',
    suggestedTags: ['utbildning', 'kommunikation']
  },
  {
    title: 'Gemensam beredskapsövning',
    description: 'Heldagsövning där vi övar på olika krisscenarier. Uppdelning i grupper, praktiska moment och utvärdering. Lunch ingår.',
    suggestedTags: ['övning', 'praktisk övning']
  },
  {
    title: 'Inventering av gruppens resurser',
    description: 'Genomgång och uppdatering av gruppens gemensamma utrustning och resurser. Vi kollar vad som behöver kompletteras eller bytas ut.',
    suggestedTags: ['planering', 'möte']
  }
];

// Bulletin board note templates
interface BulletinNoteTemplate {
  title: string;
  content: string;
  category: NoteCategory;
  contact?: string;
}

const bulletinNoteTemplates: BulletinNoteTemplate[] = [
  {
    title: 'Utlåning: Släpkärra',
    content: 'Jag har en släpkärra som jag gärna lånar ut till andra i gruppen. Maxlast 750 kg. Hör av er om ni behöver låna!',
    category: 'lend',
    contact: 'Tel/SMS'
  },
  {
    title: 'Erbjuder: Kunskapsutbyte om konservering',
    content: 'Jag har lång erfarenhet av att konservera grönsaker och frukt. Kan dela med mig av tips och tekniker till den som är intresserad.',
    category: 'offer'
  },
  {
    title: 'Söker: Hjälp med ved',
    content: 'Behöver hjälp med att kapa och klyva ved inför vintern. Har motorsåg men behöver en extra hand. Kan stå för fika!',
    category: 'request',
    contact: 'Helst SMS'
  },
  {
    title: 'Erbjuder: Undervisning i första hjälpen',
    content: 'Jag är utbildad första hjälpen-instruktör och kan hålla en grundkurs för gruppen om det finns intresse.',
    category: 'offer'
  },
  {
    title: 'Till salu: Reservdelar till generator',
    content: 'Har över reservdelar till Honda-generator (modell EU20i). Oljefiltrer, tändstift och luftfilter. Rimligt pris till gruppmedlem.',
    category: 'sell',
    contact: 'Ring eller SMS'
  },
  {
    title: 'Info: Gemensam övning nästa månad',
    content: 'Påminnelse om att vi planerar en gemensam beredskapsövning den 15:e nästa månad. Mer info kommer i diskussionstråden.',
    category: 'info'
  },
  {
    title: 'Utlåning: Vattenrenare',
    content: 'Har en LifeStraw Family vattenrenare som kan lånas ut för kortare perioder. Perfekt att testa innan man investerar i egen.',
    category: 'lend'
  },
  {
    title: 'Erbjuder: Delad beställning av torrvaror',
    content: 'Funderar på att göra en större beställning av torrvaror (ris, pasta, gryn) från grossist. Är någon intresserad av att dela på för bättre pris?',
    category: 'offer'
  },
  {
    title: 'Söker: Radioamatör för rådgivning',
    content: 'Vill lära mig mer om amatörradio för beredskapskommunikation. Söker någon med erfarenhet som kan ge råd om utrustning.',
    category: 'request'
  },
  {
    title: 'Info: Lokalt beredskapslager',
    content: 'MSB har uppdaterat informationen om närmaste beredskapslager. Nyckeln finns hos gruppens samordnare. Länk till info kommer.',
    category: 'info'
  }
];

// Helper to convert markdown tables with <br /> to HTML tables
const convertTablesThatNeedBr = (markdown: string): { type: 'markdown' | 'html', content: string }[] => {
  // Check if markdown contains both a table and <br tags
  if (!markdown.includes('|') || !markdown.includes('<br')) {
    return [{ type: 'markdown', content: markdown }];
  }
  
  // Split by lines and find table blocks
  const lines = markdown.split('\n');
  const result: { type: 'markdown' | 'html', content: string }[] = [];
  let i = 0;
  let markdownBuffer: string[] = [];
  
  const flushMarkdown = () => {
    if (markdownBuffer.length > 0) {
      result.push({ type: 'markdown', content: markdownBuffer.join('\n') });
      markdownBuffer = [];
    }
  };
  
  while (i < lines.length) {
    const line = lines[i];
    
    // Check if this line starts a table (contains |)
    if (line.includes('|') && line.trim().startsWith('|')) {
      // Collect all table lines
      const tableLines: string[] = [];
      let tableHasBr = false;
      
      while (i < lines.length && lines[i].includes('|')) {
        tableLines.push(lines[i]);
        if (lines[i].includes('<br')) {
          tableHasBr = true;
        }
        i++;
      }
      
      // If table has <br>, convert to HTML
      if (tableHasBr && tableLines.length >= 3) {
        flushMarkdown();
        
        const headerLine = tableLines[0];
        const bodyLines = tableLines.slice(2); // Skip separator line
        
        // Parse header
        const headers = headerLine.split('|').filter(c => c.trim()).map(h => h.trim());
        
        // Build HTML table with prose-compatible styling
        let html = '<table>\n<thead>\n<tr>\n';
        headers.forEach(h => {
          html += `<th>${h}</th>\n`;
        });
        html += '</tr>\n</thead>\n<tbody>\n';
        
        // Parse body rows
        bodyLines.forEach(line => {
          if (!line.trim() || line.includes('---')) return;
          const cells = line.split('|').filter(c => c.trim()).map(c => c.trim());
          html += '<tr>\n';
          cells.forEach(cell => {
            html += `<td>${cell}</td>\n`;
          });
          html += '</tr>\n';
        });
        
        html += '</tbody>\n</table>';
        result.push({ type: 'html', content: html });
      } else {
        // Keep original markdown table
        markdownBuffer.push(tableLines.join('\n'));
      }
    } else {
      markdownBuffer.push(line);
      i++;
    }
  }
  
  flushMarkdown();
  return result;
};

// Allow <br /> tags in sanitized output
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), 'br', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
  attributes: {
    ...defaultSchema.attributes,
    '*': [...(defaultSchema.attributes?.['*'] || [])],
  },
};

// threads data is fetched from API

type Tab = 'threads' | 'wiki' | 'calendar' | 'members' | 'board';

// Kategori-konfiguration för anslagstavlan
const categoryConfig: Record<NoteCategory, { icon: typeof HandHelping; color: string }> = {
  offer: { icon: HandHelping, color: 'bg-green-900/30 text-green-300 border-green-700' },
  request: { icon: HelpCircle, color: 'bg-orange-900/30 text-orange-300 border-orange-700' },
  lend: { icon: Package, color: 'bg-blue-900/30 text-blue-300 border-blue-700' },
  sell: { icon: ShoppingCart, color: 'bg-purple-900/30 text-purple-300 border-purple-700' },
  info: { icon: Info, color: 'bg-gray-900/30 text-gray-300 border-gray-600' },
};

// Note Card Component
function NoteCard({ 
  note, 
  onEdit, 
  onDelete,
  canEdit,
  canDelete,
  t,
  onImageClick,
  isNew,
}: { 
  note: BulletinNote; 
  onEdit: () => void; 
  onDelete: () => void;
  canEdit: boolean;
  canDelete: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
  onImageClick?: (imageUrl: string) => void;
  isNew?: boolean;
}) {
  const config = categoryConfig[note.category];
  const CategoryIcon = config.icon;
  const isExpired = note.validUntil && new Date(note.validUntil) < new Date();

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('sv-SE', {
      day: 'numeric',
      month: 'short',
    });
  };

  

  return (
    <div 
      className={`rounded-lg border-2 bg-card p-4 transition-all ${
        isExpired ? 'opacity-60 border-border' : (isNew ? '' : 'border-border hover:border-primary/50')
      }`}
      style={isNew && !isExpired ? { borderColor: '#945123' } : {}}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm border ${config.color}`}>
            <CategoryIcon className="h-3 w-3" />
            <span>{t(`groups.board.categoryLabels.${note.category}`)}</span>
          </span>
          {isNew && (
            <span className="px-2 py-1 rounded-md text-xs font-medium text-white border" style={{ backgroundColor: '#945123', borderColor: '#945123' }}>
              Ny
            </span>
          )}
          {note.isPinned && (
            <Pin className="h-4 w-4 text-primary" />
          )}
          {isExpired && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-sm bg-red-900/30 text-red-300">
              <AlertTriangle className="h-3 w-3" />
            </span>
          )}
        </div>
        {(canEdit || canDelete) && (
          <div className="flex items-center gap-1">
            {canEdit && (
              <button
                onClick={onEdit}
                className="p-1.5 rounded hover:bg-muted transition-colors"
                title={t('groups.board.note.edit')}
              >
                <Edit2 className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
            {canDelete && (
              <button
                onClick={onDelete}
                className="p-1.5 rounded hover:bg-red-900/30 transition-colors"
                title={t('groups.board.note.delete')}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground hover:text-red-400" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <h3 className="font-semibold mb-2">{note.title}</h3>
      <p className="text-base text-muted-foreground mb-3 whitespace-pre-wrap">{note.content}</p>

      {/* Image thumbnail */}
      {note.imageUrl && (
        <div className="mb-3">
          <img 
            src={note.imageUrl} 
            alt="Bild" 
            className="h-32 md:h-40 w-auto rounded-lg border border-border object-cover cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => onImageClick?.(note.imageUrl!)}
          />
        </div>
      )}

      {/* Footer */}
      <div className="flex flex-col gap-1.5 text-sm text-muted-foreground">
        <span>{t('groups.board.note.postedBy', { author: note.authorName })}</span>
        <div className="flex items-center gap-3 flex-wrap">
          {note.validUntil && !isExpired && (
            <span>{t('groups.board.note.validUntil', { date: formatDate(new Date(note.validUntil)) })}</span>
          )}
          {note.contact && (
            <a 
              href={note.contact.includes('@') ? `mailto:${note.contact}` : `tel:${note.contact}`}
              className="flex items-center gap-1 text-primary hover:underline"
            >
              {note.contact.includes('@') ? <Mail className="h-3 w-3" /> : <Phone className="h-3 w-3" />}
              {t('groups.board.note.contact')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ConfirmModal: simple reusable confirmation dialog used by threads actions
function ConfirmModal({ open, title, message, confirmLabel, confirmClassName, onCancel, onConfirm }: { open: boolean; title?: string; message?: string; confirmLabel?: string; confirmClassName?: string; onCancel: () => void; onConfirm: () => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-lg bg-card p-6 z-10">
        <h3 className="text-lg font-semibold mb-2">{title || 'Bekräfta'}</h3>
        <p className="text-sm text-muted-foreground mb-4">{message || 'Är du säker?'}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-1 rounded border border-border">Avbryt</button>
          <button onClick={onConfirm} className={confirmClassName || 'px-3 py-1 rounded bg-red-600 text-white'}>{confirmLabel || 'Radera'}</button>
        </div>
      </div>
    </div>
  );
}

// ReportMemberModal: lets any member report another member for ToS violations
function ReportMemberModal({
  open,
  target,
  groupId,
  groupName,
  onClose,
}: {
  open: boolean;
  target: { id: string; name: string } | null;
  groupId?: string;
  groupName?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      setComment('');
      setSubmitting(false);
      setSubmitted(false);
      setError('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason || !target) return;
    setSubmitting(true);
    setError('');

    const result = await apiPost('/reports', {
      reportedUserId: target.id,
      reportedUserName: target.name,
      reason,
      comment: comment.trim() || undefined,
      groupId,
      groupName,
    });

    setSubmitting(false);
    if (result.success) {
      setSubmitted(true);
    } else {
      if (result.error?.includes('429') || result.error?.includes('dagskvot') || result.error?.includes('limit')) {
        setError(t('groups.members.report.rateLimitError'));
      } else {
        setError(t('groups.members.report.error'));
      }
    }
  };

  if (!open || !target) return null;

  const reasons = [
    { value: 'harassment', label: t('groups.members.report.reasons.harassment') },
    { value: 'hate_speech', label: t('groups.members.report.reasons.hate_speech') },
    { value: 'inappropriate', label: t('groups.members.report.reasons.inappropriate') },
    { value: 'spam', label: t('groups.members.report.reasons.spam') },
    { value: 'threat', label: t('groups.members.report.reasons.threat') },
    { value: 'other', label: t('groups.members.report.reasons.other') },
  ];

  return (
    <ModalBackdrop onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-card rounded-xl p-6 w-full mx-4 shadow-xl border border-border max-h-[90vh] overflow-y-auto max-w-md"
      >
        <h3 className="text-lg font-semibold mb-1">{t('groups.members.report.title')}</h3>
        <p className="text-sm text-muted-foreground mb-5">{target.name}</p>

        {submitted ? (
          <div className="space-y-4">
            <p className="text-sm text-green-600 dark:text-green-400">{t('groups.members.report.success')}</p>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-sm"
              >
                {t('groups.members.report.close')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {t('groups.members.report.reasonLabel')}
              </label>
              <select
                value={reason}
                onChange={e => setReason(e.target.value)}
                required
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">{t('groups.members.report.reasonPlaceholder')}</option>
                {reasons.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {t('groups.members.report.commentLabel')}
              </label>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder={t('groups.members.report.commentPlaceholder')}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
              <p className="text-xs text-muted-foreground mt-1 text-right">{comment.length}/500</p>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <p className="text-xs text-muted-foreground">{t('groups.members.report.disclaimer')}</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-sm"
              >
                {t('groups.members.report.cancel')}
              </button>
              <button
                type="submit"
                disabled={!reason || submitting}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ backgroundColor: '#67724e' }}
              >
                {submitting ? t('groups.members.report.submitting') : t('groups.members.report.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </ModalBackdrop>
  );
}

// Full-screen modal for viewing a thread (modeled after FamilyChatPage)
function ThreadModal({
  open,
  threadId,
  threadTitle,
  loading,
  onClose,
  messages,
  setMessages,
  draft,
  setDraft,
  onSend,
  onDeleteMessage,
  replyImageFile,
  setReplyImageFile,
  replyImagePreview,
  setReplyImagePreview,
  replyImageBase64,
  setReplyImageBase64,
  groupId,
  onReportUser,
}: {
  open: boolean;
  threadId: string | null;
  threadTitle?: string | null;
  loading?: boolean;
  onClose: () => void;
  messages: any[];
  setMessages: (m: any[]) => void;
  draft: string;
  setDraft: (s: string) => void;
  onSend: () => Promise<void> | void;
  onDeleteMessage: (id: string) => Promise<void> | void;
  replyImageFile: File | null;
  setReplyImageFile: (f: File | null) => void;
  replyImagePreview: string | null;
  setReplyImagePreview: (s: string | null) => void;
  replyImageBase64: string | null;
  setReplyImageBase64: (s: string | null) => void;
  groupId: string | null;
  onReportUser: (target: { id: string; name: string }) => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const nativePlatform = getNativePlatform();
  const isNativeIOS = nativePlatform === 'ios';
  const [isStartMessageCollapsed, setIsStartMessageCollapsed] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // viewportHeight not currently used; remove to satisfy linter
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    try {
      return typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true;
    } catch {
      return true;
    }
  });

  useLayoutEffect(() => {
    if (!open) return;
    const previousBodyOverflow = document.body.style.overflow;
    const previousDocOverflow = document.documentElement.style.overflow;
    const y = window.scrollY || window.pageYOffset || 0;

    if (isNativeIOS) {
      try {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      } catch (_e) {
        void _e;
      }
    } else {
      try { document.body.dataset.prevScrollY = String(y); } catch (_e) { void _e; }
      try { lockScroll(); } catch (_e) { try { document.body.style.overflow = 'hidden'; } catch { /* noop */ } }
      try { requestAnimationFrame(() => { try { window.scrollTo(0, y); } catch { /* noop */ } }); } catch { /* noop */ }
    }

    return () => {
      if (isNativeIOS) {
        try {
          document.body.style.overflow = previousBodyOverflow;
          document.documentElement.style.overflow = previousDocOverflow;
        } catch (_e) {
          void _e;
        }
      } else {
        try { unlockScroll(); } catch (_e) { try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { /* noop */ } }
      }
    };
  }, [isNativeIOS, open]);

  // Load more messages when scrolling to top
  const loadMoreMessages = useCallback(async () => {
    if (!groupId || !threadId || !hasMoreMessages || isLoadingMore || messages.length === 0) return;
    
    setIsLoadingMore(true);
    const oldestMessage = messages[0]; // First message is oldest (after initial load reverses)
    
    try {
      // Save current scroll position
      const scrollContainer = scrollRef.current;
      if (!scrollContainer) return;
      
      const oldScrollHeight = scrollContainer.scrollHeight;
      const oldScrollTop = scrollContainer.scrollTop;
      
      const res = await apiGet<{ 
        messages: any[];
        meta: { hasMore?: boolean };
      }>(`/groups/${groupId}/threads/${threadId}?limit=5&before=${encodeURIComponent(oldestMessage.createdAt)}`);
      
      if (res.success && res.data) {
        const fetched = res.data.messages || [];
        
        if (fetched.length > 0) {
          // Prepend older messages
          const newMessages = [...fetched, ...messages];
          setMessages(newMessages);
          setHasMoreMessages(res.data.meta?.hasMore || false);
          
          // Restore scroll position after DOM update
          requestAnimationFrame(() => {
            if (scrollContainer) {
              const newScrollHeight = scrollContainer.scrollHeight;
              const heightDiff = newScrollHeight - oldScrollHeight;
              scrollContainer.scrollTop = oldScrollTop + heightDiff;
            }
          });
        }
      }
    } catch (err) {
      console.error('Failed to load more thread messages', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [groupId, threadId, hasMoreMessages, isLoadingMore, messages, setMessages]);

  // Ensure vars passed from parent are considered used (some builds warn on unused params)
  void threadId;
  void setMessages;
  void replyImageFile;
  void replyImageBase64;

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // Handle browser back button to close modal
  useEffect(() => {
    if (!open) return;
    
    const handlePopState = () => {
      onClose();
    };
    
    // Save original state and replace it with modal state
    const originalState = window.history.state;
    window.history.replaceState({ ...originalState, threadModal: true }, '');
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Restore original state when modal closes normally
      if (window.history.state?.threadModal) {
        window.history.replaceState(originalState, '');
      }
    };
  }, [open, onClose]);

  // Poll for new messages
  useEffect(() => {
    if (!open || !groupId || !threadId) return;

    let mounted = true;

    const pollFn = async () => {
      try {
        // Only fetch recent messages for polling (limit 20 to catch any new ones)
        const res = await apiGet<{ 
          messages: any[];
        }>(`/groups/${groupId}/threads/${threadId}?limit=20`);
        if (!mounted || !res.success || !res.data) return;
        const fetched = res.data.messages || [];

        // Only add messages that are NEWER than what we have
        if (messages.length === 0) return;
        
        // Get the timestamp of the newest message we currently have
        const newestTimestamp = messages[messages.length - 1].createdAt;
        
        // Only add messages that are NEWER than our newest message
        const prevIds = new Set(messages.map((p: any) => p.id));
        const toAdd = fetched.filter((f: any) => !prevIds.has(f.id) && f.createdAt > newestTimestamp);
        
        if (toAdd.length > 0) {
          const newMessages = [...messages, ...toAdd];
          setMessages(newMessages);
        }
      } catch (err) {
        console.error('Failed to poll thread messages', err);
      }
    };

    const timer = setInterval(pollFn, 2500);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, [open, groupId, threadId, messages, setMessages]);

  // Scroll handler for loading more messages
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (scrollContainer.scrollTop < 100 && hasMoreMessages && !isLoadingMore) {
        loadMoreMessages();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [hasMoreMessages, isLoadingMore, loadMoreMessages]);

  useEffect(() => {
    // scroll to bottom on messages change
    try {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    } catch {
      // ignore
    }
  }, [messages]);

  useEffect(() => {
    let mq: MediaQueryList | null = null;
    let mqHandler: ((ev: MediaQueryListEvent) => void) | null = null;
    try {
      mq = window.matchMedia('(min-width: 768px)');
      setIsDesktop(mq.matches);
      mqHandler = (ev: MediaQueryListEvent) => setIsDesktop(ev.matches);
      if (mq.addEventListener) mq.addEventListener('change', mqHandler);
      else mq.addListener(mqHandler as unknown as EventListener);
    } catch {
      // ignore
    }
    return () => {
      try {
        if (mq && mqHandler) {
          if (mq.removeEventListener) mq.removeEventListener('change', mqHandler);
          else mq.removeListener(mqHandler as unknown as EventListener);
        }
      } catch {
        // ignore
      }
    };
  }, []);

  if (!open) return null;

  const linkifyThreadMessage = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline soft-wrap font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const containerStyle: React.CSSProperties = { maxHeight: 'calc(100vh - 32px)' };

  return (
    <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm p-2 md:p-8">
      <div className="max-w-4xl mx-auto flex flex-col bg-card rounded-lg overflow-hidden h-full min-h-0" style={containerStyle}>
        <div className="flex items-center justify-between px-3 py-2 md:px-4 md:py-3 border-b border-border shrink-0">
          <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 min-w-0">
            <MessageSquare className="w-4 h-4 md:w-5 md:h-5 text-white shrink-0" />
            <span className="truncate">{threadTitle || t('groups.threads.title', 'Diskussion')}</span>
            {loading && (
              <svg className="animate-spin ml-2 h-4 w-4 text-muted-foreground shrink-0" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
            )}
          </h2>
          <button onClick={onClose} className="p-1.5 md:p-2 rounded hover:bg-muted shrink-0">
            <X className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
        {/* pinned start message - collapsible with max-height when there are replies */}
        {messages && messages.length > 0 && messages[0] && messages[0].isStartMessage && (
          <div className={`relative ${messages.length === 1 ? 'flex-1 flex flex-col' : 'shrink-0'}`}>
            <div className={`px-2 md:px-4 bg-card overflow-y-auto transition-all duration-300 ${
              isStartMessageCollapsed 
                ? 'max-h-0 pt-0 pb-0' 
                : messages.length === 1
                  ? 'pt-2 md:pt-3 flex-1'
                  : 'pt-2 md:pt-3 max-h-[25vh] md:max-h-[30vh]'
            }`}>
              <div className="p-2 md:p-3 rounded-lg bg-orange-50 dark:bg-orange-900/30 border border-orange-300 shadow-sm mb-2 md:mb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm md:text-base font-medium text-gray-800 dark:text-gray-100 min-w-0 flex-1">{messages[0].createdByDisplayName || messages[0].createdBy}</div>
                  <div className="flex items-center gap-1 shrink-0">
                    {user && messages[0].createdBy && String(messages[0].createdBy) !== String(user.id) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onReportUser({
                            id: String(messages[0].createdBy),
                            name: messages[0].createdByDisplayName || String(messages[0].createdBy) || '',
                          });
                        }}
                        className="p-1 rounded hover:bg-muted/60 transition-colors text-muted-foreground/50 hover:text-red-500"
                        title={t('groups.members.report.button')}
                        aria-label={t('groups.members.report.button')}
                      >
                        <Flag className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <div className="text-xs md:text-sm text-muted-foreground shrink-0">{new Date(messages[0].createdAt).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}</div>
                  </div>
                </div>
                <div className="mt-1 text-sm md:text-base whitespace-pre-wrap break-words">{linkifyThreadMessage(messages[0].content)}</div>
                {messages[0].imageUrl && (
                  <div className="mt-2">
                    <img 
                      src={messages[0].imageUrl} 
                      alt="Bild" 
                      className="h-32 md:h-40 w-auto rounded-lg border border-orange-300 cursor-pointer hover:opacity-90 transition-opacity object-cover" 
                      onClick={() => setFullscreenImage(messages[0].imageUrl)}
                    />
                  </div>
                )}
              </div>
            </div>
            {/* Fade effect above divider */}
            <div className="absolute bottom-0 left-0 right-0 h-6 pointer-events-none bg-gradient-to-t from-card to-transparent"></div>
            <div className="relative" style={{ borderTop: '4px solid #2a382e' }}>
              <button
                onClick={() => setIsStartMessageCollapsed(!isStartMessageCollapsed)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-orange-100 dark:bg-orange-900/50 hover:bg-orange-200 dark:hover:bg-orange-900/70 border border-orange-300 dark:border-orange-700 transition-colors shadow-md z-20"
                aria-label={isStartMessageCollapsed ? 'Visa första meddelandet' : 'Dölj första meddelandet'}
              >
                {isStartMessageCollapsed ? (
                  <ChevronDown className="w-4 h-4 md:w-5 md:h-5" />
                ) : (
                  <ChevronUp className="w-4 h-4 md:w-5 md:h-5" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* messages list: scrollable area - only show when there are replies */}
        {messages && messages.length > 1 && (
          <div className="flex-1 min-h-0 relative">
            {/* Fade effect below divider */}
            <div className="absolute top-0 left-0 right-0 h-6 pointer-events-none bg-gradient-to-b from-card to-transparent z-10"></div>
            <div className="overflow-y-auto p-2 md:p-4 h-full" ref={scrollRef} style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' } as React.CSSProperties}>
              <div className="space-y-2 md:space-y-4">
            {messages.map((m) => {
              if (m.isStartMessage) return null;
              const isMe = (user && ((m.createdBy && user.id && String(m.createdBy) === String(user.id)) || (m.createdByDisplayName && user.displayName && m.createdByDisplayName === user.displayName)));
              return (
                <div key={m.id} className={`p-2 md:p-3 rounded-lg relative ${isMe ? 'self-end' : 'self-start'} bg-background border border-border`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm md:text-base font-medium text-gray-800 dark:text-gray-100 min-w-0 flex-1">{m.createdByDisplayName || m.createdBy}</div>
                    <div className="flex items-center gap-1 text-xs md:text-sm text-muted-foreground shrink-0 mr-6">
                      {isMe && (
                        <button
                          type="button"
                          onClick={() => { setDeleteTargetId(m.id); setDeleteConfirmOpen(true); }}
                          className="p-1 rounded hover:bg-muted/60 transition-colors"
                          aria-label={t('common.delete')}
                          title={t('common.delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      {!isMe && m.createdBy && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onReportUser({
                              id: String(m.createdBy),
                              name: m.createdByDisplayName || String(m.createdBy) || '',
                            });
                          }}
                          className="p-1 rounded hover:bg-muted/60 transition-colors text-muted-foreground/50 hover:text-red-500"
                          title={t('groups.members.report.button')}
                          aria-label={t('groups.members.report.button')}
                        >
                          <Flag className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <span>{new Date(m.createdAt).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}</span>
                    </div>
                  </div>
                  <div className="mt-1 text-sm md:text-base whitespace-pre-wrap break-words pr-6">{linkifyThreadMessage(m.content)}</div>
                  {m.imageUrl && (
                    <div className="mt-2 pr-6">
                      <img 
                        src={m.imageUrl} 
                        alt="Bild" 
                        className="h-32 md:h-40 w-auto rounded-lg border border-border cursor-pointer hover:opacity-90 transition-opacity object-cover" 
                        onClick={() => setFullscreenImage(m.imageUrl)}
                      />
                    </div>
                  )}
                  {isMe && (
                    <ConfirmModal
                      open={deleteConfirmOpen && deleteTargetId === m.id}
                      title={t('groups.threads.deleteMessageConfirmTitle', 'Radera meddelande')}
                      message={t('groups.threads.deleteMessageConfirmMessage', 'Är du säker på att du vill radera detta meddelande? Denna åtgärd kan inte ångras.')}
                      confirmLabel={t('common.delete')}
                      onCancel={() => { setDeleteConfirmOpen(false); setDeleteTargetId(null); }}
                      onConfirm={async () => {
                        setDeleteConfirmOpen(false);
                        const toDelete = deleteTargetId;
                        setDeleteTargetId(null);
                        if (toDelete) await onDeleteMessage(toDelete);
                      }}
                    />
                  )}
                </div>
              );
            })}
            </div>
          </div>
          </div>
        )}

        <div className="p-2 md:p-4 shrink-0" style={{ borderTop: '4px solid #2a382e' }}>
          {/* Image preview */}
          {replyImagePreview && (
            <div className="mb-2">
              <img src={replyImagePreview} alt="Preview" className="max-w-full h-auto rounded-lg border border-border max-h-48" />
            </div>
          )}
          
          <div className="flex flex-col gap-2">
            {/* Image upload controls */}
            <div className="flex gap-2">
              <input
                type="file"
                accept="image/*"
                id="reply-image-upload"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files && e.target.files[0];
                  if (!f) {
                    setReplyImageFile(null);
                    setReplyImagePreview(null);
                    setReplyImageBase64(null);
                    return;
                  }
                  setReplyImageFile(f);
                  try {
                    const base64 = await resizeToMaxSize(f, 2 * 1024 * 1024, 800);
                    setReplyImageBase64(base64);
                    setReplyImagePreview(`data:image/jpeg;base64,${base64}`);
                  } catch (err) {
                    console.error('Failed to resize image:', err);
                    alert('Det gick inte att bearbeta bilden. Försök med en annan bild.');
                    setReplyImageFile(null);
                    setReplyImagePreview(null);
                    setReplyImageBase64(null);
                  }
                }}
              />
              <label htmlFor="reply-image-upload" className="px-2 py-1 rounded border border-border cursor-pointer hover:bg-muted inline-flex items-center gap-1.5 text-xs md:text-sm">
                <Image className="w-3 h-3 md:w-4 md:h-4" />
                Bild
              </label>
              {replyImagePreview && (
                <button type="button" onClick={() => { setReplyImageFile(null); setReplyImagePreview(null); setReplyImageBase64(null); }} className="px-2 py-1 rounded border border-border text-muted-foreground hover:bg-muted text-xs md:text-sm">Ta bort bild</button>
              )}
            </div>

            <div className="flex gap-2 items-center">
              <textarea
                maxLength={300}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('groups.threads.replyPlaceholder')}
                className="flex-1 rounded-lg border border-border p-2 bg-background resize-none h-16 md:h-20 text-sm md:text-base"
                onFocus={() => {
                  try {
                    if (scrollRef.current) {
                      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                    }
                  } catch {
                    // ignore
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (isDesktop && !e.shiftKey) {
                      e.preventDefault();
                      void onSend();
                    }
                    // otherwise allow newline (Shift+Enter or small screens)
                  }
                }}
              />
              <button 
                onClick={() => { void onSend(); }} 
                className="w-10 h-10 rounded bg-[#67724e] text-primary-foreground flex items-center justify-center flex-shrink-0 hover:bg-[#5a6342] transition-colors"
                aria-label={t('groups.threads.reply')}
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen image modal */}
      {fullscreenImage && (
        <div 
          className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-sm flex items-center justify-center p-0 md:p-4"
          onClick={() => setFullscreenImage(null)}
        >
          <button
            onClick={() => setFullscreenImage(null)}
            className="absolute bottom-4 right-4 md:top-4 md:bottom-auto p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
            aria-label="Stäng"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img 
            src={fullscreenImage} 
            alt="Fullscreen" 
            style={{ 
              position: 'absolute',
              top: '50%',
              left: '50%',
              minWidth: '100vh',
              minHeight: '100vw',
              transform: 'translate(-50%, -50%) rotate(90deg)',
              transformOrigin: 'center'
            }}
            className="object-contain md:object-contain md:relative md:max-w-full md:max-h-full md:!w-auto md:!h-auto md:!transform-none"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

// Owner Invites Panel - list pending invites for a group (owner only)
type Invite = { id: string; inviteeEmail: string; groupName?: string; invitedByEmail?: string; groupId?: string };

function OwnerInvitesPanel({ groupId, refetchSignal }: { groupId: string; refetchSignal?: number }) {
  const { t } = useTranslation();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; email: string } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet<Invite[]>(`/invites/group/${groupId}`);
      if (res.success && res.data) setInvites(res.data as Invite[]);
    } catch (err) {
      console.error('Failed to fetch group invites', err);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return;
    fetchInvites();
  }, [groupId, refetchSignal, fetchInvites]);

  async function handleOwnerReject() {
    if (!deleteConfirm) return;
    
    const inviteId = deleteConfirm.id;
    setDeleteConfirm(null);
    setActionLoading(inviteId);
    
    try {
      await apiPost(`/invites/${inviteId}/owner-reject`, {});
      setInvites((s) => s.filter((i) => i.id !== inviteId));
      setSuccessMessage('Inbjudan borttagen');
      setTimeout(() => setSuccessMessage(null), 3000);
      try { queryClient.invalidateQueries({ queryKey: ['invites-summary'] }); } catch (e) { void e; }
    } catch (err) {
      console.error('Owner reject failed', err);
      setErrorMessage('Misslyckades ta bort inbjudan');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setActionLoading(null);
    }
  }
  
  // Lock scroll when modal is open
  useEffect(() => {
    if (deleteConfirm) {
      lockScroll();
    } else {
      unlockScroll();
    }
    return () => unlockScroll();
  }, [deleteConfirm]);

  if (loading) return <p>{t('groups.members.fetchingInvites')}</p>;
  if (!invites || invites.length === 0) return <p>Inga väntande inbjudningar</p>;

  return (
    <>
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800">
          {errorMessage}
        </div>
      )}
      
      <div className="space-y-2">
      {invites.map((inv) => (
        <div key={inv.id} className="rounded-lg border border-border bg-card p-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="font-medium">{inv.inviteeEmail}</div>
              <div className="text-sm text-muted-foreground">Inbjuden till: {inv.groupName}</div>
            </div>
          </div>
          <button 
            className="btn btn-secondary w-full" 
            onClick={() => setDeleteConfirm({ id: inv.id, email: inv.inviteeEmail })} 
            disabled={actionLoading === inv.id}
          >
            {actionLoading === inv.id ? '...' : 'Ta bort'}
          </button>
        </div>
      ))}
      </div>
      
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <ConfirmModal
          open={true}
          title="Ta bort inbjudan"
          message={`Är du säker på att du vill ta bort inbjudan till ${deleteConfirm.email}?`}
          confirmLabel="Ta bort"
          confirmClassName="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={handleOwnerReject}
        />
      )}
    </>
  );
}

// Membership Requests Panel - list pending membership requests for a group (owner only)
type MembershipRequest = {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  message?: string;
  createdAt: string;
  status: 'pending' | 'approved' | 'rejected';
};

function MembershipRequestsPanel({ groupId, refetchSignal, onCountChange, onMemberApproved }: { groupId: string; refetchSignal?: number; onCountChange?: (count: number) => void; onMemberApproved?: () => void }) {
  const [requests, setRequests] = useState<MembershipRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet<{ requests: MembershipRequest[] }>(`/groups/${groupId}/membership-requests`);
      if (res.success && res.data) {
        const reqs = res.data.requests || [];
        setRequests(reqs);
        // Notify parent of count change
        if (onCountChange) {
          const pendingCount = reqs.filter((r: any) => r.status === 'pending').length;
          onCountChange(pendingCount);
        }
      }
    } catch (err) {
      console.error('Failed to fetch membership requests', err);
    } finally {
      setLoading(false);
    }
  }, [groupId, onCountChange]);

  useEffect(() => {
    if (!groupId) return;
    fetchRequests();
  }, [groupId, refetchSignal, fetchRequests]);

  async function handleApprove(requestId: string) {
    setActionLoading(requestId);
    try {
      await apiPost(`/groups/${groupId}/membership-requests/${requestId}/approve`, {});
      setRequests((s) => {
        const updated = s.filter((r) => r.id !== requestId);
        // Update parent count
        if (onCountChange) {
          const pendingCount = updated.filter((r) => r.status === 'pending').length;
          onCountChange(pendingCount);
        }
        return updated;
      });
      // Invalidate groups badge count in Layout
      queryClient.invalidateQueries({ queryKey: ['groups-badge-count'] });
      // Notify parent to refetch group members
      if (onMemberApproved) {
        onMemberApproved();
      }
      setSuccessMessage('Medlemskap godkänt!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Approve failed', err);
      setErrorMessage('Misslyckades godkänna ansökan');
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleReject(requestId: string) {
    setActionLoading(requestId);
    try {
      await apiPost(`/groups/${groupId}/membership-requests/${requestId}/reject`, {});
      setRequests((s) => {
        const updated = s.filter((r) => r.id !== requestId);
        // Update parent count
        if (onCountChange) {
          const pendingCount = updated.filter((r) => r.status === 'pending').length;
          onCountChange(pendingCount);
        }
        return updated;
      });
      // Invalidate groups badge count in Layout
      queryClient.invalidateQueries({ queryKey: ['groups-badge-count'] });
      setSuccessMessage('Ansökan nekad');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Reject failed', err);
      setErrorMessage('Misslyckades neka ansökan');
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setActionLoading(null);
    }
  }

  if (loading) return <p>Hämtar ansökningar...</p>;
  if (!requests || requests.length === 0) return <p>Inga väntande ansökningar</p>;

  return (
    <div className="space-y-2">
      {successMessage && (
        <div className="p-3 rounded-lg bg-green-900/20 border border-green-700/50 text-green-300 text-sm">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="p-3 rounded-lg bg-orange-900/20 border border-orange-700/50 text-orange-300 text-sm">
          {errorMessage}
        </div>
      )}
      {requests.map((req) => (
        <div key={req.id} className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3">
          <div className="min-w-0">
            <div className="font-medium break-words">{req.userName}</div>
            <div className="text-sm text-muted-foreground soft-wrap">{req.userEmail}</div>
            {req.message && (
              <div className="text-sm mt-1 italic text-muted-foreground break-words">&quot;{req.message}&quot;</div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              Ansökt {new Date(req.createdAt).toLocaleDateString('sv-SE')}
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              className="px-3 py-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 whitespace-nowrap" 
              onClick={() => handleReject(req.id)} 
              disabled={actionLoading === req.id}
            >
              {actionLoading === req.id ? '...' : 'Neka'}
            </button>
            <button 
              className="px-3 py-2 rounded-lg bg-[#67724e] text-white hover:bg-[#67724e]/90 disabled:opacity-50 whitespace-nowrap" 
              onClick={() => handleApprove(req.id)} 
              disabled={actionLoading === req.id}
            >
              {actionLoading === req.id ? '...' : 'Godkänn'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// Create/Edit Note Modal
// Group Settings Modal - edit description and visibility (owner only)
function GroupSettingsModal({
  groupId,
  groupName,
  description,
  isPublic,
  imageUrl,
  city,
  onClose,
  onSaved,
}: {
  groupId: string;
  groupName: string;
  description?: string;
  isPublic?: boolean;
  imageUrl?: string | null;
  city?: string;
  onClose: () => void;
  onSaved: (updates: { name?: string; description?: string; isPublic?: boolean; imageUrl?: string | null; city?: string }) => void;
}) {
  const [name, setName] = useState(groupName || '');
  const [desc, setDesc] = useState(description || '');
  const [pub, setPub] = useState(!!isPublic);
  const [localCity, setLocalCity] = useState(city || '');
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(imageUrl || null);
  const [localFile, setLocalFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageResizedBase64, setImageResizedBase64] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Note: imageUrl is not sent here because image uploads are handled separately
      // via POST /groups/{id}/image which directly updates the group document
      const res = await apiPut(`/groups/${groupId}`, { 
        name, 
        description: desc, 
        isPublic: pub,
        city: localCity || null
      });
      if (!res.success) {
        alert(res.error || 'Kunde inte uppdatera grupp');
        return;
      }
      onSaved({ name, description: desc, isPublic: pub, imageUrl: localImageUrl, city: localCity });
    } catch (e) {
      console.error('Failed update group', e);
      alert('Kunde inte uppdatera grupp');
    } finally {
      setSaving(false);
    }
  };

  // sync when prop changes
  useEffect(() => { setLocalImageUrl(imageUrl || null); }, [imageUrl]);

  const handleUploadImage = async () => {
    if (!localFile) return;
    setUploadingImage(true);
    try {
        const data = imageResizedBase64 ?? await import('@/utils/image').then(m => m.default(localFile, 512, 0.8));
        const payload = { filename: localFile.name, contentType: 'image/jpeg', data };
      const res = await apiPost<{ url: string }>(`/groups/${groupId}/image`, payload);
      if (res.success && res.data && (res.data as any).url) {
        setLocalImageUrl((res.data as any).url);
      } else {
        alert(res.error || 'Kunde inte ladda upp bild');
      }
    } catch (e) {
      console.error('Upload failed', e);
      alert('Kunde inte ladda upp bilden');
    } finally {
      setUploadingImage(false);
      setLocalFile(null);
      setImageResizedBase64(null);
    }
  };

  const handleDeleteImage = async () => {
    if (!localImageUrl) return;
    setDeleting(true);
    try {
      const res = await apiDelete(`/groups/${groupId}/image`);
      if (res.success) {
        setLocalImageUrl(null);
      } else {
        alert(res.error || 'Kunde inte radera bilden');
      }
    } catch (e) {
      console.error('Delete image failed', e);
      alert('Kunde inte radera bilden');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-lg bg-card p-6 z-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Gruppinställningar</h3>
          <button onClick={onClose} className="p-2 rounded hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-5">
          <div>
            <label className="block text-sm">Namn</label>
            <input 
              value={name} 
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              className="w-full mt-1 rounded-lg border border-border p-2 bg-background" 
            />
            <div className="text-sm text-muted-foreground mt-1">{name.length}/40</div>
          </div>

          <div>
            <label className="block text-sm">Beskrivning</label>
            <textarea 
              value={desc} 
              onChange={(e) => setDesc(e.target.value)}
              maxLength={250}
              className="w-full mt-1 rounded-lg border border-border p-2 bg-background" 
            />
            <div className="text-sm text-muted-foreground mt-1">{desc.length}/250 tecken</div>
          </div>

          <div>
            <label className="block text-sm">Ort (valfritt)</label>
            <CityAutocomplete
              value={localCity}
              onChange={setLocalCity}
              placeholder="Välj ort..."
              className="w-full mt-1 rounded-lg border border-border p-2 bg-background"
              maxLength={20}
            />
            {localCity && <div className="text-sm text-muted-foreground mt-1">{localCity.length}/20</div>}
          </div>

          <div>
            <label className="block text-sm">Gruppbild</label>
            <div className="flex items-center gap-3 mt-2">
              <div className="w-20 h-20 rounded-md overflow-hidden bg-muted/10 flex items-center justify-center border border-orange-500/60">
                {localImageUrl ? (
                  <img src={localImageUrl} alt="Group image" className="w-full h-full object-cover" />
                ) : (
                  <Users className="h-6 w-6 text-orange-500" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input 
                  type="file" 
                  accept="image/*" 
                  id="group-image-upload"
                  className="hidden"
                  onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  if (!f) {
                    setLocalFile(null);
                    setLocalImageUrl(imageUrl || null);
                    setImageResizedBase64(null);
                    return;
                  }
                  setLocalFile(f);
                  (async () => {
                    try {
                      const base64 = await import('@/utils/image').then(m => m.default(f, 512, 0.8));
                      setImageResizedBase64(base64);
                      setLocalImageUrl(`data:image/jpeg;base64,${base64}`);
                    } catch {
                      setImageResizedBase64(null);
                      setLocalImageUrl(URL.createObjectURL(f));
                    }
                    })();
                }} />
                <div className="flex gap-2">
                  <label htmlFor="group-image-upload" className="px-3 py-1 rounded border border-border cursor-pointer hover:bg-muted inline-block">Välj bild</label>
                  <button type="button" onClick={async () => { await handleUploadImage(); }} disabled={!localFile || uploadingImage} className="px-3 py-1 rounded border border-border disabled:opacity-50 disabled:cursor-not-allowed">{uploadingImage ? 'Laddar...' : 'Ladda upp'}</button>
                  {localImageUrl && (imageUrl) && (
                    <button type="button" onClick={async () => { await handleDeleteImage(); }} disabled={deleting} className="px-3 py-1 rounded border border-border text-muted-foreground hover:bg-muted">{deleting ? 'Tar bort...' : 'Ta bort bild'}</button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={pub} onChange={(e) => setPub(e.target.checked)} />
              <span>Publik (sökbar för andra användare)</span>
            </label>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setConfirmOpen(true)} className="rounded-lg px-3 py-2 border border-red-400 text-red-600 hover:bg-red-50">Radera grupp</button>
            <button onClick={onClose} className="rounded-lg px-3 py-2 border border-border">Avbryt</button>
            <button onClick={handleSave} disabled={saving} className="rounded-lg px-3 py-2 btn-primary-custom disabled:opacity-50">
              {saving ? 'Sparar...' : 'Spara'}
            </button>
          </div>
        </div>
      </div>
      {confirmOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="absolute inset-0 bg-black/60" onClick={() => setConfirmOpen(false)} />
          <div className="relative w-full max-w-md rounded-lg bg-card p-6 z-[10000]">
            <h3 className="text-lg font-semibold mb-2">Bekräfta radering</h3>
            <p className="text-sm text-muted-foreground mb-4">Är du säker på att du vill radera gruppen? Denna åtgärd går inte att ångra och alla data i gruppen kommer tas bort.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)} className="rounded-lg px-3 py-2 border border-border">Avbryt</button>
              <button disabled={deleting} onClick={async () => {
                setDeleting(true);
                try {
                  const res = await apiDelete(`/groups/${groupId}`);
                  if (!res.success) {
                    alert(res.error || 'Kunde inte radera gruppen');
                    setDeleting(false);
                    return;
                  }
                  // Close both modals and navigate away
                  onClose();
                  setConfirmOpen(false);
                  // Optionally navigate back to groups list
                  window.location.href = '/app/groups';
                } catch (e) {
                  console.error('Failed to delete group', e);
                  alert('Kunde inte radera gruppen');
                } finally {
                  setDeleting(false);
                }
              }} className="rounded-lg px-3 py-2 bg-red-600 text-white disabled:opacity-50">{deleting ? 'Raderar...' : 'Radera'}</button>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
}

function NoteModal({
  isOpen,
  onClose,
  onSave,
  editNote,
  initialCategory,
  t,
  groupId,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { title: string; content: string; category: NoteCategory; contact?: string; validUntil?: Date; imageUrl?: string; imageBlobName?: string }) => Promise<void>;
  editNote?: BulletinNote;
  initialCategory?: NoteCategory;
  t: (key: string) => string;
  groupId: string;
}) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState<NoteCategory>('offer');
  const [contact, setContact] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);

  // Update form fields when modal opens (isOpen changes to true)
  useEffect(() => {
    if (isOpen) {
      if (editNote) {
        setTitle(editNote.title || '');
        setContent(editNote.content || '');
        setCategory(editNote.category || 'offer');
        setContact(editNote.contact || '');
        setValidUntil(editNote.validUntil ? new Date(editNote.validUntil).toISOString().split('T')[0] : '');
        setImagePreview(editNote.imageUrl || null);
        setImageFile(null);
        setImageBase64(null);
      } else {
        // New note: set default validUntil to 90 days from now
        const ninetyDaysFromNow = new Date();
        ninetyDaysFromNow.setDate(ninetyDaysFromNow.getDate() + 90);
        const defaultValidUntil = ninetyDaysFromNow.toISOString().split('T')[0];
        
        setTitle('');
        setContent('');
        setCategory(initialCategory || 'offer');
        setContact('');
        setValidUntil(defaultValidUntil);
        setImagePreview(null);
        setImageFile(null);
        setImageBase64(null);
      }
    }
  }, [isOpen, editNote?.id, initialCategory]); // Only run when modal opens or when editing a different note

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Basic client-side validation to avoid sending empty payloads
    if (!title || !content || !category) {
      alert(t('groups.board.form.validationRequired'));
      return;
    }
    // Contact is required and must be a phone number or email
    const trimmedContact = contact.trim();
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedContact);
    const isPhone = trimmedContact.replace(/\D/g, '').length >= 5;
    if (!trimmedContact || (!isEmail && !isPhone)) {
      alert(t('groups.board.form.contactRequired'));
      return;
    }

    setSaving(true);
    try {
      let imageUrl: string | undefined;
      let imageBlobName: string | undefined;

      // Upload image if present
      if (imageFile) {
        const data = imageBase64 ?? await resizeToMaxSize(imageFile, 2 * 1024 * 1024, 800);
        const uploadRes = await apiPost(`/groups/${groupId}/board/images`, {
          filename: imageFile.name,
          contentType: 'image/jpeg',
          data
        });
        if (uploadRes.success && uploadRes.data) {
          const uploadData = uploadRes.data as { url: string; blobName: string };
          imageUrl = uploadData.url;
          imageBlobName = uploadData.blobName;
        }
      } else if (editNote?.imageUrl && imagePreview === editNote.imageUrl) {
        // Keep existing image when editing
        imageUrl = editNote.imageUrl;
        imageBlobName = editNote.imageBlobName;
      }

      await onSave({
        title,
        content,
        category,
        contact: contact.trim(),
        validUntil: validUntil ? new Date(validUntil) : undefined,
        imageUrl,
        imageBlobName,
      });
      onClose();
    } catch (err) {
      console.error('Failed to submit note', err);
      alert(editNote ? t('groups.board.form.updateFailed') : t('groups.board.form.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const categories: NoteCategory[] = ['offer', 'request', 'lend', 'sell', 'info'];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative bg-card rounded-xl border border-border p-4 sm:p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4 sm:mx-0">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">
            {editNote ? t('groups.board.form.editTitle') : t('groups.board.form.title')}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Template button - only show when creating new note */}
          {!editNote && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-background hover:bg-muted transition-colors text-sm font-medium"
              >
                <FileStack className="h-4 w-4" />
                Mallar
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {/* Template dropdown */}
              {showTemplateDropdown && (
                <div className="absolute top-full mt-2 left-0 w-80 max-w-full bg-background border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                  <div className="py-1">
                    {bulletinNoteTemplates.map((template, index) => {
                      const config = categoryConfig[template.category];
                      const Icon = config.icon;
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setTitle(template.title);
                            setContent(template.content);
                            setCategory(template.category);
                            if (template.contact) {
                              setContact(template.contact);
                            }
                            setShowTemplateDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-muted transition-colors border-b border-border last:border-b-0"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Icon className="h-4 w-4" />
                            <span className="font-medium text-foreground text-sm">{template.title}</span>
                          </div>
                          <div className="text-xs text-muted-foreground line-clamp-2 ml-6">
                            {template.content}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Category */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('groups.board.form.category')}</label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {categories.map((cat) => {
                const config = categoryConfig[cat];
                const Icon = config.icon;
                // Extract shadow color based on category
                const shadowColors: Record<NoteCategory, string> = {
                  offer: 'rgba(34, 197, 94, 0.5)',
                  request: 'rgba(251, 146, 60, 0.5)',
                  lend: 'rgba(59, 130, 246, 0.5)',
                  sell: 'rgba(168, 85, 247, 0.5)',
                  info: 'rgba(156, 163, 175, 0.5)',
                };
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${config.color} ${
                      category === cat
                        ? 'ring-2 ring-offset-2 ring-offset-background ring-current/50 scale-105'
                        : 'opacity-70 hover:opacity-100'
                    }`}
                    style={{
                      boxShadow: category === cat ? `0 0 16px ${shadowColors[cat].replace('0.5', '0.8')}` : 'none'
                    }}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-sm">{t(`groups.board.categories.${cat}`)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('groups.board.form.noteTitle')}
              <span className="ml-2 text-xs text-muted-foreground">{title.length}/200</span>
            </label>
            <input
              type="text"
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('groups.board.form.noteTitlePlaceholder')}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('groups.board.form.content')}
              <span className="ml-2 text-xs text-muted-foreground">{content.length}/2000</span>
            </label>
            <textarea
              maxLength={2000}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('groups.board.form.contentPlaceholder')}
              rows={4}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              required
            />
          </div>

          {/* Contact */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('groups.board.form.contact')}
              <span className="ml-2 text-xs text-muted-foreground">{contact.length}/200</span>
            </label>
            <input
              type="text"
              maxLength={200}
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder={t('groups.board.form.contactPlaceholder')}
              required
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Valid until */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('groups.board.form.validUntil')}</label>
            <input
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              lang="sv-SE"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            {validUntil && (
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(validUntil).toLocaleDateString('sv-SE', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            )}
          </div>

          {/* Image upload */}
          <div>
            <label className="block text-sm font-medium mb-2">Bild (valfritt)</label>
            {imagePreview && (
              <div className="mb-2">
                <img src={imagePreview} alt="Preview" className="max-w-full h-auto rounded-lg border border-border max-h-48" />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              id="note-image-upload"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files && e.target.files[0];
                if (!f) {
                  setImageFile(null);
                  setImagePreview(null);
                  setImageBase64(null);
                  return;
                }
                setImageFile(f);
                try {
                  const base64 = await resizeToMaxSize(f, 2 * 1024 * 1024, 800);
                  setImageBase64(base64);
                  setImagePreview(`data:image/jpeg;base64,${base64}`);
                } catch (err) {
                  console.error('Failed to resize image:', err);
                  alert('Det gick inte att bearbeta bilden. Försök med en annan bild.');
                  setImageFile(null);
                  setImagePreview(null);
                  setImageBase64(null);
                }
              }}
            />
            <div className="flex gap-2">
              <label htmlFor="note-image-upload" className="px-3 py-1 rounded border border-border cursor-pointer hover:bg-muted inline-flex items-center gap-1.5 text-sm">
                <Image className="w-4 h-4" />
                Bifoga bild
              </label>
              {imagePreview && (
                <button type="button" onClick={() => { setImageFile(null); setImagePreview(null); setImageBase64(null); }} className="px-3 py-1 rounded border border-border text-muted-foreground hover:bg-muted text-sm">Ta bort bild</button>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50"
            >
              {t('groups.board.form.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-[#67724e] text-primary-foreground hover:bg-[#67724e]/90 transition-colors disabled:opacity-50"
            >
              {saving ? (editNote ? t('groups.board.form.saving') : t('groups.board.form.creating')) : (editNote ? t('groups.board.form.save') : t('groups.board.form.create'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Invite Modal - owner sends invites by email
function InviteModal({ groupId, groupName, onClose, onSent }: { groupId: string; groupName?: string; onClose: () => void; onSent: () => void }) {
  const { t } = useTranslation();
  const defaultMessage = `Hej! Jag vill bjuda in dig till vår beredskapsgrupp "${groupName || 'gruppen'}" i appen Beredskapshubben. Välkommen!`;
  const [email, setEmail] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState(defaultMessage);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const queryClient = useQueryClient();

  // Lock scroll when modal is open
  useLayoutEffect(() => {
    const y = window.scrollY || window.pageYOffset || 0;
    try { document.body.dataset.prevScrollY = String(y); } catch (_e) { void _e; }
    try { lockScroll(); } catch (_e) { try { document.body.style.overflow = 'hidden'; } catch { /* noop */ } }
    try { requestAnimationFrame(() => { try { window.scrollTo(0, y); } catch { /* noop */ } }); } catch { /* noop */ }
    return () => {
      try { unlockScroll(); } catch (_e) { try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { /* noop */ } }
    };
  }, []);

  async function sendInvite() {
    if (!email.trim()) return;
    setError(null);
    setSuccess(false);
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Ange en giltig e-postadress');
      return;
    }

    setSending(true);
    try {
      const response = await apiPost('/invites', { 
        groupId, 
        inviteeEmail: email.trim(),
        welcomeMessage: welcomeMessage.trim() || undefined
      });
      if (response.success) {
        setSuccess(true);
        setEmail('');
        setWelcomeMessage('');
        try { queryClient.invalidateQueries({ queryKey: ['invites-summary'] }); } catch (e) { void e; }
        // Close after showing success for 1.5 seconds
        setTimeout(() => {
          onSent();
        }, 1500);
      } else {
        setError(response.error || 'Misslyckades skicka inbjudan');
      }
    } catch (err: any) {
      console.error('Send invite failed', err);
      setError(err?.message || 'Misslyckades skicka inbjudan');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-lg bg-card p-6 z-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{t('groups.members.inviteMember')}</h3>
          <button onClick={onClose} className="p-2 rounded hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              E-postadress
              <span className="ml-2 text-xs text-muted-foreground">{email.length}/100</span>
            </label>
            <input 
              type="email"
              maxLength={100}
              value={email} 
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
              className="w-full rounded-lg border border-border p-2 bg-background" 
              placeholder="exempel@email.se"
              disabled={sending || success}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Välkomstmeddelande (valfritt)</label>
            <textarea 
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              className="w-full rounded-lg border border-border p-2 bg-background resize-none"
              placeholder={defaultMessage}
              rows={3}
              maxLength={200}
              disabled={sending || success}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Lämna tomt för standardmeddelande. Detta meddelande syns i e-posten och på Notiser-sidan. ({welcomeMessage.length}/200 tecken)
            </p>
          </div>
          
          {error && (
            <div className="p-3 rounded-lg bg-red-900/20 border border-red-700/50 text-red-300 text-sm">
              {error}
            </div>
          )}
          
          {success && (
            <div className="p-3 rounded-lg bg-green-900/20 border border-green-700/50 text-green-300 text-sm">
              Inbjudan skickad!
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <button 
              onClick={onClose} 
              className="rounded-lg px-3 py-2 border border-border hover:bg-muted"
              disabled={sending}
            >
              {success ? 'Stäng' : t('common.cancel')}
            </button>
            {!success && (
              <button 
                onClick={sendInvite} 
                disabled={!email.trim() || sending} 
                className="rounded-lg px-3 py-2 bg-[#67724e] text-white hover:bg-[#67724e]/90 disabled:opacity-50"
              >
                {sending ? 'Skickar...' : t('groups.members.sendInvite')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GroupDetailPage() {
  const { t } = useTranslation();
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('threads');
  const { user } = useAuthStore();
  const currentUserId = user?.id;
  
  // Helper function to format member join date
  const formatMemberDate = (joinedAt?: string) => {
    if (!joinedAt) return '';
    try {
      const date = new Date(joinedAt);
      return date.toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<NoteCategory | 'all'>('all');
  const [showExpired, setShowExpired] = useState(false);
  const [showMyNotes, setShowMyNotes] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<BulletinNote | undefined>();
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [bulletinFullscreenImage, setBulletinFullscreenImage] = useState<string | null>(null);
  const [inviteSentSignal, setInviteSentSignal] = useState(0);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberRoleFilter, setMemberRoleFilter] = useState<'all' | 'owner' | 'member'>('all');
  const [roleError, setRoleError] = useState<string | null>(null);
  
  const [confirmRemoveFor, setConfirmRemoveFor] = useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareInviteOpen, setShareInviteOpen] = useState(false);
  const [shareInviteCopied, setShareInviteCopied] = useState(false);
  
  // Leave group state
  const [showLeaveGroupConfirm, setShowLeaveGroupConfirm] = useState(false);
  const [leavingGroup, setLeavingGroup] = useState(false);
  const [leaveGroupError, setLeaveGroupError] = useState<string | null>(null);
  
  // Join group state
  const [joiningGroup, setJoiningGroup] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  
  // Pending membership requests count (for owner badge)
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  
  // New members count (for green badge)
  const [newMembersCount, setNewMembersCount] = useState(0);
  
  // Handle tab change - mark members as viewed when visiting members tab
  const handleTabChange = (tabId: Tab) => {
    setActiveTab(tabId);
    if (tabId === 'members' && groupId) {
      const storageKey = `group-${groupId}-members-last-visit`;
      localStorage.setItem(storageKey, new Date().toISOString());
      setNewMembersCount(0);
      
      // Invalidate unread counts to update badges in Layout
      queryClient.invalidateQueries({ queryKey: ['groups-unread-counts'] });
    }
  };
  
  // Application modal state
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showOpenJoinConfirm, setShowOpenJoinConfirm] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('Jag är intresserad av att bli medlem i denna grupp eftersom...');
  
  // Description expansion state
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  
  // Premium modal state
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  
  // Member management dropdown state
  const [memberDropdownOpen, setMemberDropdownOpen] = useState<string | null>(null);
  // Report member modal state
  const [reportTarget, setReportTarget] = useState<{ id: string; name: string } | null>(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = () => setMemberDropdownOpen(null);
    if (memberDropdownOpen) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [memberDropdownOpen]);

  // Bulletin board store - use selector to subscribe to changes
  const { addNote, updateNote, deleteNote } = useBulletinBoardStore();
  const allNotes = useBulletinBoardStore((state) => 
    groupId ? state.getNotesForGroup(groupId) : []
  );

  // Wiki store
  const { fetchArticles, createArticle, updateArticle, deleteArticle: deleteWikiArticle } = useGroupWikiStore();

  // Calendar store
  const { fetchEvents, createEvent, updateEvent, deleteEvent: deleteCalendarEvent } = useGroupCalendarStore();

  // Fetch latest bulletin notes when the board tab becomes active
  useEffect(() => {
    if (activeTab !== 'board' || !groupId) return;
    
    (async () => {
      setBoardLoading(true);
      try {
        await useBulletinBoardStore.getState().fetchNotes(groupId);
        
        // Update last visited timestamp in backend
        console.log('[updateTabVisit] calling for groupId:', groupId);
        const updateRes = await updateTabVisit(groupId, 'board');
        console.log('[updateTabVisit] response:', updateRes);
        
        if (updateRes.success) {
          // Update boardLastVisited immediately for badge calculation (badges disappear now)
          // but keep boardLastVisitedForDisplay unchanged ("Ny" markers stay until page refresh)
          const now = new Date().toISOString();
          console.log('[updateTabVisit] updating boardLastVisited to:', now);
          setBoardLastVisited(now);
          // Do NOT update boardLastVisitedForDisplay here - keep showing "Ny" until page leave/refresh
          
          // Invalidate unread counts to update badges in Layout
          queryClient.invalidateQueries({ queryKey: ['groups-unread-counts'] });
        }
      } catch (e) {
        console.error('Failed to fetch bulletin notes', e);
      } finally {
        setBoardLoading(false);
      }
    })();
    
    return () => { /* cleanup noop */ };
  }, [activeTab, groupId]);

  // Fetch Wiki articles when wiki tab is active
  useEffect(() => {
    if (activeTab !== 'wiki' || !groupId) return;
    (async () => {
      setWikiLoading(true);
      try {
        await fetchArticles(groupId);
        const articles = useGroupWikiStore.getState().articlesByGroup[groupId] || [];
        setWikiArticles(articles);
        
        // Update last visited timestamp in backend
        const updateRes = await updateTabVisit(groupId, 'wiki');
        
        if (updateRes.success) {
          // Update wikiLastVisited immediately for badge calculation (badges disappear now)
          // but keep wikiLastVisitedForDisplay unchanged ("Ny" markers stay until page refresh)
          const now = new Date().toISOString();
          setWikiLastVisited(now);
          // Do NOT update wikiLastVisitedForDisplay here - keep showing "Ny" until page leave/refresh
          
          // Invalidate unread counts to update badges in Layout
          queryClient.invalidateQueries({ queryKey: ['groups-unread-counts'] });
        }
      } catch (e) {
        console.error('Failed to fetch wiki articles', e);
      } finally {
        setWikiLoading(false);
      }
    })();
  }, [activeTab, groupId, fetchArticles]);

  // Fetch Calendar events when calendar tab is active
  useEffect(() => {
    if (activeTab !== 'calendar' || !groupId) return;
    (async () => {
      setCalendarLoading(true);
      try {
        await fetchEvents(groupId);
        const events = useGroupCalendarStore.getState().eventsByGroup[groupId] || [];
        setCalendarEvents(events);
        
        // Update last visited timestamp in backend
        const updateRes = await updateTabVisit(groupId, 'calendar');
        
        if (updateRes.success) {
          // Update calendarLastVisited immediately for badge calculation (badges disappear now)
          // but keep calendarLastVisitedForDisplay unchanged ("Ny" markers stay until page refresh)
          const now = new Date().toISOString();
          setCalendarLastVisited(now);
          // Do NOT update calendarLastVisitedForDisplay here - keep showing "Ny" until page leave/refresh
          
          // Invalidate unread counts to update badges in Layout
          queryClient.invalidateQueries({ queryKey: ['groups-unread-counts'] });
        }
      } catch (e) {
        console.error('Failed to fetch calendar events', e);
      } finally {
        setCalendarLoading(false);
      }
    })();
  }, [activeTab, groupId, fetchEvents]);

  // Fetch last visited timestamp for bulletin board
  useEffect(() => {
    if (!groupId) return;
    (async () => {
      try {
        const res = await getTabVisit(groupId, 'board');
        console.log('[getTabVisit] response:', res);
        if (res.success && res.data) {
          console.log('[getTabVisit] setting boardLastVisited to:', res.data.lastVisitedAt);
          setBoardLastVisited(res.data.lastVisitedAt);
          setBoardLastVisitedForDisplay(res.data.lastVisitedAt);
        }
      } catch (e) {
        console.error('Failed to get board last visited', e);
      }
    })();
  }, [groupId]);

  // Fetch last visited timestamp for calendar
  useEffect(() => {
    if (!groupId) return;
    (async () => {
      try {
        const res = await getTabVisit(groupId, 'calendar');
        if (res.success && res.data) {
          setCalendarLastVisited(res.data.lastVisitedAt);
          setCalendarLastVisitedForDisplay(res.data.lastVisitedAt);
        }
      } catch (e) {
        console.error('Failed to get calendar last visited', e);
      }
    })();
  }, [groupId]);

  // Fetch last visited timestamp for wiki
  useEffect(() => {
    if (!groupId) return;
    (async () => {
      try {
        const res = await getTabVisit(groupId, 'wiki');
        if (res.success && res.data) {
          setWikiLastVisited(res.data.lastVisitedAt);
          setWikiLastVisitedForDisplay(res.data.lastVisitedAt);
        }
      } catch (e) {
        console.error('Failed to get wiki last visited', e);
      }
    })();
  }, [groupId]);

  // Fetch bulletin notes on mount to calculate badge count
  useEffect(() => {
    if (!groupId) return;
    (async () => {
      try {
        await useBulletinBoardStore.getState().fetchNotes(groupId);
      } catch (e) {
        console.error('Failed to prefetch bulletin notes for badge', e);
      }
    })();
  }, [groupId]);

  // Fetch calendar events on mount to calculate badge count
  useEffect(() => {
    if (!groupId) return;
    (async () => {
      try {
        await useGroupCalendarStore.getState().fetchEvents(groupId);
        const events = useGroupCalendarStore.getState().eventsByGroup[groupId] || [];
        setCalendarEvents(events);
      } catch (e) {
        console.error('Failed to prefetch calendar events for badge', e);
      }
    })();
  }, [groupId]);

  // Fetch wiki articles on mount to calculate badge count
  useEffect(() => {
    if (!groupId) return;
    (async () => {
      try {
        await useGroupWikiStore.getState().fetchArticles(groupId);
        const articles = useGroupWikiStore.getState().articlesByGroup[groupId] || [];
        setWikiArticles(articles);
      } catch (e) {
        console.error('Failed to prefetch wiki articles for badge', e);
      }
    })();
  }, [groupId]);

  // Group state fetched from API
  type Member = { id: string; name: string; role?: string; avatar?: string | null; joinedAt?: string; subscriptionTier?: string };
  type Group = {
    id: string;
    name: string;
    description?: string;
    memberCount?: number;
    isPublic?: boolean;
    isOpen?: boolean;
    role?: 'owner' | 'admin' | 'member';
    tags?: string[];
    members?: Member[];
    ownerId?: string;
    imageUrl?: string;
    createdAt?: string;
    updatedAt?: string;
    city?: string;
  } | null;

  const [group, setGroup] = useState<Group>(null);
  const [groupAccessDenied, setGroupAccessDenied] = useState(false);
  const [groupLoading, setGroupLoading] = useState(true);

  // Threads state
  const [threads, setThreads] = useState<any[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadReadStatuses, setThreadReadStatuses] = useState<Record<string, string>>({});
  const [showMarkAllReadSuccess, setShowMarkAllReadSuccess] = useState(false);
  const [showNewThreadForm, setShowNewThreadForm] = useState(false);
  const [showThreadTemplateDropdown, setShowThreadTemplateDropdown] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newThreadContent, setNewThreadContent] = useState('');
  const [showEventTemplateDropdown, setShowEventTemplateDropdown] = useState(false);
  const [newThreadImageFile, setNewThreadImageFile] = useState<File | null>(null);
  const [newThreadImagePreview, setNewThreadImagePreview] = useState<string | null>(null);
  const [newThreadImageBase64, setNewThreadImageBase64] = useState<string | null>(null);
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [openThreadMessages, setOpenThreadMessages] = useState<any[]>([]);
  const [openThreadTitle, setOpenThreadTitle] = useState<string | null>(null);
  const [openThreadLoading, setOpenThreadLoading] = useState<boolean>(false);
  const [messageDraft, setMessageDraft] = useState('');
  const [replyImageFile, setReplyImageFile] = useState<File | null>(null);
  const [replyImagePreview, setReplyImagePreview] = useState<string | null>(null);
  const [replyImageBase64, setReplyImageBase64] = useState<string | null>(null);

  // Bulletin board state
  const [boardLoading, setBoardLoading] = useState(false);
  const [boardLastVisited, setBoardLastVisited] = useState<string | null>(null);
  const [boardLastVisitedForDisplay, setBoardLastVisitedForDisplay] = useState<string | null>(null);

  // Wiki state
  const [wikiArticles, setWikiArticles] = useState<WikiArticle[]>([]);
  const [wikiLoading, setWikiLoading] = useState(false);
  const [wikiLastVisited, setWikiLastVisited] = useState<string | null>(null);
  const [wikiLastVisitedForDisplay, setWikiLastVisitedForDisplay] = useState<string | null>(null);
  const [wikiSearch, setWikiSearch] = useState('');
  const [wikiTagFilter, setWikiTagFilter] = useState<string | null>(null);
  const [showWikiEditor, setShowWikiEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState<WikiArticle | null>(null);
  const [wikiTitle, setWikiTitle] = useState('');
  const [wikiContent, setWikiContent] = useState('');
  const [wikiTags, setWikiTags] = useState<string[]>([]);
  const [wikiIsProtected, setWikiIsProtected] = useState(false);
  const [wikiTagInput, setWikiTagInput] = useState('');
  const [viewingArticle, setViewingArticle] = useState<WikiArticle | null>(null);
  const [deletingArticleId, setDeletingArticleId] = useState<string | null>(null);
  const [showMarkdownHelp, setShowMarkdownHelp] = useState(false);
  const [showWikiTemplateDropdown, setShowWikiTemplateDropdown] = useState(false);

  // Calendar state
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarLastVisited, setCalendarLastVisited] = useState<string | null>(null);
  const [calendarLastVisitedForDisplay, setCalendarLastVisitedForDisplay] = useState<string | null>(null);
  const [calendarSearch, setCalendarSearch] = useState('');
  const [calendarTagFilter, setCalendarTagFilter] = useState<string | null>(null);
  const [showOldEvents, setShowOldEvents] = useState(false);
  const [showEventEditor, setShowEventEditor] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTags, setEventTags] = useState<string[]>([]);
  const [eventLink, setEventLink] = useState('');
  const [eventIsProtected, setEventIsProtected] = useState(false);
  const [eventTagInput, setEventTagInput] = useState('');
  const [viewingEvent, setViewingEvent] = useState<CalendarEvent | null>(null);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);

  const [threadsConfirm, setThreadsConfirm] = useState<null | { type: 'thread' | 'message' | 'pin'; id: string; title?: string; isPinned?: boolean }>(null);
  
  // Prevent background scrolling when any modal is open
  useLayoutEffect(() => {
    const isOpen = settingsOpen || shareInviteOpen || showWikiEditor || viewingArticle || showEventEditor || viewingEvent || isNoteModalOpen || editingNote || deletingNoteId || deletingArticleId || deletingEventId || showMarkdownHelp || threadsConfirm !== null || showLeaveGroupConfirm;
    
    const html = document.documentElement;
    const body = document.body;
    
    if (isOpen) {
      // Store scroll position
      const scrollY = window.scrollY;
      body.dataset.scrollY = String(scrollY);
      
      // Add class to trigger CSS scroll lock
      html.classList.add('modal-open');
      
      // Set top position to maintain visual scroll position
      body.style.top = `-${scrollY}px`;
    } else {
      // Remove class
      html.classList.remove('modal-open');
      
      // Restore scroll position
      const scrollY = parseInt(body.dataset.scrollY || '0', 10);
      body.style.top = '';
      window.scrollTo(0, scrollY);
    }
    
    return () => {
      html.classList.remove('modal-open');
      const scrollY = parseInt(body.dataset.scrollY || '0', 10);
      body.style.top = '';
      if (scrollY > 0) {
        window.scrollTo(0, scrollY);
      }
    };
  }, [settingsOpen, shareInviteOpen, showWikiEditor, viewingArticle, showEventEditor, viewingEvent, isNoteModalOpen, editingNote, deletingNoteId, deletingArticleId, deletingEventId, showMarkdownHelp, threadsConfirm, showLeaveGroupConfirm]);

  const getGroupShareLink = useCallback(() => {
    if (!group?.id) return '';
    const origin = window.location.origin.replace(/\/$/, '');
    return `${origin}/groups/${encodeURIComponent(group.id)}`;
  }, [group?.id]);

  const buildGroupInviteText = useCallback(() => {
    const groupName = group?.name || '';
    const groupCity = group?.city || '';
    const groupDescription = group?.description || '';
    const groupLink = getGroupShareLink();

    return [
      'Beredskapshubben | Sveriges beredskapsapp',
      '',
      `Gå med i beredskapsgruppen "${groupName}" på beredskapshubben.se eller i appen.`,
      '',
      groupLink,
      '',
      'Beredskapsgrupp:',
      groupName,
      groupCity,
      '',
      groupDescription,
    ].join('\n');
  }, [group?.name, group?.city, group?.description, getGroupShareLink]);

  const copyGroupInviteText = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(buildGroupInviteText());
      setShareInviteCopied(true);
      window.setTimeout(() => setShareInviteCopied(false), 2000);
    } catch {
      setShareInviteCopied(false);
    }
  }, [buildGroupInviteText]);
  
  // Confirm modal actions for threads
  const handleThreadsConfirm = async () => {
    if (!threadsConfirm || !group) return;
    const { type, id } = threadsConfirm;
    try {
      if (type === 'pin') {
        // toggle pin state: find thread
        const thr = threads.find((t) => t.id === id);
        if (!thr) return;
        await apiPut(`/groups/${group.id}/threads/${id}/pin`, { isPinned: !thr.isPinned });
        const list = await apiGet<any[]>(`/groups/${group.id}/threads`);
        if (list.success) setThreads(list.data || []);
      }
      if (type === 'thread') {
        await apiDelete(`/groups/${group.id}/threads/${id}`);
        const list = await apiGet<any[]>(`/groups/${group.id}/threads`);
        if (list.success) setThreads(list.data || []);
        if (openThreadId === id) { setOpenThreadId(null); setOpenThreadMessages([]); }
      }
      if (type === 'message') {
        // message id stored in id, need thread context — backend uses partitioning, endpoint includes threadId in path
        // We keep openThreadId as current thread
        if (!openThreadId) return;
        await apiDelete(`/groups/${group.id}/threads/${openThreadId}/messages/${id}`);
        // refresh messages
        const res = await apiGet<any>(`/groups/${group.id}/threads/${openThreadId}`);
        if (res.success) setOpenThreadMessages(res.data.messages || []);
        const list = await apiGet<any[]>(`/groups/${group.id}/threads`);
        if (list.success) setThreads(list.data || []);
      }
    } catch (e) {
      console.error('Confirm action failed', e);
      alert('Åtgärden misslyckades');
    } finally {
      setThreadsConfirm(null);
    }
  };

  // Load threads when tab active
  useEffect(() => {
    if (activeTab !== 'threads' || !group) return;
    let cancelled = false;
    (async () => {
      setThreadsLoading(true);
      try {
        // Invalidate unread counts to update badges
        queryClient.invalidateQueries({ queryKey: ['groups-unread-counts'] });
        
        const res = await apiGet<any[]>(`/groups/${group.id}/threads`);
        if (!cancelled && res.success) {
          const normalized = (res.data || []).map((t: any) => ({
            ...t,
            createdByDisplayName: t.createdByDisplayName || (t.createdBy === user?.id ? user?.displayName : t.createdByDisplayName),
            startMessage: t.startMessage ? ({
              ...t.startMessage,
              createdByDisplayName: t.startMessage.createdByDisplayName || (t.startMessage.createdBy === user?.id ? user?.displayName : t.startMessage.createdByDisplayName),
            }) : null,
            // Compute a lastActivity timestamp for sorting: prefer `lastReplyAt`, then start message createdAt, then thread createdAt
            _lastActivityAt: (t.lastReplyAt && new Date(t.lastReplyAt)) || (t.startMessage && t.startMessage.createdAt && new Date(t.startMessage.createdAt)) || (t.createdAt && new Date(t.createdAt)) || new Date(0),
          }))
          // Sort: pinned threads first, then by _lastActivityAt descending (most recent first)
          .sort((a: any, b: any) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            return (b._lastActivityAt?.getTime() || 0) - (a._lastActivityAt?.getTime() || 0);
          });
          setThreads(normalized);
        }
        
        // Invalidate unread counts to update badges in Layout
        queryClient.invalidateQueries({ queryKey: ['groups-unread-counts'] });
      } catch (e) {
        console.error('Failed to load threads', e);
      } finally {
        if (!cancelled) setThreadsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, group]);

  // Load thread read statuses when on threads tab
  useEffect(() => {
    if (activeTab !== 'threads' || !group) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getThreadReadStatuses(group.id);
        if (!cancelled && res.success && res.data) {
          setThreadReadStatuses(res.data);
        }
      } catch (e) {
        console.error('Failed to load thread read statuses', e);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, group]);

  const queryClient = useQueryClient();

  const openThread = async (id: string) => {
    if (!group) return;
    // Clear previous messages/title immediately to avoid flashing old content
    setOpenThreadMessages([]);
    // set title from threads list immediately if available to avoid 'Diskussion' placeholder
    const found = threads.find((t) => t.id === id);
    setOpenThreadTitle(found ? (found.title || null) : null);
    setOpenThreadId(id);
    
    // Mark thread as read in background and update local state
    markThreadAsRead(group.id, id).then(() => {
      // Update read status locally to immediately reflect the change
      setThreadReadStatuses(prev => ({
        ...prev,
        [id]: new Date().toISOString()
      }));
      // Invalidate unread counts query to update badge immediately
      queryClient.invalidateQueries({ queryKey: ['groups-unread-counts'] });
    }).catch((err) => {
      console.error('Failed to mark thread as read:', err);
    });

    try {
      setOpenThreadLoading(true);
      // Load initial 10 messages
      const res = await apiGet<any>(`/groups/${group.id}/threads/${id}?limit=10`);
      if (res.success) {
        const rawMsgs = (res.data.messages || []).map((m: any) => ({
          ...m,
          createdByDisplayName: m.createdByDisplayName || (m.createdBy === user?.id ? user?.displayName : m.createdByDisplayName),
        }));
        // mark the very first message as the start message so the modal can pin it
        if (rawMsgs.length > 0) rawMsgs[0] = { ...rawMsgs[0], isStartMessage: true };
        setOpenThreadMessages(rawMsgs);
        // Use server title if provided (overwrite optimistic title)
        setOpenThreadTitle(res.data.title || found?.title || null);
      }
    } catch (e) {
      console.error('Failed to open thread', e);
    } finally {
      setOpenThreadLoading(false);
    }
  };

  // Mark all threads as read
  const markAllThreadsAsRead = async () => {
    if (!group) return;
    
    try {
      // Call backend to mark all as read FIRST
      const response = await apiPost(`/groups/${group.id}/mark-all-threads-read`, {});
      
      if (!response.success) {
        console.error('Failed to mark all threads as read:', response.error);
        alert('Kunde inte markera diskussioner som lästa. Försök igen.');
        return;
      }
      
      // Only update local state if backend succeeded
      const now = new Date().toISOString();
      const updates: Record<string, string> = {};
      
      threads.forEach((thread) => {
        updates[thread.id] = now;
      });
      
      setThreadReadStatuses(prev => ({
        ...prev,
        ...updates
      }));
      
      // Invalidate unread counts query to update badge
      queryClient.invalidateQueries({ queryKey: ['groups-unread-counts'] });
      
      // Show success message only after backend confirms
      setShowMarkAllReadSuccess(true);
      setTimeout(() => setShowMarkAllReadSuccess(false), 3000);
      
      const marked = (response.data as any)?.markedCount || threads.length;
      console.log('[markAllThreadsAsRead] Successfully marked', marked, 'threads as read');
    } catch (err) {
      console.error('Failed to mark all threads as read:', err);
      alert('Ett fel uppstod. Försök igen.');
    }
  };

  // Calculate unread thread count
  // Helper function to check if a thread is unread
  const isThreadUnread = (thread: any): boolean => {
    // Get user's join date for this group
    const currentUserId = user?.id;
    const members = Array.isArray(group?.members) ? group.members : [];
    const userMember = members.find((m: any) => String(m.userId || m.id) === String(currentUserId));
    const joinedAt = userMember?.joinedAt;
    
    const lastReadAt = threadReadStatuses[thread.id];
    const lastActivityAt = thread._lastActivityAt;
    
    // If no activity, it's read
    if (!lastActivityAt) return false;
    
    // If thread activity (last reply or creation) was before user joined, consider it read
    if (joinedAt) {
      const joinDate = new Date(joinedAt);
      const activityDate = lastActivityAt instanceof Date ? lastActivityAt : new Date(lastActivityAt);
      if (activityDate < joinDate) {
        return false; // Last activity is older than membership, so it's considered read
      }
    }
    
    // If never read and activity is after join date, it's unread
    if (!lastReadAt) return true;
    
    const lastReadDate = new Date(lastReadAt);
    // _lastActivityAt is already a Date object, don't convert it again
    const lastActivityDate = lastActivityAt instanceof Date ? lastActivityAt : new Date(lastActivityAt);
    
    return lastActivityDate > lastReadDate;
  };

  const unreadThreadCount = useMemo(() => {
    if (!threads || threads.length === 0) return 0;
    return threads.filter(thread => isThreadUnread(thread)).length || 0;
  }, [threads, threadReadStatuses, group, user]);

  const newBoardNotesCount = useMemo(() => {
    if (!allNotes || allNotes.length === 0) return 0;
    
    // Get user's join date for fallback
    const currentUserId = user?.id;
    const members = Array.isArray(group?.members) ? group.members : [];
    const userMember = members.find((m: any) => String(m.userId || m.id) === String(currentUserId));
    const joinedAt = userMember?.joinedAt;
    
    // If never visited, use joinedAt as fallback (notes created after joining are "new")
    const referenceDate = boardLastVisited ? new Date(boardLastVisited) : (joinedAt ? new Date(joinedAt) : null);
    if (!referenceDate) return 0;
    
    // Filter: created after reference date AND not created by current user
    const count = allNotes.filter(note => 
      new Date(note.createdAt) > referenceDate && 
      String(note.authorId) !== String(currentUserId)
    ).length || 0;
    
    console.log('[newBoardNotesCount]', {
      count,
      boardLastVisited,
      joinedAt,
      referenceDate: referenceDate.toISOString(),
      totalNotes: allNotes.length,
      notesAfterRef: allNotes.filter(note => new Date(note.createdAt) > referenceDate).length,
      currentUserId
    });
    
    return count;
  }, [allNotes, boardLastVisited, group?.members, user?.id]);

  const newCalendarEventsCount = useMemo(() => {
    if (!calendarEvents || calendarEvents.length === 0) return 0;
    
    // Get user's join date for fallback
    const currentUserId = user?.id;
    const members = Array.isArray(group?.members) ? group.members : [];
    const userMember = members.find((m: any) => String(m.userId || m.id) === String(currentUserId));
    const joinedAt = userMember?.joinedAt;
    
    // If never visited, use joinedAt as fallback (events created after joining are "new")
    const referenceDate = calendarLastVisited ? new Date(calendarLastVisited) : (joinedAt ? new Date(joinedAt) : null);
    if (!referenceDate) return 0;
    
    // Filter: created after reference date AND not created by current user
    // Also consider updatedAt for events that were modified
    const count = calendarEvents.filter(event => {
      const createdDate = new Date(event.createdAt);
      const updatedDate = event.updatedAt ? new Date(event.updatedAt) : createdDate;
      const latestChange = updatedDate > createdDate ? updatedDate : createdDate;
      
      return latestChange > referenceDate && String(event.authorId) !== String(currentUserId);
    }).length || 0;
    
    console.log('[newCalendarEventsCount]', {
      count,
      calendarLastVisited,
      joinedAt,
      referenceDate: referenceDate.toISOString(),
      totalEvents: calendarEvents.length,
      eventsAfterRef: calendarEvents.filter(event => {
        const createdDate = new Date(event.createdAt);
        const updatedDate = event.updatedAt ? new Date(event.updatedAt) : createdDate;
        const latestChange = updatedDate > createdDate ? updatedDate : createdDate;
        return latestChange > referenceDate;
      }).length,
      currentUserId
    });
    
    return count;
  }, [calendarEvents, calendarLastVisited, group?.members, user?.id]);

  const newWikiArticlesCount = useMemo(() => {
    if (!wikiArticles || wikiArticles.length === 0) {
      return 0;
    }
    
    // Get user's join date for fallback
    const currentUserId = user?.id;
    const members = Array.isArray(group?.members) ? group.members : [];
    const userMember = members.find((m: any) => String(m.userId || m.id) === String(currentUserId));
    const joinedAt = userMember?.joinedAt;
    
    // If never visited, use joinedAt as fallback (articles created after joining are "new")
    const referenceDate = wikiLastVisited ? new Date(wikiLastVisited) : (joinedAt ? new Date(joinedAt) : null);
    if (!referenceDate) {
      return 0;
    }
    
    // Filter: created after reference date AND not created by current user
    // Also consider updatedAt for articles that were modified
    const count = wikiArticles.filter(article => {
      const createdDate = new Date(article.createdAt);
      const updatedDate = article.updatedAt ? new Date(article.updatedAt) : createdDate;
      const latestChange = updatedDate > createdDate ? updatedDate : createdDate;
      
      return latestChange > referenceDate && String(article.authorId) !== String(currentUserId);
    }).length || 0;
    
    return count;
  }, [wikiArticles, wikiLastVisited, group?.members, user?.id]);

  // Refetch group data (used when member is approved)
  const refetchGroup = useCallback(async () => {
    if (!groupId) return;
    try {
      const res = await apiGet<any>(`/groups/${groupId}`);
      if (res.success && res.data) {
        const g = res.data as any;
        const currentUserId = useAuthStore.getState().user?.id;
        const members = Array.isArray(g.members)
          ? g.members.map((m: any) => {
              const id = String(m.id || m.userId || '');
              const unknown = t ? t('common.unknown') : 'Okänd';
              const fallback = m.displayName || m.name || m.email || id || unknown;
              return {
                id,
                name: String(fallback),
                displayName: String(fallback),
                role: m.role,
                joinedAt: m.joinedAt,
                subscriptionTier: m.subscriptionTier,
              };
            })
          : undefined;

        const mapped = {
          id: String(g.id || ''),
          name: String(g.name || ''),
          description: typeof g.description === 'string' ? g.description : undefined,
          memberCount: typeof g.memberCount === 'number' ? g.memberCount : members ? members.length : undefined,
          members,
          tags: Array.isArray(g.tags) ? g.tags.map((t: any) => String(t)) : [],
          isPublic: typeof g.isPublic === 'boolean' ? g.isPublic : undefined,
          isOpen: typeof g.isOpen === 'boolean' ? g.isOpen : false,
          role: undefined as any,
          ownerId: typeof g.ownerId === 'string' ? g.ownerId : undefined,
          imageUrl: (g.image && typeof g.image.url === 'string' ? g.image.url : undefined) || (typeof g.imageUrl === 'string' ? g.imageUrl : undefined),
          updatedAt: typeof g.updatedAt === 'string' ? g.updatedAt : undefined,
          city: typeof g.city === 'string' ? g.city : undefined,
        };

        // Determine role for current user
        if (mapped.ownerId && currentUserId && mapped.ownerId === currentUserId) {
          mapped.role = 'owner';
        } else if (members) {
          const userMember = members.find((m: any) => String(m.id) === String(currentUserId));
          if (userMember) {
            mapped.role = userMember.role;
          }
        }
        setGroup(mapped);
      }
    } catch (err) {
      console.error('Failed to refetch group', err);
    }
  }, [groupId, t]);


  useEffect(() => {
    let mounted = true;
    if (!groupId) return;
    
    setGroupLoading(true);
    setGroupAccessDenied(false);
    
    (async () => {
      try {
        const res = await apiGet<any>(`/groups/${groupId}`);
        console.log('[GroupDetailPage] API response:', res);
        console.log('[GroupDetailPage] API response data:', res.data);
        console.log('[GroupDetailPage] API response data.role:', res.data?.role);
        if (!mounted) return;
        
        // Check if access was denied (401 or error indicating not a member)
        if (!res.success) {
          console.log('[GroupDetailPage] API failed, error:', res.error);
          if (res.error?.includes('401') || res.error?.includes('Unauthorized') || res.error?.includes('Authentication required') || res.error?.includes('medlem')) {
            console.log('[GroupDetailPage] Access denied, fetching public group info and pending requests');
            setGroupAccessDenied(true);
            let publicGroupIsOpen = false;
            
            // Fetch basic group info from public API to get isPublic flag and image
            try {
              const publicRes = await apiAnonGet(`/groups-public?q=`);
              console.log('[GroupDetailPage] Public groups response:', publicRes);
              if (publicRes.success && publicRes.data && Array.isArray(publicRes.data)) {
                const publicGroup = publicRes.data.find((g: any) => String(g.id) === String(groupId));
                console.log('[GroupDetailPage] Found public group:', publicGroup);
                if (publicGroup) {
                  publicGroupIsOpen = Boolean(publicGroup.isOpen);
                  const groupData = {
                    id: String(publicGroup.id || ''),
                    name: String(publicGroup.name || ''),
                    description: String(publicGroup.description || ''),
                    isPublic: Boolean(publicGroup.isPublic),
                    isOpen: Boolean(publicGroup.isOpen),
                    imageUrl: (publicGroup.image as any)?.url || undefined,
                    memberCount: publicGroup.memberCount,
                    city: publicGroup.city ? String(publicGroup.city) : undefined,
                    role: undefined,
                    members: undefined,
                    createdAt: undefined,
                    updatedAt: undefined,
                  };
                  console.log('[GroupDetailPage] Setting group data:', groupData);
                  setGroup(groupData);

                  if (groupData.isOpen) {
                    setHasPendingRequest(false);
                    setPendingRequestId(null);
                  }
                }
              }
            } catch (e) {
              console.error('Failed to fetch public group info', e);
            }
            
            // Check if user has pending request
            try {
              const shouldCheckPending = !publicGroupIsOpen;
              if (shouldCheckPending) {
                const pendingRes = await apiGet<{ requests: Array<{ id: string; groupId: string; status: string }> }>('/membership-requests/my-requests');
                console.log('[GroupDetailPage] Pending requests response:', pendingRes);
                if (pendingRes.success && pendingRes.data && Array.isArray(pendingRes.data.requests)) {
                  const pendingRequest = pendingRes.data.requests.find((r) => String(r.groupId) === String(groupId) && r.status === 'pending');
                  console.log('[GroupDetailPage] Found pending request:', pendingRequest);
                  if (pendingRequest) {
                    setHasPendingRequest(true);
                    setPendingRequestId(pendingRequest.id);
                    console.log('[GroupDetailPage] Set hasPendingRequest=true, pendingRequestId=', pendingRequest.id);
                  }
                }
              }
            } catch (e) {
              console.error('Failed to check pending request', e);
            }
          }
          setGroupLoading(false);
          return;
        }
        
        if (res.success && res.data) {
          const g = res.data as any;
          const members = Array.isArray(g.members)
            ? g.members.map((m: any) => {
                const id = String(m.id || m.userId || '');
                // Use i18n-aware unknown label when translation function is available
                const unknown = t ? t('common.unknown') : 'Okänd';
                const fallback = m.displayName || m.name || m.email || id || unknown;
                return {
                  id,
                  name: String(fallback),
                  displayName: String(fallback),
                  role: m.role,
                  joinedAt: m.joinedAt,
                  subscriptionTier: m.subscriptionTier,
                };
              })
            : undefined;

          const mapped = {
            id: String(g.id || ''),
            name: String(g.name || ''),
            description: typeof g.description === 'string' ? g.description : undefined,
            memberCount: typeof g.memberCount === 'number' ? g.memberCount : members ? members.length : undefined,
            members,
            tags: Array.isArray(g.tags) ? g.tags.map((t: any) => String(t)) : [],
            isPublic: typeof g.isPublic === 'boolean' ? g.isPublic : undefined,
            isOpen: typeof g.isOpen === 'boolean' ? g.isOpen : false,
            role: undefined as any,
            ownerId: typeof g.ownerId === 'string' ? g.ownerId : undefined,
            imageUrl: (g.image && typeof g.image.url === 'string' ? g.image.url : undefined) || (typeof g.imageUrl === 'string' ? g.imageUrl : undefined),
            updatedAt: typeof g.updatedAt === 'string' ? g.updatedAt : undefined,
            city: typeof g.city === 'string' ? g.city : undefined,
          };

          // Determine role for current user
          if (mapped.ownerId && currentUserId && mapped.ownerId === currentUserId) {
            mapped.role = 'owner';
          } else if (members) {
            const userMember = members.find((m: any) => String(m.id) === String(currentUserId));
            if (userMember) {
              mapped.role = userMember.role; // Use the actual role from members array
            }
          }
          setGroup(mapped);
          
          // If user is owner, fetch pending membership requests count
          if (mapped.role === 'owner') {
            try {
              const reqRes = await apiGet<{ requests: MembershipRequest[] }>(`/groups/${groupId}/membership-requests`);
              if (reqRes.success && reqRes.data && Array.isArray(reqRes.data.requests)) {
                const pendingCount = reqRes.data.requests.filter((r: any) => r.status === 'pending').length;
                setPendingRequestsCount(pendingCount);
              }
            } catch (e) {
              console.error('Failed to fetch pending requests count', e);
            }
          }
          
          // If user is not a member and group is public+closed, fetch pending requests
          if (!mapped.role && mapped.isPublic && !mapped.isOpen) {
            console.log('[GroupDetailPage] User is not a member of public group, fetching pending requests');
            try {
              const pendingRes = await apiGet<{ requests: Array<{ id: string; groupId: string; status: string }> }>('/membership-requests/my-requests');
              console.log('[GroupDetailPage] Pending requests response:', pendingRes);
              if (pendingRes.success && pendingRes.data && Array.isArray(pendingRes.data.requests)) {
                const pendingRequest = pendingRes.data.requests.find((r) => String(r.groupId) === String(groupId) && r.status === 'pending');
                console.log('[GroupDetailPage] Found pending request:', pendingRequest);
                if (pendingRequest) {
                  setHasPendingRequest(true);
                  setPendingRequestId(pendingRequest.id);
                  console.log('[GroupDetailPage] Set hasPendingRequest=true, pendingRequestId=', pendingRequest.id);
                }
              }
            } catch (e) {
              console.error('Failed to check pending request', e);
            }
          }

          // Enrich members with real displayName from user service when fallback was used
          (async () => {
            if (!mapped.members || mapped.members.length === 0) return;
            const needsEnrich = mapped.members.filter((m: any) => {
              // If displayName equals id or is 'Okänd', try to fetch real profile
              return !m.displayName || m.displayName === m.id || m.displayName === 'Okänd';
            });
            if (needsEnrich.length === 0) return;

            const updated = { ...mapped, members: [...mapped.members] } as any;
            await Promise.all(needsEnrich.map(async (m: any) => {
              const res = await apiGet<any>(`/users/id/${encodeURIComponent(m.id)}`);
              if (!res.success) {
                // If user not found (404) or other error, skip enriching this member
                console.debug('Member profile not available', { id: m.id, error: res.error });
                return;
              }

              const user = res.data as any;
              const name = user.displayName || user.name || user.email || m.id;
              const foundIndex = updated.members.findIndex((mm: any) => mm.id === m.id);
              if (foundIndex >= 0) {
                updated.members[foundIndex] = { ...updated.members[foundIndex], name: String(name), displayName: String(name) };
              }
            }));

            setGroup(updated);
            
            // Calculate new members count
            if (groupId && updated.members && updated.members.length > 0) {
              const storageKey = `group-${groupId}-members-last-visit`;
              const lastVisit = localStorage.getItem(storageKey);
              if (lastVisit) {
                const newMembers = updated.members.filter((m: any) => {
                  if (!m.joinedAt) return false;
                  return new Date(m.joinedAt) > new Date(lastVisit);
                });
                setNewMembersCount(newMembers.length);
              }
            }
          })();
        }
        setGroupLoading(false);
      } catch (err: any) {
        console.error('Failed to fetch group', err);
        setGroupAccessDenied(true);
        setGroupLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [groupId, currentUserId]);

  // Join group function (now creates membership request for public groups)
  const handleJoinGroup = async () => {
    if (!groupId || joiningGroup) return;

    // Open groups are joined directly (no application flow)
    if (group?.isOpen) {
      setShowOpenJoinConfirm(false);
      if (user?.subscriptionTier !== 'premium') {
        try {
          const res = await apiGet<{ groups: Array<{ id: string }> }>('/groups/my-groups');
          if (res.success && res.data) {
            const memberGroupCount = res.data.groups.length;
            if (memberGroupCount >= 2) {
              setShowPremiumModal(true);
              return;
            }
          }
        } catch (err) {
          console.error('Failed to check group membership count', err);
        }
      }

      setJoiningGroup(true);
      setErrorMessage(null);
      try {
        const res = await apiPost(`/groups/${groupId}/join`, {});
        if (res.success) {
          setGroup((prev) => prev ? {
            ...prev,
            role: 'member',
            memberCount: (prev.memberCount || 0) + 1,
          } : prev);
          queryClient.setQueryData<Array<{ id: string; name: string; imageUrl?: string; unreadMessages?: number; pendingRequestCount?: number }>>(
            ['groups-badge-count', user?.id],
            (current) => {
              const existing = current || [];
              if (!group) {
                return existing;
              }

              if (existing.some((entry) => entry.id === group.id)) {
                return existing;
              }

              return [
                {
                  id: group.id,
                  name: group.name,
                  imageUrl: group.imageUrl,
                  unreadMessages: 0,
                  pendingRequestCount: 0,
                  lastActivityAt: group.updatedAt || new Date().toISOString(),
                },
                ...existing,
              ];
            }
          );
          void queryClient.invalidateQueries({ queryKey: ['groups-badge-count', user?.id] });
          void queryClient.invalidateQueries({ queryKey: ['groups-unread-counts', user?.id] });
          setHasPendingRequest(false);
          setPendingRequestId(null);
          setShowApplicationModal(false);
          await refetchGroup();
        } else {
          setErrorMessage(res.error || 'Kunde inte gå med i gruppen');
          setTimeout(() => setErrorMessage(null), 5000);
        }
      } catch (err) {
        console.error('Failed to join open group', err);
        setErrorMessage('Kunde inte gå med i gruppen');
        setTimeout(() => setErrorMessage(null), 5000);
      } finally {
        setJoiningGroup(false);
      }
      return;
    }
    
    // Validate message
    const trimmedMessage = applicationMessage.trim();
    if (!trimmedMessage || trimmedMessage.length < 10) {
      setErrorMessage('Vänligen skriv en kort motivation (minst 10 tecken)');
      setTimeout(() => setErrorMessage(null), 5000);
      return;
    }

    // Check premium limit: free users can only have max 2 pending membership requests
    if (user?.subscriptionTier !== 'premium') {
      try {
        const res = await apiGet<{ requests: Array<{ id: string; status: string }> }>('/membership-requests/my-requests');
        if (res.success && res.data) {
          const pendingCount = res.data.requests.filter(req => req.status === 'pending').length;
          if (pendingCount >= 2) {
            setShowPremiumModal(true);
            return;
          }
        }
      } catch (err) {
        console.error('Failed to check pending requests', err);
        // Continue with request if check fails
      }

      // Check premium limit: free users can only be members of max 2 groups
      try {
        const res = await apiGet<{ groups: Array<{ id: string }> }>('/groups/my-groups');
        if (res.success && res.data) {
          const memberGroupCount = res.data.groups.length;
          if (memberGroupCount >= 2) {
            setShowPremiumModal(true);
            return;
          }
        }
      } catch (err) {
        console.error('Failed to check group membership count', err);
        // Continue with request if check fails
      }
    }
    
    setJoiningGroup(true);
    setErrorMessage(null);
    try {
      // For public groups, create a membership request instead of joining directly
      const res = await apiPost<{ id?: string }>(`/groups/${groupId}/membership-requests`, { message: trimmedMessage });
      if (res.success) {
        setShowSuccessMessage(true);
        setHasPendingRequest(true);
        if (res.data?.id) {
          setPendingRequestId(res.data.id);
        }
        setShowApplicationModal(false);
        setTimeout(() => setShowSuccessMessage(false), 5000);
      } else {
        // Parse error message and provide Swedish translation
        let errorMsg = res.error || 'Kunde inte skicka ansökan';
        if (errorMsg.includes('already have a pending membership request')) {
          errorMsg = 'Du har redan en pågående ansökan till denna grupp';
        }
        setErrorMessage(errorMsg);
        setTimeout(() => setErrorMessage(null), 5000);
      }
    } catch (err) {
      console.error('Failed to request membership', err);
      setErrorMessage('Kunde inte skicka ansökan om medlemskap');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setJoiningGroup(false);
    }
  };

  const handleJoinButtonClick = () => {
    if (group?.isOpen) {
      setShowOpenJoinConfirm(true);
      return;
    }
    setShowApplicationModal(true);
  };

  // Cancel membership request
  const handleCancelRequest = async () => {
    if (!groupId || !pendingRequestId || joiningGroup) return;
    setJoiningGroup(true);
    setErrorMessage(null);
    try {
      const res = await apiDelete(`/groups/${groupId}/membership-requests/${pendingRequestId}`);
      if (res.success) {
        setHasPendingRequest(false);
        setPendingRequestId(null);
        // Navigate back to search page
        navigate('/app/groups/search');
      } else {
        setErrorMessage(res.error || 'Kunde inte avbryta ansökan');
        setTimeout(() => setErrorMessage(null), 5000);
      }
    } catch (err) {
      console.error('Failed to cancel request', err);
      setErrorMessage('Kunde inte avbryta ansökan');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setJoiningGroup(false);
    }
  };

  // Member management functions
  const handleChangeMemberRole = async (memberId: string, newRole: 'owner' | 'member') => {
    if (!groupId || !group) return;

    // Check if trying to make someone owner
    if (newRole === 'owner') {
      // Find the member to check their subscription tier
      const member = group.members?.find((m: any) => String(m.id || m.userId) === String(memberId));
      if (member && member.subscriptionTier !== 'premium') {
        setRoleError('Denna medlem har ingen prenumeration och kan därför inte tilldelas rollen ägare.');
        setMemberDropdownOpen(null);
        setTimeout(() => setRoleError(null), 5000);
        return;
      }
    }

    try {
      const res = await apiPut(`/groups/${groupId}/members/${memberId}`, { role: newRole });
      if (res.success) {
        // Refresh group
        const refreshRes = await apiGet<any>(`/groups/${groupId}`);
        if (refreshRes.success && refreshRes.data) {
          const g = refreshRes.data as any;
          const currentUserId = useAuthStore.getState().user?.id;
          const members = Array.isArray(g.members)
            ? g.members.map((m: any) => ({
                id: String(m.id || m.userId || ''),
                name: String(m.displayName || m.name || m.email || m.id || t('common.unknown')),
                displayName: String(m.displayName || m.name || m.email || m.id || t('common.unknown')),
                role: m.role,
              }))
            : undefined;
          
          // Determine role for current user
          let userRole: 'owner' | 'member' | undefined = undefined;
          const ownerId = typeof g.ownerId === 'string' ? g.ownerId : undefined;
          if (ownerId && currentUserId && ownerId === currentUserId) {
            userRole = 'owner';
          } else if (members) {
            const userMember = members.find((m: any) => String(m.id) === String(currentUserId));
            if (userMember) {
              userRole = userMember.role;
            }
          }
          
          setGroup({
            ...group,
            id: group.id,
            name: group.name,
            members,
            memberCount: members?.length,
            role: userRole,
          });
        }
      } else {
        alert(res.error || 'Kunde inte ändra roll');
      }
    } catch (err) {
      console.error('Failed to change member role', err);
      alert('Kunde inte ändra roll');
    }
    setMemberDropdownOpen(null);
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!groupId || !group) return;
    try {
      const res = await apiDelete(`/groups/${groupId}/members/${memberId}`);
      if (res.success) {
        // Refresh group
        const refreshRes = await apiGet<any>(`/groups/${groupId}`);
        if (refreshRes.success && refreshRes.data) {
          const g = refreshRes.data as any;
          const currentUserId = useAuthStore.getState().user?.id;
          const members = Array.isArray(g.members)
            ? g.members.map((m: any) => ({
                id: String(m.id || m.userId || ''),
                name: String(m.displayName || m.name || m.email || m.id || t('common.unknown')),
                displayName: String(m.displayName || m.name || m.email || m.id || t('common.unknown')),
                role: m.role,
              }))
            : undefined;
          
          // Determine role for current user (same logic as initial load)
          let userRole: 'owner' | 'member' | undefined = undefined;
          const ownerId = typeof g.ownerId === 'string' ? g.ownerId : undefined;
          if (ownerId && currentUserId && ownerId === currentUserId) {
            userRole = 'owner';
          } else if (members && members.some((m: any) => String(m.id) === String(currentUserId))) {
            userRole = 'member';
          }
          
          const updatedGroup = {
            ...group,
            id: group.id,
            name: group.name,
            members,
            memberCount: members?.length,
            role: userRole,
          };
          setGroup(updatedGroup);

          // Enrich members with real displayName from user service when fallback was used
          (async () => {
            if (!members || members.length === 0) return;
            const needsEnrich = members.filter((m: any) => {
              // If displayName equals id or is 'Okänd', try to fetch real profile
              return !m.displayName || m.displayName === m.id || m.displayName === 'Okänd' || m.displayName === t('common.unknown');
            });
            if (needsEnrich.length === 0) return;

            const enriched = { ...updatedGroup, members: [...members] } as any;
            await Promise.all(needsEnrich.map(async (m: any) => {
              const res = await apiGet<any>(`/users/id/${encodeURIComponent(m.id)}`);
              if (!res.success) {
                // If user not found (404) or other error, skip enriching this member
                console.debug('Member profile not available', { id: m.id, error: res.error });
                return;
              }

              const user = res.data as any;
              const name = user.displayName || user.name || user.email || m.id;
              const foundIndex = enriched.members.findIndex((mm: any) => mm.id === m.id);
              if (foundIndex >= 0) {
                enriched.members[foundIndex] = { ...enriched.members[foundIndex], name: String(name), displayName: String(name) };
              }
            }));

            setGroup(enriched);
          })();
        }
      } else {
        alert(res.error || 'Kunde inte ta bort medlem');
      }
    } catch (err) {
      console.error('Failed to remove member', err);
      alert('Kunde inte ta bort medlem');
    }
    setMemberDropdownOpen(null);
  };

  // Leave group function
  const handleLeaveGroup = async () => {
    if (!groupId || !group) return;
    
    setLeaveGroupError(null);
    
    // Check if user is owner
    const currentUserId = useAuthStore.getState().user?.id;
    const userMember = group.members?.find((m: any) => String(m.id) === String(currentUserId));
    const isOwner = userMember?.role === 'owner';
    
    if (isOwner) {
      // Count how many owners exist
      const ownerCount = group.members?.filter((m: any) => m.role === 'owner').length || 0;
      
      if (ownerCount <= 1) {
        setLeaveGroupError('Du är den enda ägaren. Tilldela ägarrollen till någon annan innan du lämnar gruppen.');
        return;
      }
    }
    
    // Proceed with leaving
    setLeavingGroup(true);
    try {
      const res = await apiDelete(`/groups/${groupId}/members/${currentUserId}`);
      if (res.success) {
        // Navigate back to groups page
        navigate('/app/groups');
      } else {
        setLeaveGroupError(res.error || 'Kunde inte lämna gruppen');
      }
    } catch (err) {
      console.error('Failed to leave group', err);
      setLeaveGroupError('Kunde inte lämna gruppen');
    } finally {
      setLeavingGroup(false);
    }
  };

  // Filter notes
  const filteredNotes = useMemo(() => {
    let notes = allNotes;

    // Filter by category
    if (categoryFilter !== 'all') {
      notes = notes.filter((n) => n.category === categoryFilter);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      notes = notes.filter(
        (n) =>
          n.title.toLowerCase().includes(query) ||
          n.content.toLowerCase().includes(query) ||
          (n.authorName || '').toLowerCase().includes(query)
      );
    }

    // Filter expired
    if (!showExpired) {
      notes = notes.filter((n) => !n.validUntil || new Date(n.validUntil) >= new Date());
    }

    // Filter my notes
    if (showMyNotes) {
      notes = notes.filter((n) => n.authorId === currentUserId);
    }

    return notes;
  }, [allNotes, categoryFilter, searchQuery, showExpired, showMyNotes]);

  // Filter wiki articles
  const filteredWikiArticles = useMemo(() => {
    let articles = wikiArticles;

    // Filter by search
    if (wikiSearch) {
      const query = wikiSearch.toLowerCase();
      articles = articles.filter(
        (a) =>
          a.title.toLowerCase().includes(query) ||
          a.content.toLowerCase().includes(query) ||
          a.tags.some(t => t.toLowerCase().includes(query))
      );
    }

    // Filter by tag
    if (wikiTagFilter) {
      articles = articles.filter((a) => a.tags.includes(wikiTagFilter));
    }

    return articles;
  }, [wikiArticles, wikiSearch, wikiTagFilter]);

  // Filter calendar events
  const filteredCalendarEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    
    let events = calendarEvents;

    // Filter out old events unless showOldEvents is true
    if (!showOldEvents) {
      events = events.filter(e => new Date(e.date) >= today);
    }

    // Filter by search
    if (calendarSearch) {
      const query = calendarSearch.toLowerCase();
      events = events.filter(
        (e) =>
          e.title.toLowerCase().includes(query) ||
          e.description.toLowerCase().includes(query) ||
          e.tags.some(t => t.toLowerCase().includes(query))
      );
    }

    // Filter by tag
    if (calendarTagFilter) {
      events = events.filter((e) => e.tags.includes(calendarTagFilter));
    }

    // Sort by date ascending (earliest first)
    events = [...events].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return events;
  }, [calendarEvents, calendarSearch, calendarTagFilter, showOldEvents]);

  // Group calendar events by month
  const groupedCalendarEvents = useMemo(() => {
    const groups = new Map<string, CalendarEvent[]>();
    
    filteredCalendarEvents.forEach(event => {
      const date = new Date(event.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!groups.has(monthKey)) {
        groups.set(monthKey, []);
      }
      groups.get(monthKey)!.push(event);
    });

    // Convert to array and sort by month key
    return Array.from(groups.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, events]) => ({
        monthKey: key,
        monthLabel: events[0] ? new Date(events[0].date).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long' }) : '',
        events
      }));
  }, [filteredCalendarEvents]);

  // Get all unique tags from wiki articles
  const allWikiTags = useMemo(() => {
    const tags = new Set<string>();
    wikiArticles.forEach(a => a.tags.forEach(t => tags.add(t)));
    return Array.from(tags).sort();
  }, [wikiArticles]);

  // Get all unique tags from calendar events
  const allCalendarTags = useMemo(() => {
    const tags = new Set<string>();
    calendarEvents.forEach(e => e.tags.forEach(t => tags.add(t)));
    return Array.from(tags).sort();
  }, [calendarEvents]);

  const tabs = [
    { id: 'threads' as const, label: t('groups.tabs.threads'), icon: MessageSquare, badge: unreadThreadCount > 0 ? unreadThreadCount : undefined },
    { id: 'board' as const, label: t('groups.tabs.board'), icon: ClipboardList, badge: newBoardNotesCount > 0 ? newBoardNotesCount : undefined },
    { id: 'calendar' as const, label: t('groups.tabs.calendar'), icon: Calendar, badge: newCalendarEventsCount > 0 ? newCalendarEventsCount : undefined },
    { id: 'wiki' as const, label: t('groups.tabs.wiki'), icon: FileText, badge: newWikiArticlesCount > 0 ? newWikiArticlesCount : undefined },
    { id: 'members' as const, label: t('groups.tabs.members'), icon: Users },
  ];

  // Split tabs into two rows for mobile layout
  const topRowTabs = tabs.slice(0, 3); // Diskussioner, Anslagstavla, Kalender
  const bottomRowTabs = tabs.slice(3); // Wiki, Medlemmar

  // Removed unused formatDate helper

  // Show loading state
  if (groupLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show "group not found" message if group doesn't exist at all
  if (!groupLoading && !group && groupAccessDenied) {
    console.log('[GroupDetailPage] Showing group not found - groupLoading:', groupLoading, ', group:', group, ', groupAccessDenied:', groupAccessDenied);
    return (
      <div className="space-y-4">
        {/* Back button */}
        <Link
          to="/app/groups/search"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          Tillbaka
        </Link>

        <div className="flex flex-col items-center justify-center p-12 border rounded-lg">
          <AlertTriangle className="h-16 w-16 text-orange-500 mb-6" />
          <h2 className="text-2xl font-bold mb-2">Beredskapsgruppen finns inte</h2>
          <p className="text-muted-foreground text-center max-w-md mb-6">
            Denna beredskapsgrupp har blivit borttagen eller existerar inte längre.
          </p>
          <Link
            to="/app/groups/search"
            className="px-6 py-3 rounded-lg bg-[#67724e] text-primary-foreground hover:bg-[#67724e]/90 font-medium transition-colors"
          >
            Sök efter grupper
          </Link>
        </div>
      </div>
    );
  }

  // Show access denied message if user is not a member
  if (groupAccessDenied || (group && !group.role)) {
    const nonMemberDescription = (group?.description || '').trim();
    console.log('[GroupDetailPage] Showing access denied - groupAccessDenied:', groupAccessDenied, ', group:', group, ', group.role:', group?.role);
    return (
      <div className="space-y-4">
        {/* Back button */}
        <Link
          to="/app/groups/search"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          Tillbaka
        </Link>

        <div className="flex flex-col p-8 sm:p-12 border rounded-lg">
          <div className="text-center space-y-4">
            {/* Group image and name */}
            <div className="flex flex-col items-center gap-3">
              <div className="rounded-xl bg-orange-500/10 border border-orange-500/60 shrink-0 overflow-hidden h-20 w-20 flex items-center justify-center">
                {group?.imageUrl ? (
                  <img src={group.imageUrl} alt="Group image" className="h-full w-full object-cover" />
                ) : (
                  <Users className="h-10 w-10 text-orange-500" />
                )}
              </div>
              {group?.name && (
                <h2 className="text-xl font-semibold soft-wrap">{group.name}</h2>
              )}
            </div>

            {nonMemberDescription && (
              <div className="mx-auto max-w-2xl text-center">
                <p className="text-sm sm:text-base text-muted-foreground whitespace-pre-wrap">
                  {nonMemberDescription}
                </p>
              </div>
            )}
          </div>

          <div className="text-center space-y-4 mt-8">
            {group?.isPublic ? (
              <>
                {(group?.memberCount || 0) >= 500 ? (
                  <div className="mx-auto max-w-md p-4 rounded-lg bg-orange-900/20 border border-orange-700/50 text-orange-300">
                    <p className="text-sm font-medium">
                      Denna grupp är full (500/500 medlemmar)
                    </p>
                  </div>
                ) : (hasPendingRequest && !group?.isOpen) ? (
                  <>
                    <div className="mx-auto max-w-md p-4 rounded-lg bg-orange-900/20 border border-orange-700/50 text-orange-300">
                      <p className="text-sm">
                        Ansökan om medlemskap skickad. Gruppägare har fått en notifiering om att granska den.
                      </p>
                    </div>
                    <button 
                      onClick={handleCancelRequest} 
                      disabled={joiningGroup}
                      className="px-6 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 font-medium transition-colors"
                    >
                      {joiningGroup ? 'Avbryter...' : 'Avbryt ansökan'}
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      onClick={handleJoinButtonClick}
                      disabled={joiningGroup}
                      className="px-6 py-3 rounded-lg bg-[#67724e] text-primary-foreground hover:bg-[#67724e]/90 disabled:opacity-50 font-medium transition-colors"
                    >
                      {joiningGroup
                        ? (group?.isOpen ? 'Bearbetar...' : 'Skickar ansökan...')
                        : (group?.isOpen ? 'Gå med i gruppen' : 'Ansök om medlemskap')}
                    </button>
                    {showSuccessMessage && (
                      <div className="mx-auto max-w-md p-4 rounded-lg bg-green-900/20 border border-green-700/50 text-green-300">
                        <p className="text-sm">
                          Din ansökan om medlemskap har skickats! Gruppägare kommer att granska och godkänna den.
                        </p>
                      </div>
                    )}
                    {errorMessage && (
                      <div className="mx-auto max-w-md p-4 rounded-lg bg-orange-900/20 border border-orange-700/50 text-orange-300">
                        <p className="text-sm">
                          {errorMessage}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Denna grupp är privat. Kontakta gruppägaren för inbjudan.
              </p>
            )}
          </div>
        </div>

        {/* Application modal - duplicated for non-members */}
        {showApplicationModal && !group?.isOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowApplicationModal(false)} />
            <div className="relative w-full max-w-md rounded-lg bg-card p-6 z-10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Ansök om medlemskap</h3>
                <button onClick={() => setShowApplicationModal(false)} className="p-2 rounded hover:bg-muted">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Motivation (obligatorisk)</label>
                  <textarea
                    value={applicationMessage}
                    onChange={(e) => {
                      if (e.target.value.length <= 200) {
                        setApplicationMessage(e.target.value);
                      }
                    }}
                    className="w-full mt-1 rounded-lg border border-border p-3 bg-background min-h-[100px] resize-none"
                    placeholder="Beskriv kort varför du vill bli medlem..."
                    maxLength={200}
                  />
                  <div className="text-xs text-muted-foreground mt-1 text-right">
                    {applicationMessage.length}/200 tecken
                  </div>
                  <p className="text-xs text-orange-300 mt-2">
                    Obs: Gruppägare kommer att se din e-postadress.
                  </p>
                </div>
                {errorMessage && (
                  <div className="p-3 rounded-lg bg-orange-900/20 border border-orange-700/50 text-orange-300 text-sm">
                    {errorMessage}
                  </div>
                )}
                <div className="flex justify-end gap-2 mt-4">
                  <button 
                    onClick={() => setShowApplicationModal(false)} 
                    className="rounded-lg px-4 py-2 border border-border hover:bg-muted"
                  >
                    Avbryt
                  </button>
                  <button
                    disabled={joiningGroup || applicationMessage.trim().length < 10}
                    onClick={handleJoinGroup}
                    className="rounded-lg px-4 py-2 bg-[#67724e] text-white hover:bg-[#67724e]/90 disabled:opacity-50"
                  >
                    {joiningGroup ? 'Skickar...' : 'Skicka ansökan'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showOpenJoinConfirm && group?.isOpen && (
          <ConfirmModal
            open={showOpenJoinConfirm}
            title="Gå med i grupp"
            message={`Vill du gå med i gruppen ${group?.name || ''}?`}
            confirmLabel={joiningGroup ? 'Bearbetar...' : 'Gå med'}
            confirmClassName="px-3 py-1 rounded bg-[#67724e] text-white hover:bg-[#67724e]/90 disabled:opacity-50"
            onCancel={() => {
              if (!joiningGroup) setShowOpenJoinConfirm(false);
            }}
            onConfirm={() => {
              if (!joiningGroup) {
                void handleJoinGroup();
              }
            }}
          />
        )}

        {/* Premium modal */}
        <PremiumRequiredModal
          open={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          feature={
            user?.subscriptionTier === 'premium' 
              ? 'Skapa fler än 30 diskussioner, 300 svar per dag, 50 anslag per vecka, 50 kalenderinlägg per dag eller 30 wiki-artiklar per dag'
              : 'Vara medlem i fler än 2 grupper, skapa fler än 1 diskussion, 20 svar per dag, 1 anslag per vecka eller skapa kalenderinlägg och wiki-artiklar'
          }
        />
      </div>
    );
  }

  // Safety check: if we're not loading but have no group, something is wrong
  if (!groupLoading && !group) {
    console.log('[GroupDetailPage] No group data available (groupAccessDenied:', groupAccessDenied, ')');
    return (
      <div className="space-y-4">
        <Link
          to="/app/groups"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          Tillbaka
        </Link>
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
          <h3 className="mt-4 text-lg font-medium">Gruppen hittades inte</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {groupAccessDenied 
              ? 'Denna grupp finns inte längre eller så har du inte tillgång till den.'
              : 'Gruppen kunde inte hittas.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Back button */}
      <Link
        to="/app/groups"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        Tillbaka
      </Link>

      {/* Header */}
      <div className="pb-4">
        <div className="flex items-start justify-between gap-2 sm:gap-4 relative">
              <div className="flex items-start gap-2 sm:gap-4 min-w-0 flex-1">
            <div className="rounded-lg sm:rounded-xl bg-orange-500/10 border border-orange-500/60 shrink-0 overflow-hidden h-14 w-14 sm:h-[72px] sm:w-[72px] flex items-center justify-center">
              {group?.imageUrl ? (
                <img src={group.imageUrl} alt="Group image" className="h-full w-full object-cover" />
              ) : (
                <Users className="h-6 w-6 sm:h-10 sm:w-10 text-orange-500" />
              )}
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <h1 className="text-lg sm:text-2xl font-bold soft-wrap mb-1">{group?.name}</h1>
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap mb-1">
                {/* Public/Private tag */}
                {typeof (group?.isPublic) !== 'undefined' ? (
                  group?.isPublic ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full border text-sm shrink-0" style={{ backgroundColor: '#4c5c41', borderColor: '#4c5c41', color: '#c9e6b5' }}>
                      <Globe className="h-3 w-3" />
                      <span>{t('groups.visibility.public')}</span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full border text-sm shrink-0" style={{ backgroundColor: '#66271f', borderColor: '#66271f', color: '#d6a9a3' }}>
                      <Lock className="h-3 w-3" />
                      <span>{t('groups.visibility.private')}</span>
                    </span>
                  )
                ) : null}
                {group?.role === 'owner' && (
                  <span className="flex items-center gap-2 px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-600 dark:bg-yellow-500/30 dark:text-yellow-200 text-sm shrink-0">
                    <Crown className="h-3 w-3" />
                    <span className="font-medium">{t('groups.roles.owner')}</span>
                  </span>
                )}
              </div>
              {/* Tags row */}
              {group?.tags && group.tags.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {group.tags.map((tag: string) => (
                    <span key={tag} className="text-sm px-2 py-0.5 rounded-full bg-muted/20 border border-muted text-muted-foreground">{tag}</span>
                  ))}
                </div>
              )}
              <p 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDescriptionExpanded(!isDescriptionExpanded);
                }}
                className={`mt-1 text-muted-foreground text-sm sm:text-base cursor-pointer hover:text-foreground transition-colors max-w-full ${
                  isDescriptionExpanded ? '' : 'line-clamp-1 sm:line-clamp-2'
                }`}
                title="Klicka för att expandera/kollapsa"
              >
                {group?.description || ''}
              </p>
              <div className="flex items-center gap-4 mt-1.5 sm:mt-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3 sm:h-4 sm:w-4" />
                  {t('groups.memberCount', { count: group?.memberCount ?? 0, count_one: '1', count_other: String(group?.memberCount ?? 0) })}
                </span>
                {group?.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
                    {group.city}
                  </span>
                )}
              </div>
            </div>
          </div>
            {/* Show Settings button only for owners, Join button for non-members */}
            <div className="relative shrink-0 self-start">
            {group ? (
              <div className="flex items-center gap-2">
                <button onClick={() => setShareInviteOpen(true)} className="p-1.5 sm:p-2 rounded-lg hover:bg-muted transition-colors bg-card" title="Dela grupp">
                  <Share2 className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                </button>
                {group.role === 'owner' && (
                  <button onClick={() => setSettingsOpen(true)} className="p-1.5 sm:p-2 rounded-lg hover:bg-muted transition-colors bg-card" title="Inställningar">
                    <Settings className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                  </button>
                )}
                {group.isPublic && !group.role && (
                  (hasPendingRequest && !group?.isOpen) ? (
                    <button
                      onClick={handleCancelRequest}
                      disabled={joiningGroup}
                      className="px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 text-sm sm:text-base font-medium shadow-sm"
                    >
                      {joiningGroup ? 'Avbryter...' : 'Avbryt ansökan'}
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        console.log('Header button clicked!', { joiningGroup, showApplicationModal, target: e.target });
                        e.preventDefault();
                        e.stopPropagation();
                        handleJoinButtonClick();
                      }}
                      className="px-3 py-2 rounded-lg bg-[#67724e] text-primary-foreground hover:bg-[#67724e]/90 disabled:opacity-50 text-sm sm:text-base font-medium shadow-sm"
                      style={{ pointerEvents: 'auto', position: 'relative', zIndex: 9999 }}
                    >
                      {joiningGroup
                        ? (group?.isOpen ? 'Bearbetar...' : 'Skickar ansökan...')
                        : (group?.isOpen ? 'Gå med i gruppen' : 'Ansök om medlemskap')}
                    </button>
                  )
                )}
              </div>
            ) : null}
            </div>
          {/** Settings button opens modal for owner/admin */}
        </div>
      </div>

        {/* Group Settings Modal */}
        {settingsOpen && (
            <GroupSettingsModal
          groupId={group?.id || ''}
          groupName={group?.name || ''}
          description={group?.description || ''}
            isPublic={Boolean(group?.isPublic)}
            imageUrl={group?.imageUrl || null}
            city={group?.city}
            onClose={() => setSettingsOpen(false)}
            onSaved={(updates) => {
              // apply optimistic update locally
              if (group) {
                setGroup({ 
                  ...group, 
                  name: updates.name ?? group.name,
                  description: updates.description ?? group.description, 
                  isPublic: typeof updates.isPublic === 'boolean' ? updates.isPublic : group.isPublic, 
                  imageUrl: updates.imageUrl ?? group.imageUrl,
                  city: updates.city ?? group.city
                });
              }
              setSettingsOpen(false);
            }}
          />
        )}

        {shareInviteOpen && (
          <div className="fixed inset-0 z-[220] bg-black/50 backdrop-blur-sm p-3 sm:p-6 overflow-y-auto" role="dialog" aria-modal="true">
            <div className="max-w-xl mx-auto mt-2 sm:mt-8 bg-card border border-border rounded-lg overflow-hidden max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h3 className="text-lg font-semibold">Dela grupp</h3>
                <button
                  onClick={() => setShareInviteOpen(false)}
                  className="p-2 rounded hover:bg-muted"
                  aria-label="Stäng"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-3 sm:p-4 flex-1 overflow-y-auto">
                <pre className="whitespace-pre-wrap break-words text-sm text-foreground bg-muted/30 border border-border rounded-lg p-3 sm:p-4 max-h-[38vh] sm:max-h-[45vh] overflow-y-auto">
{buildGroupInviteText()}
                </pre>
              </div>

              <div className="p-4 border-t border-border flex justify-end">
                <button
                  onClick={copyGroupInviteText}
                  className="px-4 py-2 rounded-lg bg-[#67724e] text-primary-foreground hover:bg-[#67724e]/90 font-medium"
                >
                  {shareInviteCopied ? 'Kopierad!' : 'Kopiera'}
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Owner without Premium warning */}
      {group && group.role === 'owner' && user?.subscriptionTier !== 'premium' && (
        <div className="p-4 rounded-lg border border-orange-700/50 bg-orange-900/20 mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-orange-300 mb-1">
                Ägarrollen kräver Premium
              </h3>
              <p className="text-sm text-orange-200/90">
                Som ägare av en beredskapsgrupp krävs ett aktivt Premium-abonnemang. 
                Uppgradera ditt konto för att fortsätta ha full tillgång till alla ägarfunktioner.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs - Two rows on mobile, single row on desktop */}
      <div className="-mx-4 sm:-mx-6 lg:-mx-8">
        <div className="border-b-4 py-3" style={{ borderColor: '#323d2d' }}>
          <div className="px-4 sm:px-6 lg:px-8">
        {/* Mobile: Two rows */}
        <div className="sm:hidden">
          {/* First row: Diskussioner, Anslagstavla, Wiki */}
          <div className="flex gap-0.5">
            {topRowTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 border-b-2 border rounded-lg transition-colors text-sm relative ${
                  activeTab === tab.id
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <tab.icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{tab.label}</span>
                {tab.badge && tab.badge > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 rounded-full text-white text-xs font-medium shadow-lg" style={{ backgroundColor: "#945123" }}>
                    {tab.badge}
                  </span>
                )}
                {tab.id === 'members' && (
                  <div className="absolute -top-1 -right-1 flex gap-1">
                    {pendingRequestsCount > 0 && (
                      <span className="flex items-center justify-center w-5 h-5 rounded-full text-white text-xs font-medium shadow-lg" style={{ backgroundColor: "#945123" }}>
                        {pendingRequestsCount}
                      </span>
                    )}
                    {newMembersCount > 0 && (
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-white text-xs font-medium shadow-lg">
                        {newMembersCount}
                      </span>
                    )}
                  </div>
                )}
              </button>
            ))}
          </div>
          {/* Second row: Kalender, Medlemmar */}
          <div className="flex gap-0.5">
            {bottomRowTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 border-b-2 border rounded-lg transition-colors text-sm relative ${
                  activeTab === tab.id
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <tab.icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{tab.label}</span>
                {tab.badge && tab.badge > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 rounded-full text-white text-xs font-medium shadow-lg" style={{ backgroundColor: "#945123" }}>
                    {tab.badge}
                  </span>
                )}
                {tab.id === 'members' && (
                  <div className="absolute -top-1 -right-1 flex gap-1">
                    {pendingRequestsCount > 0 && (
                      <span className="flex items-center justify-center w-5 h-5 rounded-full text-white text-xs font-medium shadow-lg" style={{ backgroundColor: "#945123" }}>
                        {pendingRequestsCount}
                      </span>
                    )}
                    {newMembersCount > 0 && (
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-white text-xs font-medium shadow-lg">
                        {newMembersCount}
                      </span>
                    )}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Desktop: Single row with all tabs */}
        <div className="hidden sm:flex gap-1 pb-px">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 border rounded-lg transition-colors whitespace-nowrap text-base relative ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.label}</span>
              {tab.badge && tab.badge > 0 && (
                <span className="flex items-center justify-center w-5 h-5 rounded-full text-white text-xs font-medium shadow-lg" style={{ backgroundColor: "#945123" }}>
                  {tab.badge}
                </span>
              )}
              {tab.id === 'members' && (
                <div className="flex gap-1">
                  {pendingRequestsCount > 0 && (
                    <span className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-white text-xs font-medium shadow-lg" style={{ backgroundColor: "#945123" }}>
                      {pendingRequestsCount}
                    </span>
                  )}
                  {newMembersCount > 0 && (
                    <span className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-green-500 text-white text-xs font-medium shadow-lg">
                      {newMembersCount}
                    </span>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
          </div>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'threads' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              <h2 className="text-xl sm:text-2xl font-semibold">{t('groups.threads.title')}</h2>
            </div>
            <div className="flex items-center gap-2 relative">
              {threads.length > 0 && (
                <div className="relative">
                  <button 
                    onClick={markAllThreadsAsRead} 
                    className="flex items-center gap-1.5 rounded-lg bg-muted px-3 py-1.5 text-sm sm:text-base font-medium hover:bg-muted/80 transition-colors shrink-0"
                    title="Markera alla som lästa"
                  >
                    <CheckCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Markera alla som lästa</span>
                  </button>
                  {showMarkAllReadSuccess && (
                    <div className="absolute top-full mt-2 right-0 bg-[#67724e] text-primary-foreground px-3 py-1.5 rounded-lg text-sm shadow-lg animate-in fade-in slide-in-from-top-2 duration-300 z-10 max-w-[calc(100vw-2rem)]">
                      ✓ Alla diskussioner markerade som lästa
                    </div>
                  )}
                </div>
              )}
              <button onClick={() => setShowNewThreadForm((s) => !s)} className="flex items-center gap-1.5 rounded-lg bg-[#67724e] px-3 py-1.5 text-sm sm:text-base font-medium text-primary-foreground hover:bg-[#67724e]/90 transition-colors shrink-0">
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">{t('groups.threads.new')}</span>
              </button>
            </div>
          </div>

          {showNewThreadForm && (
            <div className="rounded-lg border border-border bg-card p-3">
              {/* Template button */}
              <div className="relative mb-3">
                <button
                  type="button"
                  onClick={() => setShowThreadTemplateDropdown(!showThreadTemplateDropdown)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-background hover:bg-muted transition-colors text-sm font-medium"
                >
                  <FileStack className="h-4 w-4" />
                  Mallar
                  <ChevronDown className="h-4 w-4" />
                </button>
                
                {/* Template dropdown */}
                {showThreadTemplateDropdown && (
                  <div className="absolute top-full mt-2 left-0 w-80 max-w-full bg-background border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                    <div className="py-1">
                      {threadTemplates.map((template, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setNewThreadTitle(template.title);
                            setNewThreadContent(template.content);
                            setShowThreadTemplateDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-muted transition-colors border-b border-border last:border-b-0"
                        >
                          <div className="font-medium text-foreground mb-1 text-sm">{template.title}</div>
                          <div className="text-xs text-muted-foreground line-clamp-2">
                            {template.content}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <input value={newThreadTitle} onChange={(e) => setNewThreadTitle(e.target.value)} placeholder={t('groups.threads.newTitlePlaceholder')} maxLength={50} className="w-full mb-2 rounded-lg border border-border p-2 bg-background" />
              <textarea value={newThreadContent} onChange={(e) => setNewThreadContent(e.target.value)} placeholder={t('groups.threads.newContentPlaceholder')} maxLength={300} className="w-full rounded-lg border border-border p-2 bg-background mb-2" rows={4} />
              
              {/* Image upload */}
              <div className="mb-2">
                {newThreadImagePreview && (
                  <div className="mb-2">
                    <img src={newThreadImagePreview} alt="Preview" className="max-w-full h-auto rounded-lg border border-border max-h-64" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  id="thread-image-upload"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files && e.target.files[0];
                    if (!f) {
                      setNewThreadImageFile(null);
                      setNewThreadImagePreview(null);
                      setNewThreadImageBase64(null);
                      return;
                    }
                    setNewThreadImageFile(f);
                    try {
                      const base64 = await resizeToMaxSize(f, 2 * 1024 * 1024, 800);
                      setNewThreadImageBase64(base64);
                      setNewThreadImagePreview(`data:image/jpeg;base64,${base64}`);
                    } catch (err) {
                      console.error('Failed to resize image:', err);
                      alert('Det gick inte att bearbeta bilden. Försök med en annan bild.');
                      setNewThreadImageFile(null);
                      setNewThreadImagePreview(null);
                      setNewThreadImageBase64(null);
                    }
                  }}
                />
                <div className="flex gap-2">
                  <label htmlFor="thread-image-upload" className="px-3 py-1 rounded border border-border cursor-pointer hover:bg-muted inline-flex items-center gap-1.5 text-sm">
                    <Image className="w-4 h-4" />
                    Bifoga bild
                  </label>
                  {newThreadImagePreview && (
                    <button type="button" onClick={() => { setNewThreadImageFile(null); setNewThreadImagePreview(null); setNewThreadImageBase64(null); }} className="px-3 py-1 rounded border border-border text-muted-foreground hover:bg-muted text-sm">Ta bort bild</button>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button onClick={() => { setShowNewThreadForm(false); setNewThreadTitle(''); setNewThreadContent(''); setNewThreadImageFile(null); setNewThreadImagePreview(null); setNewThreadImageBase64(null); setShowThreadTemplateDropdown(false); }} className="px-3 py-1 rounded border border-border">{t('common.cancel')}</button>
                <button 
                  onClick={async () => {
                  if (!group) return;
                  const title = (newThreadTitle || '').trim();
                  const content = (newThreadContent || '').trim();
                  if (title.length < 3 || content.length < 3) {
                    return;
                  }
                  try {
                    let imageUrl: string | undefined;
                    let imageBlobName: string | undefined;

                    // Upload image if present
                    if (newThreadImageFile) {
                      const data = newThreadImageBase64 ?? await resizeFileToBase64(newThreadImageFile, 800, 0.85);
                      const uploadRes = await apiPost(`/groups/${group.id}/threads/images`, {
                        filename: newThreadImageFile.name,
                        contentType: 'image/jpeg',
                        data
                      });
                      if (uploadRes.success && uploadRes.data) {
                        const uploadData = uploadRes.data as { url: string; blobName: string };
                        imageUrl = uploadData.url;
                        imageBlobName = uploadData.blobName;
                      }
                    }

                    const endpoint = `/groups/${group.id}/threads`;
                    console.debug('Create thread POST', { endpoint, payload: { title, content, imageUrl, imageBlobName } });
                    const res = await apiPost(endpoint, { title, content, imageUrl, imageBlobName });
                    if (res.success) {
                      const createdThread = res.data as any;
                      
                      // Mark the newly created thread as read immediately
                      if (createdThread?.id) {
                        markThreadAsRead(group.id, createdThread.id).then(() => {
                          setThreadReadStatuses(prev => ({
                            ...prev,
                            [createdThread.id]: new Date().toISOString()
                          }));
                          queryClient.invalidateQueries({ queryKey: ['groups-unread-counts'] });
                        }).catch(err => console.error('Failed to mark created thread as read:', err));
                      }
                      
                      const list = await apiGet<any[]>(`/groups/${group.id}/threads`);
                      if (list.success) {
                        const normalized = (list.data || []).map((t: any) => ({
                          ...t,
                          createdByDisplayName: t.createdByDisplayName || (t.createdBy === user?.id ? user?.displayName : t.createdByDisplayName),
                          startMessage: t.startMessage ? ({
                            ...t.startMessage,
                            createdByDisplayName: t.startMessage.createdByDisplayName || (t.startMessage.createdBy === user?.id ? user?.displayName : t.startMessage.createdByDisplayName),
                          }) : null,
                          _lastActivityAt: (t.lastReplyAt && new Date(t.lastReplyAt)) || (t.startMessage && t.startMessage.createdAt && new Date(t.startMessage.createdAt)) || (t.createdAt && new Date(t.createdAt)) || new Date(0),
                        }))
                        .sort((a: any, b: any) => {
                          if (a.isPinned && !b.isPinned) return -1;
                          if (!a.isPinned && b.isPinned) return 1;
                          return (b._lastActivityAt?.getTime() || 0) - (a._lastActivityAt?.getTime() || 0);
                        });
                        setThreads(normalized);
                      }
                      setShowNewThreadForm(false);
                      setNewThreadTitle(''); 
                      setNewThreadContent('');
                      setNewThreadImageFile(null);
                      setNewThreadImagePreview(null);
                      setNewThreadImageBase64(null);
                    } else {
                      console.error('Create thread failed - server response', { endpoint, res });
                      alert(res.error || t('groups.threads.createFailed'));
                    }
                  } catch (e) {
                    console.error('Create thread failed', e);
                    alert(t('groups.threads.createFailed'));
                  }
                }} 
                disabled={newThreadTitle.trim().length < 3 || newThreadContent.trim().length < 3}
                className="px-3 py-1 rounded bg-[#67724e] text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('groups.threads.create')}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {threadsLoading && (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-lg border border-border bg-card p-3 sm:p-4 animate-pulse">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="h-5 bg-muted rounded w-2/3" />
                        <div className="h-4 bg-muted rounded w-24" />
                      </div>
                      <div className="h-4 bg-muted rounded w-1/3" />
                      <div className="h-4 bg-muted rounded w-full" />
                      <div className="h-4 bg-muted rounded w-5/6" />
                      <div className="h-4 bg-muted rounded w-20 mt-1" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!threadsLoading && threads.length === 0 && <p className="text-sm text-muted-foreground">{t('groups.threads.noThreads')}</p>}
            {!threadsLoading && threads.map((thread) => {
              const isUnread = isThreadUnread(thread);
              return (
              <div 
                key={thread.id} 
                onClick={() => openThread(thread.id)} 
                className={`rounded-lg border bg-card p-2.5 sm:p-4 hover:border-primary/50 transition-colors cursor-pointer ${
                  isUnread ? 'border-2 bg-[#945123]/5' : 'border-border'
                }`}
                style={isUnread ? { borderColor: '#945123' } : {}}
              >
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                      <MessageSquare className={`h-4 w-4 shrink-0 ${isUnread ? 'text-[#945123]' : 'text-muted-foreground'}`} />
                      <h3 className={`font-medium text-sm sm:text-lg truncate ${isUnread ? 'font-bold' : ''}`}>{thread.title}</h3>
                      {isUnread && (
                        <span className="px-2 py-0.5 rounded-full text-white text-xs font-bold shrink-0" style={{ backgroundColor: '#945123' }}>
                          OLÄST
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground shrink-0">{thread.lastReplyAt ? new Date(thread.lastReplyAt).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : ''}</span>
                      {/* Owner-only pin/unpin */}
                      {group?.role === 'owner' && (
                        <button onClick={async (e) => { e.stopPropagation(); setThreadsConfirm({ type: 'pin', id: thread.id, title: thread.title, isPinned: !!thread.isPinned }); }} className="p-1 rounded hover:bg-muted">
                          {thread.isPinned ? <Pin className="h-4 w-4 text-yellow-500" /> : <Pin className="h-4 w-4 text-muted-foreground" />}
                        </button>
                      )}
                    </div>
                  </div>
                    <p className="text-sm text-muted-foreground">{t('groups.threads.by', { author: thread.createdByDisplayName || thread.createdBy })}</p>
                    {thread.startMessage && (
                      <>
                        <p className="text-sm sm:text-base text-muted-foreground mt-1 line-clamp-2 font-semibold">{thread.startMessage.content}</p>
                        {thread.startMessage.imageUrl && (
                          <div className="mt-2">
                            <img src={thread.startMessage.imageUrl} alt="" className="h-32 md:h-40 w-auto rounded-lg border border-border object-cover" />
                          </div>
                        )}
                        <p className="text-sm text-muted-foreground mt-1">{Math.max(0, (thread.replyCount ?? 0) - (thread.startMessage ? 1 : 0))} {t('groups.threads.replies')}</p>
                      </>
                    )}
                  <div className="flex justify-end mt-2">
                    {/* Delete visible to owner or thread creator */}
                      {(group?.role === 'owner' || String(thread.createdBy) === String(currentUserId)) && (
                      <button onClick={(e) => { e.stopPropagation(); setThreadsConfirm({ type: 'thread', id: thread.id, title: thread.title }); }} className="text-sm px-2 py-1 rounded border border-border" aria-label={t('common.delete')}>
                        <Trash2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); openThread(thread.id); }} className="ml-2 text-sm px-2 py-1 rounded border border-border">{t('common.open', 'Öppna')}</button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Thread panel */}
          {openThreadId && (
            <ThreadModal
              open={!!openThreadId}
              threadId={openThreadId}
              threadTitle={openThreadTitle}
              groupId={group?.id || null}
              onReportUser={(target) => setReportTarget(target)}
              onClose={() => { setOpenThreadId(null); setOpenThreadMessages([]); setOpenThreadTitle(null); setReplyImageFile(null); setReplyImagePreview(null); setReplyImageBase64(null); }}
              loading={openThreadLoading}
              messages={openThreadMessages}
              setMessages={(msgs: any[]) => setOpenThreadMessages(msgs)}
              draft={messageDraft}
              setDraft={(v: string) => setMessageDraft(v)}
              replyImageFile={replyImageFile}
              setReplyImageFile={setReplyImageFile}
              replyImagePreview={replyImagePreview}
              setReplyImagePreview={setReplyImagePreview}
              replyImageBase64={replyImageBase64}
              setReplyImageBase64={setReplyImageBase64}
              onSend={async () => {
                if (!group || !openThreadId || !messageDraft) return;

                // Premium check: free users can send max 20 replies per day, premium users max 300
                if (user) {
                  try {
                    const res = await apiGet<{ todayCount: number }>(`/groups/${group.id}/threads/replies-count-today`);
                    if (res.success && res.data) {
                      const limit = user.subscriptionTier === 'premium' ? 300 : 20;
                      if (res.data.todayCount >= limit) {
                        setShowPremiumModal(true);
                        return;
                      }
                    }
                  } catch (err) {
                    console.error('Failed to check reply count', err);
                    // Continue if check fails
                  }
                }

                try {
                  let imageUrl: string | undefined;
                  let imageBlobName: string | undefined;

                  // Upload image if present
                  if (replyImageFile) {
                    const data = replyImageBase64 ?? await resizeFileToBase64(replyImageFile, 800, 0.85);
                    const uploadRes = await apiPost(`/groups/${group.id}/threads/images`, {
                      filename: replyImageFile.name,
                      contentType: 'image/jpeg',
                      data
                    });
                    if (uploadRes.success && uploadRes.data) {
                      const uploadData = uploadRes.data as { url: string; blobName: string };
                      imageUrl = uploadData.url;
                      imageBlobName = uploadData.blobName;
                    }
                  }

                  const res = await apiPost(`/groups/${group.id}/threads/${openThreadId}/messages`, { content: messageDraft, imageUrl, imageBlobName });
                  if (res.success) {
                    const created = res.data as any;
                    const msgToAdd = {
                      ...created,
                      createdBy: created.createdBy || user?.id,
                      createdByDisplayName: created.createdByDisplayName || user?.displayName || created.createdBy,
                    };
                    setOpenThreadMessages((s) => [...s, msgToAdd]);
                    setMessageDraft('');
                    setReplyImageFile(null);
                    setReplyImagePreview(null);
                    setReplyImageBase64(null);
                    
                    // Mark thread as read after replying (update local state immediately)
                    setThreadReadStatuses(prev => ({
                      ...prev,
                      [openThreadId]: new Date().toISOString()
                    }));
                    markThreadAsRead(group.id, openThreadId).then(() => {
                      queryClient.invalidateQueries({ queryKey: ['groups-unread-counts'] });
                    }).catch(err => console.error('Failed to mark thread as read after reply:', err));
                    
                    const list = await apiGet<any[]>(`/groups/${group.id}/threads`);
                    if (list.success) setThreads(list.data || []);
                  }
                } catch (e) { console.error('Reply failed', e); }
              }}
              onDeleteMessage={async (messageId: string) => {
                if (!group || !openThreadId) return;
                try {
                  await apiDelete(`/groups/${group.id}/threads/${openThreadId}/messages/${encodeURIComponent(messageId)}`);
                  const res = await apiGet<any>(`/groups/${group.id}/threads/${openThreadId}`);
                  if (res.success) setOpenThreadMessages(res.data.messages || []);
                } catch (e) { console.error('Delete message failed', e); }
              }}
            />
          )}
        </div>
      )}

      {activeTab === 'wiki' && (
        <div className="space-y-4">
          {/* Wiki Editor/Viewer Modal */}
          {(showWikiEditor || viewingArticle) && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 overflow-hidden bg-black/40 backdrop-blur-sm">
              <div className="absolute inset-0" onClick={() => {
                setShowWikiEditor(false);
                setViewingArticle(null);
                setEditingArticle(null);
                setWikiTitle('');
                setWikiContent('');
                setWikiTags([]);
                setWikiIsProtected(false);
              }} />
              <div className="relative w-full max-w-5xl h-[90vh] rounded-lg bg-card p-4 sm:p-6 z-10 flex flex-col overflow-hidden">
                {viewingArticle ? (
                  // Article Viewer
                  <>
                    <div className="flex items-start justify-between gap-4 mb-4 flex-shrink-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h2 className="text-xl sm:text-2xl font-bold">{viewingArticle.title}</h2>
                          {viewingArticle.isProtected && (
                            <Lock className="h-5 w-5 text-yellow-600 dark:text-yellow-400 shrink-0" />
                          )}
                        </div>
                        {viewingArticle.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {viewingArticle.tags.map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-sm"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-sm text-muted-foreground">
                          Av {viewingArticle.authorName} • {new Date(viewingArticle.updatedAt).toLocaleDateString('sv-SE')}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {(!viewingArticle.isProtected || group?.role === 'owner') && (
                          <>
                            <button
                              onClick={() => {
                                setEditingArticle(viewingArticle);
                                setWikiTitle(viewingArticle.title);
                                setWikiContent(viewingArticle.content);
                                setWikiTags(viewingArticle.tags);
                                setWikiIsProtected(viewingArticle.isProtected);
                                setViewingArticle(null);
                                setShowWikiEditor(true);
                              }}
                              className="p-2 rounded-lg hover:bg-muted transition-colors"
                              title="Redigera"
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setDeletingArticleId(viewingArticle.id)}
                              className="p-2 rounded-lg hover:bg-red-500/10 text-red-600 transition-colors"
                              title="Ta bort"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setViewingArticle(null)}
                          className="p-2 rounded-lg hover:bg-muted transition-colors"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto w-full prose prose-sm sm:prose dark:prose-invert max-w-none sm:max-w-none">
                      {convertTablesThatNeedBr(viewingArticle.content).map((block, idx) => {
                        if (block.type === 'html') {
                          return <div key={idx} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.content) }} />;
                        }
                        return (
                          <ReactMarkdown 
                            key={idx}
                            remarkPlugins={[remarkGfm]} 
                            rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
                            components={{
                              p: ({ node: _node, ...props }) => <p className="whitespace-pre-line" {...props} />,
                            }}
                          >
                            {block.content}
                          </ReactMarkdown>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  // Article Editor
                  <>
                    <div className="flex items-center justify-between gap-4 mb-4 flex-shrink-0">
                      <h2 className="text-xl font-bold">{editingArticle ? 'Redigera artikel' : 'Ny artikel'}</h2>
                      <button
                        onClick={() => {
                          setShowWikiEditor(false);
                          setEditingArticle(null);
                          setWikiTitle('');
                          setWikiContent('');
                          setWikiTags([]);
                          setWikiIsProtected(false);
                          setShowWikiTemplateDropdown(false);
                        }}
                        className="p-2 rounded-lg hover:bg-muted transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-4">
                      {/* Wiki article template dropdown */}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowWikiTemplateDropdown(!showWikiTemplateDropdown)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-input bg-background hover:bg-muted/50 transition-colors text-sm"
                        >
                          <div className="flex items-center gap-2">
                            <FileStack className="h-4 w-4" />
                            <span>Välj mall</span>
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform ${showWikiTemplateDropdown ? 'rotate-180' : ''}`} />
                        </button>
                        {showWikiTemplateDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                            {wikiArticleTemplates.map((template, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => {
                                  setWikiTitle(template.title);
                                  setWikiContent(template.content);
                                  if (template.suggestedTags) {
                                    setWikiTags(template.suggestedTags);
                                  }
                                  setShowWikiTemplateDropdown(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-muted transition-colors border-b border-border last:border-b-0"
                              >
                                <div className="font-medium text-sm">{template.title}</div>
                                <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{template.content.split('\n')[0].replace(/^#+ /, '')}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Rubrik</label>
                        <input
                          type="text"
                          value={wikiTitle}
                          onChange={(e) => setWikiTitle(e.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base"
                          placeholder="Artikelrubrik"
                          maxLength={200}
                        />
                      </div>
                      <div>
                        <label className="flex items-center gap-1 text-sm font-medium mb-2">
                          Innehåll (Markdown)
                          <button
                            type="button"
                            onClick={() => setShowMarkdownHelp(true)}
                            className="text-primary hover:text-primary/80 transition-colors"
                            title="Visa Markdown-hjälp"
                          >
                            <HelpCircle className="h-4 w-4" />
                          </button>
                          <span className="ml-auto text-xs text-muted-foreground">{wikiContent.length}/5000</span>
                        </label>
                        <textarea
                          maxLength={5000}
                          value={wikiContent}
                          onChange={(e) => setWikiContent(e.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base resize-none font-mono text-sm"
                          placeholder="Skriv artikelinnehåll i Markdown-format..."
                          rows={12}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Taggar
                          <span className="ml-2 text-xs text-muted-foreground">{wikiTags.length}/20 taggar</span>
                        </label>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {wikiTags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-sm"
                            >
                              {tag}
                              <button
                                onClick={() => setWikiTags(wikiTags.filter(t => t !== tag))}
                                className="hover:text-primary/70"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              maxLength={50}
                              value={wikiTagInput}
                              onChange={(e) => setWikiTagInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && wikiTagInput.trim()) {
                                  e.preventDefault();
                                  if (wikiTags.length < 20 && !wikiTags.includes(wikiTagInput.trim())) {
                                    setWikiTags([...wikiTags, wikiTagInput.trim()]);
                                  }
                                  setWikiTagInput('');
                                }
                              }}
                              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                              placeholder="Lägg till tagg och tryck Enter"
                              disabled={wikiTags.length >= 20}
                            />
                            {wikiTagInput.length > 0 && (
                              <span className="absolute right-2 top-2 text-xs text-muted-foreground">{wikiTagInput.length}/50</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {group?.role === 'owner' && (
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="wiki-protected"
                            checked={wikiIsProtected}
                            onChange={(e) => setWikiIsProtected(e.target.checked)}
                            className="rounded"
                          />
                          <label htmlFor="wiki-protected" className="text-sm flex items-center gap-2">
                            <Lock className="h-4 w-4" />
                            Skyddad artikel (endast ägare kan redigera)
                          </label>
                        </div>
                      )}
                      <div className="flex justify-end gap-2 pt-4 border-t">
                        <button
                          onClick={() => {
                            setShowWikiEditor(false);
                            setEditingArticle(null);
                            setWikiTitle('');
                            setWikiContent('');
                            setWikiTags([]);
                            setWikiIsProtected(false);
                            setShowWikiTemplateDropdown(false);
                          }}
                          className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                        >
                          Avbryt
                        </button>
                        <button
                          onClick={async () => {
                            if (!wikiTitle.trim() || !wikiContent.trim() || !groupId) return;
                            try {
                              if (editingArticle) {
                                const success = await updateArticle(groupId, editingArticle.id, {
                                  title: wikiTitle,
                                  content: wikiContent,
                                  tags: wikiTags,
                                  isProtected: wikiIsProtected,
                                });
                                if (success) {
                                  const articles = useGroupWikiStore.getState().articlesByGroup[groupId] || [];
                                  setWikiArticles(articles);
                                  setShowWikiEditor(false);
                                  setEditingArticle(null);
                                  setWikiTitle('');
                                  setWikiContent('');
                                  setWikiTags([]);
                                  setWikiIsProtected(false);
                                  setShowWikiTemplateDropdown(false);
                                  // Update wikiLastVisited so the edited article doesn't appear as "new" to the author
                                  setWikiLastVisited(new Date().toISOString());
                                }
                              } else {
                                const article = await createArticle(groupId, {
                                  title: wikiTitle,
                                  content: wikiContent,
                                  tags: wikiTags,
                                  isProtected: wikiIsProtected,
                                });
                                if (article) {
                                  const articles = useGroupWikiStore.getState().articlesByGroup[groupId] || [];
                                  setWikiArticles(articles);
                                  setShowWikiEditor(false);
                                  setWikiTitle('');
                                  setWikiContent('');
                                  setWikiTags([]);
                                  setWikiIsProtected(false);
                                  setShowWikiTemplateDropdown(false);
                                  // Update wikiLastVisited so the newly created article doesn't appear as "new" to the author
                                  setWikiLastVisited(new Date().toISOString());
                                }
                              }
                            } catch (e) {
                              console.error('Failed to save article', e);
                            }
                          }}
                          disabled={!wikiTitle.trim() || !wikiContent.trim()}
                          className="px-4 py-2 rounded-lg bg-[#67724e] text-white hover:bg-[#67724e]/90 disabled:opacity-50 transition-colors"
                        >
                          {editingArticle ? 'Spara' : 'Skapa'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Delete Confirmation */}
          {deletingArticleId && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
              <div className="absolute inset-0 bg-black/40" onClick={() => setDeletingArticleId(null)} />
              <div className="relative w-full max-w-md rounded-lg bg-card p-6 z-10">
                <h3 className="text-lg font-semibold mb-2">Bekräfta radering</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Är du säker på att du vill ta bort denna artikel? Denna åtgärd går inte att ångra.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setDeletingArticleId(null)}
                    className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={async () => {
                      if (!groupId) return;
                      const success = await deleteWikiArticle(groupId, deletingArticleId);
                      if (success) {
                        const articles = useGroupWikiStore.getState().articlesByGroup[groupId] || [];
                        setWikiArticles(articles);
                        setDeletingArticleId(null);
                        setViewingArticle(null);
                      }
                    }}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    Ta bort
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              <h2 className="text-xl sm:text-2xl font-semibold">{t('groups.wiki.title')}</h2>
            </div>
            <button
              onClick={() => setShowWikiEditor(true)}
              className="flex items-center gap-1.5 rounded-lg bg-[#67724e] px-3 py-1.5 text-sm sm:text-base font-medium text-primary-foreground hover:bg-[#67724e]/90 transition-colors shrink-0"
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">{t('groups.wiki.new')}</span>
              <span className="xs:hidden">Ny</span>
            </button>
          </div>

          {/* Search and Filters */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={wikiSearch}
                onChange={(e) => setWikiSearch(e.target.value)}
                placeholder="Sök i artiklar..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-base"
              />
            </div>
            {allWikiTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setWikiTagFilter(null)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    !wikiTagFilter
                      ? 'bg-[#67724e] text-white'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  Alla
                </button>
                {allWikiTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setWikiTagFilter(tag === wikiTagFilter ? null : tag)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      tag === wikiTagFilter
                        ? 'bg-[#67724e] text-white'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Articles List */}
          {wikiLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-xl border border-border bg-card p-4 sm:p-5 animate-pulse">
                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 bg-muted rounded shrink-0" />
                    <div className="flex-1 space-y-3">
                      <div className="h-6 bg-muted rounded w-3/4" />
                      <div className="h-4 bg-muted rounded w-full" />
                      <div className="h-4 bg-muted rounded w-5/6" />
                      <div className="flex gap-2 mt-2">
                        <div className="h-5 bg-muted rounded w-16" />
                        <div className="h-5 bg-muted rounded w-20" />
                      </div>
                      <div className="h-4 bg-muted rounded w-2/3 mt-2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredWikiArticles.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-border p-4 sm:p-8 text-center">
              <FileText className="h-8 w-8 sm:h-12 sm:w-12 mx-auto text-muted-foreground mb-2" />
              <h3 className="font-semibold text-sm sm:text-base mb-1">
                {wikiArticles.length === 0 ? 'Inga artiklar ännu' : 'Inga artiklar matchar filtret'}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base">
                {wikiArticles.length === 0 
                  ? 'Skapa den första artikeln i gruppens wiki'
                  : 'Prova att ändra sökningen eller filtret'}
              </p>
            </div>
          ) : (
            <div className="grid gap-3 sm:gap-4">
              {filteredWikiArticles.map((article) => {
                // Calculate if article is new (created or updated after last visit)
                const currentUserId = user?.id;
                const members = Array.isArray(group?.members) ? group.members : [];
                const userMember = members.find((m: any) => String(m.userId || m.id) === String(currentUserId));
                const joinedAt = userMember?.joinedAt;
                const referenceDate = wikiLastVisitedForDisplay ? new Date(wikiLastVisitedForDisplay) : (joinedAt ? new Date(joinedAt) : null);
                const isAuthor = String(article.authorId) === String(currentUserId);
                
                // Check both createdAt and updatedAt
                const createdDate = new Date(article.createdAt);
                const updatedDate = article.updatedAt ? new Date(article.updatedAt) : createdDate;
                const latestChange = updatedDate > createdDate ? updatedDate : createdDate;
                const isNew = !isAuthor && referenceDate ? latestChange > referenceDate : false;
                const isUpdated = updatedDate > createdDate; // Article has been edited after creation
                
                return (
                <div
                  key={article.id}
                  onClick={() => setViewingArticle(article)}
                  className="rounded-xl border border-border bg-card p-4 sm:p-5 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer"
                  style={isNew ? { borderColor: '#945123' } : {}}
                >
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-primary shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 mb-2">
                        <h3 className="font-semibold text-base sm:text-lg flex-1">{article.title}</h3>
                        {isNew && (
                          <span className="px-2 py-1 rounded-md text-xs font-medium text-white border" style={{ backgroundColor: '#945123', borderColor: '#945123' }}>
                            {isUpdated ? 'Uppdaterad' : 'Ny'}
                          </span>
                        )}
                        {article.isProtected && (
                          <Lock className="h-4 w-4 text-yellow-600 dark:text-yellow-400 shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                        {article.content
                          .replace(/#{1,6}\s+/g, '') // Remove headers
                          .replace(/(\*\*|__)(.*?)\1/g, '$2') // Remove bold
                          .replace(/(\*|_)(.*?)\1/g, '$2') // Remove italic
                          .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links, keep text
                          .replace(/`([^`]+)`/g, '$1') // Remove inline code
                          .replace(/^[\s-*+>]+/gm, '') // Remove list markers and blockquotes
                          .replace(/\n+/g, ' ') // Replace newlines with spaces
                          .substring(0, 200)
                          .trim()}...
                      </p>
                      {article.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {article.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Av {article.authorName} • Uppdaterad {new Date(article.updatedAt).toLocaleDateString('sv-SE')}
                      </p>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'calendar' && (
        <div className="space-y-4">
          {/* Event Editor/Viewer Modal */}
          {(showEventEditor || viewingEvent) && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-black/40 backdrop-blur-sm">
              <div className="absolute inset-0 z-[9998]" onClick={() => {
                setShowEventEditor(false);
                setViewingEvent(null);
                setEditingEvent(null);
                setEventTitle('');
                setEventDescription('');
                setEventDate('');
                setEventTags([]);
                setEventLink('');
                setEventIsProtected(false);
              }} />
              <div className="relative w-full max-w-3xl max-h-[90vh] rounded-lg bg-card p-4 sm:p-6 z-[9999] overflow-y-auto">
                {viewingEvent ? (
                  // Event Viewer
                  <>
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h2 className="text-xl sm:text-2xl font-bold">{viewingEvent.title}</h2>
                          {viewingEvent.isProtected && (
                            <Lock className="h-5 w-5 text-yellow-600 dark:text-yellow-400 shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                          <Calendar className="h-4 w-4" />
                          <span>{new Date(viewingEvent.date).toLocaleDateString('sv-SE', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</span>
                        </div>
                        {viewingEvent.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {viewingEvent.tags.map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-sm"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-sm text-muted-foreground mb-4">
                          Av {viewingEvent.authorName} • {new Date(viewingEvent.updatedAt).toLocaleDateString('sv-SE')}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {(!viewingEvent.isProtected || group?.role === 'owner') && (
                          <>
                            <button
                              onClick={() => {
                                setEditingEvent(viewingEvent);
                                setEventTitle(viewingEvent.title);
                                setEventDescription(viewingEvent.description);
                                setEventDate(viewingEvent.date);
                                setEventTags(viewingEvent.tags);
                                setEventLink(viewingEvent.link || '');
                                setEventIsProtected(viewingEvent.isProtected);
                                setViewingEvent(null);
                                setShowEventEditor(true);
                              }}
                              className="p-2 rounded-lg hover:bg-muted transition-colors"
                              title="Redigera"
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setDeletingEventId(viewingEvent.id)}
                              className="p-2 rounded-lg hover:bg-red-500/10 text-red-600 transition-colors"
                              title="Ta bort"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setViewingEvent(null)}
                          className="p-2 rounded-lg hover:bg-muted transition-colors"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <h3 className="font-semibold mb-2">Beskrivning</h3>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{viewingEvent.description}</p>
                      </div>
                      {viewingEvent.link && (
                        <div>
                          <h3 className="font-semibold mb-2">Länk</h3>
                          <a 
                            href={viewingEvent.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline soft-wrap"
                          >
                            {viewingEvent.link}
                          </a>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  // Event Editor
                  <>
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <h2 className="text-xl font-bold">{editingEvent ? 'Redigera händelse' : 'Ny händelse'}</h2>
                      <button
                        onClick={() => {
                          setShowEventEditor(false);
                          setEditingEvent(null);
                          setEventTitle('');
                          setEventDescription('');
                          setEventDate('');
                          setEventTags([]);
                          setEventLink('');
                          setEventIsProtected(false);
                          setShowEventTemplateDropdown(false);
                        }}
                        className="p-2 rounded-lg hover:bg-muted transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      {/* Event template dropdown */}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowEventTemplateDropdown(!showEventTemplateDropdown)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-input bg-background hover:bg-muted/50 transition-colors text-sm"
                        >
                          <div className="flex items-center gap-2">
                            <FileStack className="h-4 w-4" />
                            <span>Välj mall</span>
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform ${showEventTemplateDropdown ? 'rotate-180' : ''}`} />
                        </button>
                        {showEventTemplateDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                            {eventTemplates.map((template, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => {
                                  setEventTitle(template.title);
                                  setEventDescription(template.description);
                                  if (template.suggestedTags) {
                                    setEventTags(template.suggestedTags);
                                  }
                                  setShowEventTemplateDropdown(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-muted transition-colors border-b border-border last:border-b-0"
                              >
                                <div className="font-medium text-sm">{template.title}</div>
                                <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{template.description}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Rubrik</label>
                        <input
                          type="text"
                          value={eventTitle}
                          onChange={(e) => setEventTitle(e.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base"
                          placeholder="Händelserubrik"
                          maxLength={200}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Datum och tid</label>
                        <input
                          type="datetime-local"
                          value={eventDate ? new Date(eventDate).toISOString().slice(0, 16) : ''}
                          onChange={(e) => setEventDate(e.target.value ? new Date(e.target.value).toISOString() : '')}
                          lang="sv-SE"
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Beskrivning
                          <span className="ml-2 text-xs text-muted-foreground">{eventDescription.length}/5000</span>
                        </label>
                        <textarea
                          maxLength={5000}
                          value={eventDescription}
                          onChange={(e) => setEventDescription(e.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base resize-none"
                          placeholder="Beskrivning av händelsen..."
                          rows={6}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Länk (valfritt)
                          <span className="ml-2 text-xs text-muted-foreground">{eventLink.length}/500</span>
                        </label>
                        <input
                          type="url"
                          maxLength={500}
                          value={eventLink}
                          onChange={(e) => setEventLink(e.target.value)}
                          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base"
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Taggar</label>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {eventTags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-sm"
                            >
                              {tag}
                              <button
                                onClick={() => setEventTags(eventTags.filter(t => t !== tag))}
                                className="hover:text-primary/70"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              maxLength={50}
                              value={eventTagInput}
                              onChange={(e) => setEventTagInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && eventTagInput.trim()) {
                                  e.preventDefault();
                                  if (!eventTags.includes(eventTagInput.trim())) {
                                    setEventTags([...eventTags, eventTagInput.trim()]);
                                  }
                                  setEventTagInput('');
                                }
                              }}
                              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                              placeholder="Lägg till tagg och tryck Enter"
                            />
                            {eventTagInput.length > 0 && (
                              <span className="absolute right-2 top-2 text-xs text-muted-foreground">{eventTagInput.length}/50</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {group?.role === 'owner' && (
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="event-protected"
                            checked={eventIsProtected}
                            onChange={(e) => setEventIsProtected(e.target.checked)}
                            className="rounded"
                          />
                          <label htmlFor="event-protected" className="text-sm flex items-center gap-2">
                            <Lock className="h-4 w-4" />
                            Skyddad händelse (endast ägare kan redigera)
                          </label>
                        </div>
                      )}
                      <div className="flex justify-end gap-2 pt-4 border-t">
                        <button
                          onClick={() => {
                            setShowEventEditor(false);
                            setEditingEvent(null);
                            setEventTitle('');
                            setEventDescription('');
                            setEventDate('');
                            setEventTags([]);
                            setEventLink('');
                            setEventIsProtected(false);
                            setShowEventTemplateDropdown(false);
                          }}
                          className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                        >
                          Avbryt
                        </button>
                        <button
                          onClick={async () => {
                            if (!eventTitle.trim() || !eventDescription.trim() || !eventDate || !groupId) return;
                            try {
                              if (editingEvent) {
                                const success = await updateEvent(groupId, editingEvent.id, {
                                  title: eventTitle,
                                  description: eventDescription,
                                  date: eventDate,
                                  tags: eventTags,
                                  link: eventLink || undefined,
                                  isProtected: eventIsProtected,
                                });
                                if (success) {
                                  const events = useGroupCalendarStore.getState().eventsByGroup[groupId] || [];
                                  setCalendarEvents(events);
                                  setShowEventEditor(false);
                                  setEditingEvent(null);
                                  setEventTitle('');
                                  setEventDescription('');
                                  setEventDate('');
                                  setEventTags([]);
                                  setEventLink('');
                                  setEventIsProtected(false);
                                  setShowEventTemplateDropdown(false);
                                  // Update calendarLastVisited so the edited event doesn't appear as "new" to the author
                                  setCalendarLastVisited(new Date().toISOString());
                                }
                              } else {
                                const event = await createEvent(groupId, {
                                  title: eventTitle,
                                  description: eventDescription,
                                  date: eventDate,
                                  tags: eventTags,
                                  link: eventLink || undefined,
                                  isProtected: eventIsProtected,
                                });
                                if (event) {
                                  const events = useGroupCalendarStore.getState().eventsByGroup[groupId] || [];
                                  setCalendarEvents(events);
                                  setShowEventEditor(false);
                                  setEventTitle('');
                                  setEventDescription('');
                                  setEventDate('');
                                  setEventTags([]);
                                  setEventLink('');
                                  setEventIsProtected(false);
                                  setShowEventTemplateDropdown(false);
                                  // Update calendarLastVisited so the newly created event doesn't appear as "new" to the author
                                  setCalendarLastVisited(new Date().toISOString());
                                }
                              }
                            } catch (e) {
                              console.error('Failed to save event', e);
                            }
                          }}
                          disabled={!eventTitle.trim() || !eventDescription.trim() || !eventDate}
                          className="px-4 py-2 rounded-lg bg-[#67724e] text-white hover:bg-[#67724e]/90 disabled:opacity-50 transition-colors"
                        >
                          {editingEvent ? 'Spara' : 'Skapa'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Delete Confirmation */}
          {deletingEventId && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
              <div className="absolute inset-0 bg-black/40" onClick={() => setDeletingEventId(null)} />
              <div className="relative w-full max-w-md rounded-lg bg-card p-6 z-10">
                <h3 className="text-lg font-semibold mb-2">Bekräfta radering</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Är du säker på att du vill ta bort denna händelse? Denna åtgärd går inte att ångra.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setDeletingEventId(null)}
                    className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={async () => {
                      if (!groupId) return;
                      const success = await deleteCalendarEvent(groupId, deletingEventId);
                      if (success) {
                        const events = useGroupCalendarStore.getState().eventsByGroup[groupId] || [];
                        setCalendarEvents(events);
                        setDeletingEventId(null);
                        setViewingEvent(null);
                      }
                    }}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    Ta bort
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              <h2 className="text-xl sm:text-2xl font-semibold">{t('groups.calendar.title')}</h2>
            </div>
            <button
              onClick={() => setShowEventEditor(true)}
              className="flex items-center gap-1.5 rounded-lg bg-[#67724e] px-3 py-1.5 text-sm sm:text-base font-medium text-primary-foreground hover:bg-[#67724e]/90 transition-colors shrink-0"
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">{t('groups.calendar.new')}</span>
              <span className="xs:hidden">Ny</span>
            </button>
          </div>

          {/* Search and Filters */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={calendarSearch}
                onChange={(e) => setCalendarSearch(e.target.value)}
                placeholder="Sök i händelser..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-base"
              />
            </div>
            
            {/* Show old events checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showOldEvents}
                onChange={(e) => setShowOldEvents(e.target.checked)}
                className="w-4 h-4 rounded border-input text-[#67724e] focus:ring-[#67724e] focus:ring-offset-0"
              />
              <span className="text-sm text-muted-foreground">Visa gamla händelser</span>
            </label>
            
            {allCalendarTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setCalendarTagFilter(null)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    !calendarTagFilter
                      ? 'bg-[#67724e] text-white'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  Alla
                </button>
                {allCalendarTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setCalendarTagFilter(tag === calendarTagFilter ? null : tag)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      tag === calendarTagFilter
                        ? 'bg-[#67724e] text-white'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Events List */}
          {calendarLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-xl border border-border bg-card p-4 sm:p-5 animate-pulse">
                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 bg-muted rounded shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-6 bg-muted rounded w-2/3" />
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 bg-muted rounded" />
                        <div className="h-4 bg-muted rounded w-40" />
                      </div>
                      <div className="h-4 bg-muted rounded w-full" />
                      <div className="h-4 bg-muted rounded w-4/5" />
                      <div className="flex gap-2 mt-2">
                        <div className="h-5 bg-muted rounded w-16" />
                        <div className="h-5 bg-muted rounded w-20" />
                      </div>
                      <div className="h-4 bg-muted rounded w-24 mt-2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredCalendarEvents.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-border p-4 sm:p-8 text-center">
              <Calendar className="h-8 w-8 sm:h-12 sm:w-12 mx-auto text-muted-foreground mb-2" />
              <h3 className="font-semibold text-sm sm:text-base mb-1">
                {calendarEvents.length === 0 ? 'Inga händelser ännu' : 'Inga händelser matchar filtret'}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base">
                {calendarEvents.length === 0 
                  ? 'Skapa den första händelsen i gruppkalendern'
                  : 'Prova att ändra sökningen eller filtret'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {groupedCalendarEvents.map((eventGroup) => (
                <div key={eventGroup.monthKey} className="space-y-3">
                  {/* Month header */}
                  <h3 className="text-lg font-semibold text-foreground capitalize sticky top-0 bg-background py-2 z-10">
                    {eventGroup.monthLabel}
                  </h3>
                  
                  {/* Events for this month */}
                  <div className="grid gap-3 sm:gap-4">
                    {eventGroup.events.map((event) => {
                      const eventDate = new Date(event.date);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const eventDay = new Date(eventDate);
                      eventDay.setHours(0, 0, 0, 0);
                      const daysUntil = Math.ceil((eventDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                      const isToday = daysUntil === 0;
                      const showDaysUntil = daysUntil > 0 && daysUntil < 30;
                      
                      // Calculate if event is new (created or updated after last visit)
                      const currentUserId = user?.id;
                      const members = Array.isArray(group?.members) ? group.members : [];
                      const userMember = members.find((m: any) => String(m.userId || m.id) === String(currentUserId));
                      const joinedAt = userMember?.joinedAt;
                      const referenceDate = calendarLastVisitedForDisplay ? new Date(calendarLastVisitedForDisplay) : (joinedAt ? new Date(joinedAt) : null);
                      const isAuthor = String(event.authorId) === String(currentUserId);
                      
                      // Check both createdAt and updatedAt
                      const createdDate = new Date(event.createdAt);
                      const updatedDate = event.updatedAt ? new Date(event.updatedAt) : createdDate;
                      const latestChange = updatedDate > createdDate ? updatedDate : createdDate;
                      const isNew = !isAuthor && referenceDate ? latestChange > referenceDate : false;
                      const isUpdated = updatedDate > createdDate; // Event has been edited after creation
                      
                      return (
                        <div
                          key={event.id}
                          onClick={() => setViewingEvent(event)}
                          className={`rounded-xl border p-4 sm:p-5 hover:shadow-md transition-all cursor-pointer ${
                            isToday 
                              ? 'border-[#67724e] border-4 bg-[#67724e]/5 shadow-lg' 
                              : 'border-border bg-card hover:border-primary/50'
                          }`}
                          style={isNew && !isToday ? { borderColor: '#945123' } : {}}
                        >
                          <div className="flex items-start gap-3">
                            <Calendar className={`h-5 w-5 sm:h-6 sm:w-6 shrink-0 mt-0.5 ${isToday ? 'text-[#67724e]' : 'text-primary'}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start gap-2 mb-2">
                                <h3 className="font-semibold text-base sm:text-lg flex-1">{event.title}</h3>
                                {isNew && (
                                  <span className="px-2 py-1 rounded-md text-xs font-medium text-white border" style={{ backgroundColor: '#945123', borderColor: '#945123' }}>
                                    {isUpdated ? 'Uppdaterad' : 'Ny'}
                                  </span>
                                )}
                                {event.isProtected && (
                                  <Lock className="h-4 w-4 text-yellow-600 dark:text-yellow-400 shrink-0 mt-1" />
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                <Clock className="h-4 w-4" />
                                <span>{new Date(event.date).toLocaleDateString('sv-SE', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}</span>
                                {isToday && (
                                  <span className="ml-2 px-2 py-0.5 rounded-full bg-[#67724e] text-white text-xs font-semibold">
                                    Idag!
                                  </span>
                                )}
                                {showDaysUntil && (
                                  <span className="ml-2 text-xs font-medium text-foreground">
                                    ({daysUntil} {daysUntil === 1 ? 'dag' : 'dagar'} kvar)
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                {event.description}
                              </p>
                              {event.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                  {event.tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs"
                                    >
                                      {tag}
                                    </span>
                                ))}
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Av {event.authorName}
                            </p>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'members' && (
        <div className={group?.role === 'owner' ? 'space-y-6' : 'space-y-6'}>
          {/* Leave Group Button - For all members */}
          {group?.role && (
            <button
              onClick={() => {
                setLeaveGroupError(null);
                setShowLeaveGroupConfirm(true);
              }}
              className="px-4 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors font-medium whitespace-nowrap"
            >
              Lämna grupp
            </button>
          )}
          
          {/* Väntande ansökningar - Owner only, first section */}
          {group?.role === 'owner' && (
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-3">Väntande ansökningar</h3>
              <MembershipRequestsPanel 
                groupId={group?.id || ''} 
                refetchSignal={inviteSentSignal}
                onCountChange={setPendingRequestsCount}
                onMemberApproved={refetchGroup}
              />
            </div>
          )}

          {/* Inbjudningar - Owner only, second section */}
          {group?.role === 'owner' && (
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-3">Inbjudningar</h3>
              <OwnerInvitesPanel groupId={group?.id || ''} refetchSignal={inviteSentSignal} />
              <button onClick={() => setInviteModalOpen(true)} className="flex items-center justify-center gap-1.5 rounded-lg bg-[#67724e] px-4 py-2 text-sm sm:text-base font-medium text-primary-foreground hover:bg-[#67724e]/90 transition-colors mt-3">
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>{t('groups.members.invite')}</span>
              </button>
            </div>
          )}

          {/* Medlemmar - Third section, always visible */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                <h2 className="text-xl sm:text-2xl font-semibold">
                  {t('groups.members.title')} ({group?.memberCount ?? 0})
                </h2>
              </div>

              {/* Role error message */}
              {roleError && (
                <div className="mb-3 p-3 rounded-lg bg-orange-900/20 border border-orange-700/50 text-orange-300 text-sm">
                  {roleError}
                </div>
              )}

              <div className="space-y-2">
                <input 
                  value={memberSearch} 
                  onChange={(e) => setMemberSearch(e.target.value)} 
                  placeholder={t('groups.members.searchPlaceholder')} 
                  className="w-full px-3 py-2 rounded-lg border border-border text-base bg-background" 
                />
                <select 
                  value={memberRoleFilter} 
                  onChange={(e) => setMemberRoleFilter(e.target.value as any)} 
                  className="w-full px-3 py-2 rounded-lg border border-border text-base bg-background"
                >
                  <option value="all">Alla roller</option>
                  <option value="owner">Ägare</option>
                  <option value="member">Medlem</option>
                </select>
              </div>
            </div>

          <div className="space-y-2">
            {!group ? (
              // Loading skeleton
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="rounded-lg border border-border bg-card p-3 animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-5 bg-muted rounded w-1/3" />
                        <div className="h-4 bg-muted rounded w-20" />
                      </div>
                      <div className="h-8 bg-muted rounded w-24" />
                    </div>
                  </div>
                ))}
              </div>
            ) : ((group && Array.isArray(group.members) && group.members.length > 0) ? (() => {
              const membersList = [...group.members!];
              membersList.sort((a, b) => {
                if (a.role === 'owner' && b.role !== 'owner') return -1;
                if (b.role === 'owner' && a.role !== 'owner') return 1;
                const an = (a.name || '').toLowerCase();
                const bn = (b.name || '').toLowerCase();
                return an.localeCompare(bn);
              });
              const filtered = membersList.filter((member) => {
                const q = memberSearch.trim().toLowerCase();
                if (memberRoleFilter !== 'all' && (member.role || 'member') !== memberRoleFilter) return false;
                if (!q) return true;
                return (member.name || '').toLowerCase().includes(q);
              });

              return filtered.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card p-2 sm:p-3"
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <span className="text-sm sm:text-base font-medium">
                        {String((member as any).displayName || member.name || '').split(' ').map((n) => (n ? n[0] : '')).join('')}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-base break-words overflow-wrap-anywhere">
                        {(member as any).displayName || member.name}
                        {user && member.id === user.id && ' (du)'}
                      </p>
                      <div className="flex flex-col gap-0.5">
                        <p className="text-sm text-muted-foreground">{t(`groups.roles.${member.role || 'member'}`)}</p>
                        {member.joinedAt && (
                          <p className="text-xs text-muted-foreground">
                            {t('groups.members.memberSince', 'Medlem sedan')} {formatMemberDate(member.joinedAt)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Flag button – shown for all non-self members when owner dropdown is not available */}
                    {member.id !== currentUserId && (group?.role !== 'owner' || member.role === 'owner') && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setReportTarget({ id: member.id, name: (member as any).displayName || member.name || '' });
                        }}
                        className="p-1.5 sm:p-2 rounded hover:bg-muted transition-colors text-muted-foreground/40 hover:text-red-500"
                        title={t('groups.members.report.button')}
                      >
                        <Flag className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                    )}
                    {/* Owner-only action dropdown – for non-owner members; includes report option */}
                    {group?.role === 'owner' && member.role !== 'owner' && (
                    <div className="relative">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setMemberDropdownOpen(memberDropdownOpen === member.id ? null : member.id);
                        }}
                        className="p-1.5 sm:p-2 rounded hover:bg-muted transition-colors shrink-0"
                      >
                        <MoreVertical className="h-4 w-4 text-muted-foreground" />
                      </button>
                      {memberDropdownOpen === member.id && (
                        <div className="absolute right-0 top-full mt-1 w-52 rounded-lg border border-border bg-card shadow-lg z-10">
                          <button
                            onClick={() => handleChangeMemberRole(member.id, member.role === 'owner' ? 'member' : 'owner')}
                            className="w-full text-left px-4 py-2 hover:bg-muted transition-colors text-sm rounded-t-lg"
                          >
                            {member.role === 'owner' ? 'Ta bort som ägare' : 'Gör till ägare'}
                          </button>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="w-full text-left px-4 py-2 hover:bg-muted transition-colors text-sm text-red-600"
                          >
                            Ta bort från grupp
                          </button>
                          <div className="border-t border-border" />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMemberDropdownOpen(null);
                              setReportTarget({ id: member.id, name: (member as any).displayName || member.name || '' });
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-muted transition-colors text-sm text-orange-600 rounded-b-lg flex items-center gap-2"
                          >
                            <Flag className="h-3.5 w-3.5" />
                            {t('groups.members.report.button')}
                          </button>
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                </div>
              ));
            })() : (
              <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
                Inga medlemmar att visa ännu.
              </div>
            ))}
          </div>
          </div>
        </div>
      )}

      {/* Anslagstavla / Bulletin Board */}
      {activeTab === 'board' && (
          <div className="space-y-2">
            {/* member cards removed from board header */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              <h2 className="text-xl sm:text-2xl font-semibold">{t('groups.board.title')}</h2>
            </div>
            <button
              onClick={() => {
                setEditingNote(undefined);
                setIsNoteModalOpen(true);
              }}
              className="flex items-center gap-1.5 rounded-lg bg-[#67724e] px-3 py-1.5 text-sm sm:text-base font-medium text-primary-foreground hover:bg-[#67724e]/90 transition-colors shrink-0"
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">{t('groups.board.new')}</span>
              <span className="xs:hidden">Ny</span>
            </button>
          </div>

          {/* Search and filters */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('groups.board.search')}
                className="w-full pl-9 pr-10 py-2 rounded-lg border border-input bg-background text-base focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded transition-colors"
                  aria-label="Rensa sökning"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setCategoryFilter('all')}
                  className={`px-2 py-1.5 rounded-lg text-sm transition-all whitespace-nowrap shrink-0 border bg-yellow-900/30 text-yellow-300 border-yellow-700 ${
                  categoryFilter === 'all'
                    ? 'ring-2 ring-offset-2 ring-offset-background ring-current/50'
                    : 'opacity-70 hover:opacity-100'
                }`}
                style={{
                  boxShadow: categoryFilter === 'all' ? '0 0 16px rgba(251, 191, 36, 0.8)' : 'none'
                }}
              >
                Alla
              </button>
              {(['offer', 'request', 'lend', 'sell', 'info'] as NoteCategory[]).map((cat) => {
                const config = categoryConfig[cat];
                const Icon = config.icon;
                const shadowColors: Record<NoteCategory, string> = {
                  offer: 'rgba(34, 197, 94, 0.5)',
                  request: 'rgba(251, 146, 60, 0.5)',
                  lend: 'rgba(59, 130, 246, 0.5)',
                  sell: 'rgba(168, 85, 247, 0.5)',
                  info: 'rgba(156, 163, 175, 0.5)',
                };
                return (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(cat)}
                    className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-sm transition-all whitespace-nowrap shrink-0 border ${config.color} ${
                      categoryFilter === cat
                        ? 'ring-2 ring-offset-2 ring-offset-background ring-current/50'
                        : 'opacity-70 hover:opacity-100'
                    }`}
                    style={{
                      boxShadow: categoryFilter === cat ? `0 0 16px ${shadowColors[cat].replace('0.5', '0.8')}` : 'none'
                    }}
                    title={t(`groups.board.categories.${cat}`)}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{t(`groups.board.categories.${cat}`)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Filter toggles */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={showExpired}
                onChange={(e) => setShowExpired(e.target.checked)}
                className="rounded border-input h-3 w-3 sm:h-4 sm:w-4"
              />
              <span className="hidden xs:inline">{t('groups.board.filters.showExpired')}</span>
              <span className="xs:hidden">Utgångna</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={showMyNotes}
                onChange={(e) => setShowMyNotes(e.target.checked)}
                className="rounded border-input h-3 w-3 sm:h-4 sm:w-4"
              />
              <span className="hidden xs:inline">{t('groups.board.filters.myNotes')}</span>
              <span className="xs:hidden">Mina</span>
            </label>
          </div>

          {/* Notes grid */}
          {boardLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="rounded-xl border border-border bg-card p-4 animate-pulse">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 bg-muted rounded" />
                        <div className="h-5 bg-muted rounded w-32" />
                      </div>
                      <div className="h-5 bg-muted rounded w-5" />
                    </div>
                    <div className="h-6 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-full" />
                    <div className="h-4 bg-muted rounded w-5/6" />
                    <div className="flex items-center justify-between mt-2">
                      <div className="h-4 bg-muted rounded w-24" />
                      <div className="h-4 bg-muted rounded w-20" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredNotes.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-border p-8 text-center">
              <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <h3 className="font-semibold mb-1">{t('groups.board.empty.title')}</h3>
              <p className="text-muted-foreground text-sm">{t('groups.board.empty.description')}</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredNotes.map((note) => {
                const currentUserId = user?.id;
                const isAuthor = !!currentUserId && note.authorId === currentUserId;
                const isGroupOwner = group?.role === 'owner';
                const canEdit = isAuthor; // Bara författaren kan redigera
                const canDelete = isAuthor || isGroupOwner; // Författaren eller gruppägaren kan radera
                
                // Use boardLastVisitedForDisplay for "Ny" markers (doesn't update when tab opened)
                // Don't mark own notes as new
                const members = Array.isArray(group?.members) ? group.members : [];
                const userMember = members.find((m: any) => String(m.userId || m.id) === String(currentUserId));
                const joinedAt = userMember?.joinedAt;
                const referenceDate = boardLastVisitedForDisplay ? new Date(boardLastVisitedForDisplay) : (joinedAt ? new Date(joinedAt) : null);
                const isNew = !isAuthor && referenceDate ? new Date(note.createdAt) > referenceDate : false;
                
                return (
                  <NoteCard
                    key={note.id}
                    note={note}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onEdit={() => {
                      setEditingNote(note);
                      setIsNoteModalOpen(true);
                    }}
                    onDelete={() => {
                      setDeletingNoteId(note.id);
                    }}
                    onImageClick={(imageUrl) => setBulletinFullscreenImage(imageUrl)}
                    t={t}
                    isNew={isNew}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Confirm remove member modal */}
      {confirmRemoveFor && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="absolute inset-0 bg-black/60" onClick={() => setConfirmRemoveFor(null)} />
          <div className="relative w-full max-w-md rounded-lg bg-card p-6 z-[10000]">
            <h3 className="text-lg font-semibold mb-2">{t('groups.members.removeMember')}</h3>
            <p className="text-sm text-muted-foreground mb-4">{t('groups.members.removeMemberConfirm', { name: confirmRemoveFor.name })}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmRemoveFor(null)} className="rounded-lg px-3 py-2 border border-border">{t('common.cancel')}</button>
              <button
                disabled={removing}
                onClick={async () => {
                  setRemoving(true);
                  try {
                    if (!group) throw new Error('Ingen grupp');
                    const res = await apiDelete(`/groups/${group.id}/members/${confirmRemoveFor.id}`);
                    if (!res.success) {
                      alert(res.error || t('groups.members.couldNotRemove'));
                      setRemoving(false);
                      return;
                    }
                    setGroup(res.data as any);
                    setConfirmRemoveFor(null);
                  } catch (err) {
                    console.error(err);
                    alert(t('groups.members.couldNotRemove'));
                  } finally {
                    setRemoving(false);
                  }
                }}
                className="rounded-lg px-3 py-2 bg-red-600 text-white disabled:opacity-50"
              >
                {removing ? t('common.deleting') : t('groups.members.remove')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm leave group modal */}
      {showLeaveGroupConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="absolute inset-0" onClick={() => !leavingGroup && setShowLeaveGroupConfirm(false)} />
          <div className="relative w-full max-w-md rounded-lg bg-card p-6 z-[10000]">
            <h3 className="text-lg font-semibold mb-2">Lämna grupp</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Är du säker på att du vill lämna <strong>{group?.name}</strong>?
              {group?.role === 'owner' && ' Du kan bara lämna om det finns minst en annan ägare.'}
            </p>
            {leaveGroupError && (
              <p className="text-sm text-red-500 mb-4">{leaveGroupError}</p>
            )}
            <div className="flex justify-end gap-2">
              <button 
                onClick={() => {
                  setShowLeaveGroupConfirm(false);
                  setLeaveGroupError(null);
                }}
                disabled={leavingGroup}
                className="rounded-lg px-3 py-2 border border-border disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                disabled={leavingGroup}
                onClick={handleLeaveGroup}
                className="rounded-lg px-3 py-2 bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
              >
                {leavingGroup ? 'Lämnar...' : 'Lämna grupp'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note Modal */}
      <NoteModal
        isOpen={isNoteModalOpen}
        onClose={() => {
          setIsNoteModalOpen(false);
          setEditingNote(undefined);
        }}
        onSave={async (data) => {
          const resolvedGroupId = group?.id || groupId;
          if (!resolvedGroupId) {
            alert('Kunde inte identifiera gruppen');
            return;
          }
          if (!currentUserId) {
            alert('Du behöver vara inloggad för att skapa anslag');
            return;
          }

          if (editingNote) {
            await updateNote(editingNote.id, resolvedGroupId, data);
          } else {
            await addNote({
              groupId: resolvedGroupId,
              authorId: currentUserId,
              ...data,
            });
            // Update boardLastVisited so the newly created note doesn't appear as "new" to the author
            setBoardLastVisited(new Date().toISOString());
          }
        }}
        editNote={editingNote}
        initialCategory={categoryFilter !== 'all' ? categoryFilter : 'offer'}
        t={t}
        groupId={group?.id || ''}
      />

      {/* Invite Modal for owners */}
      {/* Application modal */}
      {showApplicationModal && !group?.isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowApplicationModal(false)} />
          <div className="relative w-full max-w-md rounded-lg bg-card p-6 z-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Ansök om medlemskap</h3>
              <button onClick={() => setShowApplicationModal(false)} className="p-2 rounded hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Motivation (obligatorisk)</label>
                <textarea
                  value={applicationMessage}
                  onChange={(e) => {
                    if (e.target.value.length <= 200) {
                      setApplicationMessage(e.target.value);
                    }
                  }}
                  className="w-full mt-1 rounded-lg border border-border p-3 bg-background min-h-[100px] resize-none"
                  placeholder="Beskriv kort varför du vill bli medlem..."
                  maxLength={200}
                />
                <div className="text-xs text-muted-foreground mt-1 text-right">
                  {applicationMessage.length}/200 tecken
                </div>
                <p className="text-xs text-orange-300 mt-2">
                  Obs: Gruppägare kommer att se din e-postadress.
                </p>
              </div>
              {errorMessage && (
                <div className="p-3 rounded-lg bg-orange-900/20 border border-orange-700/50 text-orange-300 text-sm">
                  {errorMessage}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button 
                  onClick={() => setShowApplicationModal(false)} 
                  className="rounded-lg px-4 py-2 border border-border hover:bg-muted"
                >
                  Avbryt
                </button>
                <button
                  disabled={joiningGroup || applicationMessage.trim().length < 10}
                  onClick={handleJoinGroup}
                  className="rounded-lg px-4 py-2 bg-[#67724e] text-white hover:bg-[#67724e]/90 disabled:opacity-50"
                >
                  {joiningGroup ? 'Skickar...' : 'Skicka ansökan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showOpenJoinConfirm && group?.isOpen && (
        <ConfirmModal
          open={showOpenJoinConfirm}
          title="Gå med i grupp"
          message={`Vill du gå med i gruppen ${group?.name || ''}?`}
          confirmLabel={joiningGroup ? 'Bearbetar...' : 'Gå med'}
          confirmClassName="px-3 py-1 rounded bg-[#67724e] text-white hover:bg-[#67724e]/90 disabled:opacity-50"
          onCancel={() => {
            if (!joiningGroup) setShowOpenJoinConfirm(false);
          }}
          onConfirm={() => {
            if (!joiningGroup) {
              void handleJoinGroup();
            }
          }}
        />
      )}

      {inviteModalOpen && (
        <InviteModal
          groupId={group?.id || ''}
          groupName={group?.name}
          onClose={() => setInviteModalOpen(false)}
          onSent={() => {
            setInviteModalOpen(false);
            setInviteSentSignal((s) => s + 1);
          }}
        />
      )}
      {/* Threads confirm modal */}
      {threadsConfirm && (
        <ConfirmModal
          open={Boolean(threadsConfirm)}
          title={threadsConfirm ? (threadsConfirm.type === 'pin' ? (threadsConfirm.isPinned ? 'Ta bort fäst' : 'Fäst tråd') : threadsConfirm.type === 'thread' ? 'Radera tråd' : 'Radera meddelande') : undefined}
          message={threadsConfirm && threadsConfirm.title ? (threadsConfirm.type === 'pin' ? `Vill du ${threadsConfirm.isPinned ? 'ta bort fästningen från' : 'fästa'} "${threadsConfirm.title}"?` : `Är du säker på att du vill ta bort: "${threadsConfirm.title}"?`) : undefined}
          confirmLabel={threadsConfirm && threadsConfirm.type === 'pin' ? (threadsConfirm.isPinned ? 'Ta bort fäst' : 'Fäst') : undefined}
          confirmClassName={threadsConfirm && threadsConfirm.type === 'pin' ? 'px-3 py-1 rounded bg-yellow-600 hover:bg-yellow-700 text-white' : undefined}
          onCancel={() => setThreadsConfirm(null)}
          onConfirm={handleThreadsConfirm}
        />
      )}
      
      {/* Delete note confirmation modal */}
      {deletingNoteId && (
        <ConfirmModal
          open={true}
          title={t('groups.board.note.delete')}
          message={t('groups.board.note.confirmDelete')}
          confirmLabel={t('common.delete')}
          confirmClassName="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
          onCancel={() => setDeletingNoteId(null)}
          onConfirm={async () => {
            const noteIdToDelete = deletingNoteId;
            setDeletingNoteId(null);
            try {
              const resolvedGroupId = group?.id || groupId;
              if (!resolvedGroupId) {
                alert('Kunde inte identifiera gruppen');
                return;
              }
              await deleteNote(noteIdToDelete, resolvedGroupId);
            } catch (err) {
              console.error('Failed to delete note', err);
              alert(t('groups.board.form.deleteFailed'));
            }
          }}
        />
      )}
      
      {/* Markdown Help Modal */}
      {showMarkdownHelp && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-background rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-foreground">Markdown-formatering</h2>
                <button
                  onClick={() => setShowMarkdownHelp(false)}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Rubriker</h3>
                  <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                    <div># Rubrik 1</div>
                    <div>## Rubrik 2</div>
                    <div>### Rubrik 3</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Textformatering</h3>
                  <div className="bg-muted rounded-lg p-3 font-mono text-sm space-y-1">
                    <div>**fet text**</div>
                    <div>*kursiv text*</div>
                    <div>~~genomstruken text~~</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Listor</h3>
                  <div className="bg-muted rounded-lg p-3 font-mono text-sm space-y-1">
                    <div>- Punkt 1</div>
                    <div>- Punkt 2</div>
                    <div className="mt-2">1. Första</div>
                    <div>2. Andra</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Länkar</h3>
                  <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                    [Länktext](https://example.com)
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Kod</h3>
                  <div className="bg-muted rounded-lg p-3 font-mono text-sm space-y-1">
                    <div>`inline kod`</div>
                    <div className="mt-2">```</div>
                    <div>kodblock</div>
                    <div>```</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Citat</h3>
                  <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                    &gt; Detta är ett citat
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulletin board fullscreen image modal */}
      {bulletinFullscreenImage && (
        <div 
          className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-sm flex items-center justify-center p-0 md:p-4"
          onClick={() => setBulletinFullscreenImage(null)}
        >
          <button
            onClick={() => setBulletinFullscreenImage(null)}
            className="absolute bottom-4 right-4 md:top-4 md:bottom-auto p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
            aria-label="Stäng"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img 
            src={bulletinFullscreenImage} 
            alt="Fullscreen" 
            style={{ 
              position: 'absolute',
              top: '50%',
              left: '50%',
              minWidth: '100vh',
              minHeight: '100vw',
              transform: 'translate(-50%, -50%) rotate(90deg)',
              transformOrigin: 'center'
            }}
            className="object-contain md:object-contain md:relative md:max-w-full md:max-h-full md:!w-auto md:!h-auto md:!transform-none"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Report member modal */}
      <ReportMemberModal
        open={reportTarget !== null}
        target={reportTarget}
        groupId={group?.id || groupId}
        groupName={group?.name}
        onClose={() => setReportTarget(null)}
      />
    </div>
  );
}
